/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps, no-useless-escape */
// src/components/SystemAdmin.js
import React, { useState, useEffect } from 'react';
import { 
  Card, Button, Form, Modal, Badge, Alert, Spinner, 
  Table, Row, Col, Tabs, Tab, Accordion, Dropdown,
  InputGroup, Nav, Navbar, Container, NavDropdown
} from 'react-bootstrap';
import axios from 'axios';
// Import axios config to ensure global interceptors take effect
import '../api/axiosConfig';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import useAudioToast from '../hooks/useAudioToast';
import audioNotificationService from '../services/audioNotificationService';
import DispatcherManagement from './DispatcherManagement';
import DriverLocationMap from './DriverLocationMap';
import MainDomainBookings from './MainDomainBookings';

// Custom styles for modern tab buttons
const modernTabStyles = `
  .modern-tabs .nav-pills .nav-link {
    border: 2px solid #e9ecef;
    border-radius: 12px;
    margin: 0 4px;
    padding: 12px 16px;
    font-weight: 500;
    transition: all 0.3s ease;
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    color: #495057;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
  
  .modern-tabs .nav-pills .nav-link:hover {
    border-color: #007bff;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,123,255,0.2);
  }
  
  .modern-tabs .nav-pills .nav-link.active {
    border-color: #007bff;
    background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0,123,255,0.3);
  }
  
  .modern-tabs .nav-pills .nav-link.active:hover {
    background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
    box-shadow: 0 8px 16px rgba(0,123,255,0.4);
  }
  
  /* Email log row styling */
  .email-log-row:hover {
    background-color: #f8f9fa !important;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
  
  .email-log-row {
    transition: all 0.2s ease;
  }
`;

const hasVehicleData = (vehicle) => {
  if (!vehicle || typeof vehicle !== 'object') return false;
  return Object.values(vehicle).some((value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim() !== '';
    return true;
  });
};

export default function SystemAdmin() {
  const audioToast = useAudioToast();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 767);
  const [userSortConfig, setUserSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [dataLoaded, setDataLoaded] = useState({
    dashboard: false,
    users: false,
    licensing: false,
    leads: false,
    tickets: false,
    banners: false,
    'driver-diaries': false,
    audit: false,
    'taxi-companies': false,
    'job-monitoring': false,
    'referral-program': false,
    'sms-templates': false,
    'merchandise-orders': false,
    'driver-locations': false,
    'email-logs': false,
    'driver-features-guide': false,
    audio: false,
    'fare-tariffs': false,
    'support-access': false,
  });
  const [users, setUsers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [banners, setBanners] = useState([]);
  const [systemStats, setSystemStats] = useState({});
  const [auditLogs, setAuditLogs] = useState([]);
  const [pendingLicensing, setPendingLicensing] = useState([]);
  const [driverDiaries, setDriverDiaries] = useState([]);
  const [selectedDriverDiary, setSelectedDriverDiary] = useState(null);
  const [driverDiaryEntries, setDriverDiaryEntries] = useState([]);
  const [driverDiaryLoading, setDriverDiaryLoading] = useState(false);
  
  // Audio management state
  const [audioFiles, setAudioFiles] = useState([]);
  const [audioUploading, setAudioUploading] = useState(false);
  const [selectedAudioFile, setSelectedAudioFile] = useState(null);
  const [showAudioModal, setShowAudioModal] = useState(false);
  const [audioTestPlaying, setAudioTestPlaying] = useState(false);
  const [audioMappings, setAudioMappings] = useState([]);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [driverDiaryFilter, setDriverDiaryFilter] = useState({
    status: 'accepted',
    startDate: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
    endDate: ''
  });
  const [driverDiarySearch, setDriverDiarySearch] = useState('');
  const [supportAccessSearch, setSupportAccessSearch] = useState(''); // Search term for support access driver lookup

  // Email Logs state
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLogsSummary, setEmailLogsSummary] = useState([]);
  const [emailLogsLoading, setEmailLogsLoading] = useState(false);
  const [emailLogsFilter, setEmailLogsFilter] = useState({
    driverId: '',
    emailType: '',
    status: '',
    startDate: '',
    endDate: ''
  });
  const [emailLogsPagination, setEmailLogsPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    hasMore: false
  });
  const [selectedDriverEmailLogs, setSelectedDriverEmailLogs] = useState(null);
  const [showEmailLogModal, setShowEmailLogModal] = useState(false);
  const [showFullEmailModal, setShowFullEmailModal] = useState(false);
  const [selectedEmailContent, setSelectedEmailContent] = useState(null);

  // Merchandise Orders state
  const [merchandiseOrders, setMerchandiseOrders] = useState([]);
  const [merchandiseStats, setMerchandiseStats] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orderStatusForm, setOrderStatusForm] = useState({
    status: '',
    adminNotes: '',
    trackingNumber: ''
  });
  const [paymentForm, setPaymentForm] = useState({
    paymentStatus: '',
    paymentMethod: ''
  });

  // Product Management state
  const [products, setProducts] = useState([]);
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    image: null,
    imagePreview: '',
    isActive: true,
    stockQuantity: '',
    units: '1',
    specifications: ''
  });
  const [productImageFile, setProductImageFile] = useState(null);

  // Taxi Companies state
  const [taxiCompanies, setTaxiCompanies] = useState([]);
  const [showTaxiCompanyModal, setShowTaxiCompanyModal] = useState(false);
  const [taxiCompanyForm, setTaxiCompanyForm] = useState({
    company: '',
    phone: '',
    email: '',
    postcodes: [''],
    notes: ''
  });
  const [taxiCompanySearch, setTaxiCompanySearch] = useState('');
  const [showCsvImportModal, setShowCsvImportModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState([]);
  const [importResults, setImportResults] = useState(null);

  // Licensing Bodies state
  const [licensingBodies, setLicensingBodies] = useState([]);
  const [showLicensingBodyModal, setShowLicensingBodyModal] = useState(false);
  const [selectedLicensingBody, setSelectedLicensingBody] = useState(null);
  const [licensingBodyForm, setLicensingBodyForm] = useState({
    code: '',
    name: '',
    fullName: '',
    region: '',
    country: 'England',
    contactEmail: '',
    contactPhone: '',
    website: '',
    postcodes: [],
    active: true,
    notes: '',
    tariffs: []
  });
  const [newPostcodeInput, setNewPostcodeInput] = useState('');
  const [showTariffModal, setShowTariffModal] = useState(false);
  const [selectedTariff, setSelectedTariff] = useState(null);
  const [tariffForm, setTariffForm] = useState({
    name: '',
    description: '',
    validFrom: '',
    firstMile: '',
    subsequentMile: '',
    active: true
  });

  // Modal states
  const [showUserModal, setShowUserModal] = useState(false);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [showSystemSettingsModal, setShowSystemSettingsModal] = useState(false);
  const [showBulkActionsModal, setShowBulkActionsModal] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkActionForm, setBulkActionForm] = useState({
    action: '',
    newStatus: '',
    newRole: ''
  });
  const [showDriverDiaryModal, setShowDriverDiaryModal] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Job Monitor Admin state
  const [jobMonitors, setJobMonitors] = useState([]);
  const [usersWithoutMonitors, setUsersWithoutMonitors] = useState([]);
  const [showJobMonitorModal, setShowJobMonitorModal] = useState(false);
  const [selectedJobMonitor, setSelectedJobMonitor] = useState(null);
  const [jobMonitorForm, setJobMonitorForm] = useState({
    isActive: false,
    postcodeAreas: [],
    alertSettings: {
      screenFlash: true,
      soundAlert: true,
      emailAlert: false,
      checkInterval: 5,
      autoStopAfterMinutes: 30
    }
  });
  const [newPostcode, setNewPostcode] = useState('');

  // Copy Monitor Areas state
  const [showCopyMonitorModal, setShowCopyMonitorModal] = useState(false);
  const [sourceDriver, setSourceDriver] = useState(null);
  const [selectedTargetDrivers, setSelectedTargetDrivers] = useState([]);
  const [copyInProgress, setCopyInProgress] = useState(false);

  // Driver Diary View state
  const [driverDiaryViewMode, setDriverDiaryViewMode] = useState('summary'); // 'summary' or 'diary'
  const [selectedDriverJobs, setSelectedDriverJobs] = useState([]);

  const createDefaultSpareVehicle = () => ({
    carMake: '',
    carModel: '',
    carSeats: '',
    regNumber: '',
    vehicleLicenseId: '',
    vehicleLicenseExpiry: '',
    roadTaxExpiry: '',
    motExpiry: '',
    insuranceExpiry: '',
    vehicleInspectionExpiry: '',
    photoOfCar: ''
  });

  const normalizeSpareVehicleForForm = (data = {}) => {
    const defaults = createDefaultSpareVehicle();
    const source = data && typeof data === 'object' ? data : {};
    Object.keys(defaults).forEach((key) => {
      defaults[key] = source[key] ?? defaults[key];
    });
    return defaults;
  };

  const createDefaultUserForm = () => ({
    name: '', // Keep for backward compatibility
    firstName: '',
    surname: '',
    email: '',
    password: '',
    role: 'professional',
    phoneNumber: '',
    subscriptionStatus: 'inactive',
    isActive: true,
    // Driver Profile fields
    companyName: '',
    companyAddress: '',
    website: '',
    carMake: '',
    carModel: '',
    carSeats: '',
    regNumber: '',
    licensingAuthority: '',
    badgeNumber: '',
    licenseType: 'Hackney License',
    mainPostcode: '',
    postcodeSectorsCovered: '',
    logo: '',
    photoOfCar: '',
    photoOfDriver: '',
    // Preferences
    paymentMethods: [],
    leadEmailOptIn: false,
    emailDiaryDaily: false,
    dispatchAccessAccepted: false,
    dispatchEmail: '',
    audioNotifications: true,
    preferredVehicleForJobs: 'primary',
    // Document expiries
    roadTaxExpiry: '',
    motExpiry: '',
    insuranceExpiry: '',
    vehicleInspectionExpiry: '',
    vehicleLicenseId: '',
    vehicleLicenseExpiry: '',
    spareVehicle: createDefaultSpareVehicle(),
    // Bank Details
    accountType: 'Personal',
    accountName: '',
    accountSortCode: '',
    accountNumber: ''
  });

  const [userForm, setUserForm] = useState(createDefaultUserForm());
  const adminSpareVehicleHasData = hasVehicleData(userForm.spareVehicle);

  const mapUserToForm = (user) => {
    const form = createDefaultUserForm();
    if (!user) return form;

    form.name = user.name || '';
    form.firstName = user.firstName || (user.name ? user.name.split(' ')[0] : '');
    form.surname = user.surname || (user.name ? user.name.split(' ').slice(1).join(' ') : '');
    form.email = user.email || '';
    form.role = user.role || 'professional';
    form.phoneNumber = user.phoneNumber || '';
    form.subscriptionStatus = user.subscriptionStatus || 'inactive';
    form.isActive = user.isActive !== undefined ? user.isActive : true;
    form.companyName = user.companyName || '';
    form.companyAddress = user.companyAddress || '';
    form.website = user.website || '';
    form.carMake = user.carMake || '';
    form.carModel = user.carModel || '';
    form.carSeats = user.carSeats || '';
    form.regNumber = user.regNumber || '';
    form.licensingAuthority = user.licensingAuthority || '';
    form.badgeNumber = user.badgeNumber || '';
    form.licenseType = user.licenseType || 'Hackney License';
    form.mainPostcode = user.mainPostcode || '';
    form.postcodeSectorsCovered = Array.isArray(user.postcodeSectorsCovered)
      ? user.postcodeSectorsCovered.join(', ')
      : (user.postcodeSectorsCovered || '');
    form.logo = user.logo || '';
    form.photoOfCar = user.photoOfCar || '';
    form.photoOfDriver = user.photoOfDriver || '';
    form.paymentMethods = Array.isArray(user.paymentMethods) ? user.paymentMethods : (user.paymentMethods ? [user.paymentMethods].flat() : []);
    const spareData = hasVehicleData(user.spareVehicle);
    form.preferredVehicleForJobs = spareData && user.preferredVehicleForJobs === 'spare' ? 'spare' : 'primary';
    form.leadEmailOptIn = !!user.leadEmailOptIn;
    form.emailDiaryDaily = !!user.emailDiaryDaily;
    form.dispatchAccessAccepted = !!user.dispatchAccessAccepted;
    form.dispatchEmail = user.dispatchEmail || '';
    form.audioNotifications = user.audioNotifications === undefined ? true : !!user.audioNotifications;
    form.roadTaxExpiry = user.roadTaxExpiry || '';
    form.motExpiry = user.motExpiry || '';
    form.insuranceExpiry = user.insuranceExpiry || '';
    form.vehicleInspectionExpiry = user.vehicleInspectionExpiry || '';
    form.vehicleLicenseId = user.vehicleLicenseId || '';
    form.vehicleLicenseExpiry = user.vehicleLicenseExpiry || '';
    form.accountType = user.accountType || 'Personal';
    form.accountName = user.accountName || '';
    form.accountSortCode = user.accountSortCode || '';
    form.accountNumber = user.accountNumber || '';
    form.spareVehicle = normalizeSpareVehicleForForm(user.spareVehicle || {});

    return form;
  };

  useEffect(() => {
    if (!adminSpareVehicleHasData && userForm.preferredVehicleForJobs === 'spare') {
      setUserForm((prev) => ({ ...prev, preferredVehicleForJobs: 'primary' }));
    }
  }, [adminSpareVehicleHasData, userForm.preferredVehicleForJobs]);

  const handleAdminSpareVehicleChange = (name, value) => {
    setUserForm(prev => ({
      ...prev,
      spareVehicle: {
        ...(prev.spareVehicle ? { ...prev.spareVehicle } : createDefaultSpareVehicle()),
        [name]: value
      }
    }));
  };

  const handleUserPaymentMethodToggle = (method) => {
    setUserForm(prev => {
      const current = prev.paymentMethods || [];
      const updated = current.includes(method)
        ? current.filter(m => m !== method)
        : [...current, method];
      return { ...prev, paymentMethods: updated };
    });
  };

  const [leadForm, setLeadForm] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    from: '',
    to: '',
    dateTime: '',
    passengers: 1,
    status: 'pending',
    returnTrip: false
  });

  const [bannerForm, setBannerForm] = useState({
    title: '',
    message: '',
    audience: 'all',
    startsAt: '',
    endsAt: '',
    isActive: true,
    priority: 0,
    targetPostcodes: '',
    rotationInterval: 5,
    imageFile: null,
    imagePreview: null,
    imageLoading: false
  });

  const [systemSettings, setSystemSettings] = useState({
    siteName: 'Taxi-Local.uk',
    maintenanceMode: false,
    allowNewRegistrations: true,
    maxFileUploadSize: 5,
    sessionTimeout: 24,
    emailNotifications: true,
    leadTimeoutSeconds: 60,
    defaultBannerPostcode: 'ZZ01'
  });

  // Ticket form state
  const [ticketForm, setTicketForm] = useState({
    title: '',
    description: '',
    status: 'open',
    priority: 'medium',
    response: ''
  });

  // Ticket responses state
  const [ticketResponses, setTicketResponses] = useState([]);

  // Search and bulk action states
  const [leadSearchTerm, setLeadSearchTerm] = useState('');
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [selectAllLeads, setSelectAllLeads] = useState(false);
  
  // Column sorting states
  const [leadSortField, setLeadSortField] = useState('dateTime');
  const [leadSortDirection, setLeadSortDirection] = useState('desc');
  
  // Column management states
  const [visibleColumns, setVisibleColumns] = useState([
    'customerName',
    'from', 
    'to',
    'dateTime',
    'status'
  ]);
  const [showColumnSelector, setShowColumnSelector] = useState(false);

  // Email Template Editor state
  const [emailTemplate, setEmailTemplate] = useState({
    subject: '🚕 Your Taxi-Local.uk Invitation - 1 Month for £1!',
    fromName: 'Taxi-Local',
    content: `<h2>🚕 You've been invited to try Taxi-Local.uk!</h2>
<p>Hi {referredName},</p>
<p>Your colleague {referrerName} thinks you'd love Taxi-Local.uk - the platform that connects drivers with customers.</p>
<p><strong>Special Offer: Try Taxi-Local.uk for 1 month for just £1!</strong></p>
<p>This exclusive invitation gives you access to:</p>
<ul>
  <li>Direct customer bookings</li>
  <li>Real-time job notifications</li>
  <li>Professional driver tools</li>
  <li>And much more!</li>
</ul>
<p><a href="{signupUrl}" style="background-color: #007bff; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px; display: inline-block;">Get Started for £1</a></p>
<p>This invitation expires on {expiryDate}.</p>
<p>Best regards,<br>The Taxi-Local Team</p>`
  });

  const [testEmail, setTestEmail] = useState({
    email: '',
    referredName: 'John Doe',
    referrerName: 'Jane Smith',
    company: 'ABC Taxis'
  });

  const [savingTemplate, setSavingTemplate] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  // ── Enhanced SMS Function with Fallback Options ──────────────────────────
  const sendSMSWithFallback = (phoneNumber, message, templateName) => {
    // Clean phone number (remove spaces, dashes, etc.)
    const cleanPhone = phoneNumber.replace(/[\s\-\(\)]/g, '');
    
    // Better mobile detection - check multiple indicators
    const isMobile = () => {
      // Check user agent
      const userAgent = navigator.userAgent.toLowerCase();
      const mobileKeywords = ['android', 'iphone', 'ipad', 'ipod', 'blackberry', 'windows phone', 'mobile'];
      const hasMobileUserAgent = mobileKeywords.some(keyword => userAgent.includes(keyword));
      
      // Check screen size
      const isSmallScreen = window.innerWidth <= 768;
      
      // Check touch capability
      const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      // Check if running in a mobile app wrapper (like Capacitor, Cordova)
      const isInApp = window.cordova || window.Capacitor || window.ionic;
      
      console.log('📱 Mobile Detection:', {
        userAgent: navigator.userAgent,
        hasMobileUserAgent,
        isSmallScreen,
        hasTouchScreen,
        isInApp,
        maxTouchPoints: navigator.maxTouchPoints,
        innerWidth: window.innerWidth
      });
      
      // Consider it mobile if it has mobile user agent OR (small screen AND touch capability)
      return hasMobileUserAgent || (isSmallScreen && hasTouchScreen) || isInApp;
    };
    
    const mobile = isMobile();
    
    console.log('🔍 SMS Debug:', {
      phoneNumber,
      cleanPhone,
      message,
      templateName,
      isMobile: mobile,
      userAgent: navigator.userAgent,
      screenWidth: window.innerWidth,
      hasTouch: 'ontouchstart' in window,
      maxTouchPoints: navigator.maxTouchPoints
    });
    
    // Try multiple SMS methods in order of preference
    const smsMethods = [
      // Method 1: SMS protocol (only on mobile)
      () => {
        if (!mobile) {
          console.log('📱 Skipping SMS protocol - not on mobile device');
          return false;
        }
        
        const smsLink = `sms:${cleanPhone}?body=${encodeURIComponent(message)}`;
        console.log('📱 Trying SMS link:', smsLink);
        
        const link = document.createElement('a');
        link.href = smsLink;
        link.textContent = 'Text Us';
        link.style.display = 'none';
        link.target = '_blank';
        
        // Add to DOM temporarily
        document.body.appendChild(link);
        
        // Try to click it
        try {
          link.click();
          console.log('✅ SMS link clicked successfully');
          return true;
        } catch (error) {
          console.warn('❌ SMS link click failed:', error);
          return false;
        } finally {
          // Clean up
          document.body.removeChild(link);
        }
      },
      
      // Method 2: WhatsApp (works on both mobile and desktop)
      () => {
        const whatsappLink = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
        console.log('📱 Trying WhatsApp with:', whatsappLink);
        window.open(whatsappLink, '_blank');
        return true;
      },
      
      // Method 3: Copy to clipboard with instructions (best for desktop)
      () => {
        const fullMessage = `To: ${cleanPhone}\n\n${message}`;
        console.log('📱 Falling back to clipboard copy');
        
        navigator.clipboard.writeText(fullMessage).then(() => {
          if (mobile) {
            alert(`📱 SMS Template "${templateName}" copied to clipboard!\n\nPhone: ${cleanPhone}\n\nPlease paste this into your SMS app.`);
          } else {
            alert(`📱 SMS Template "${templateName}" copied to clipboard!\n\nPhone: ${cleanPhone}\n\nSince you're on desktop, please:\n1. Copy this message\n2. Use your phone to send the SMS\n3. Or use WhatsApp Web if available`);
          }
        }).catch(() => {
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = fullMessage;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          
          if (mobile) {
            alert(`📱 SMS Template "${templateName}" copied to clipboard!\n\nPhone: ${cleanPhone}\n\nPlease paste this into your SMS app.`);
          } else {
            alert(`📱 SMS Template "${templateName}" copied to clipboard!\n\nPhone: ${cleanPhone}\n\nSince you're on desktop, please:\n1. Copy this message\n2. Use your phone to send the SMS\n3. Or use WhatsApp Web if available`);
          }
        });
        return true;
      }
    ];
    
    // Try each method until one works
    for (let i = 0; i < smsMethods.length; i++) {
      try {
        console.log(`🔄 Trying SMS method ${i + 1}`);
        if (smsMethods[i]()) {
          console.log(`✅ SMS method ${i + 1} succeeded for template: ${templateName}`);
          return;
        }
      } catch (error) {
        console.warn(`❌ SMS method ${i + 1} failed:`, error);
        continue;
      }
    }
    
    // If all methods fail, show error message
    console.error('❌ All SMS methods failed');
    if (mobile) {
      alert(`❌ Unable to send SMS for template "${templateName}".\n\nPhone: ${cleanPhone}\n\nPlease manually send the message to the customer.`);
    } else {
      alert(`❌ Unable to send SMS for template "${templateName}".\n\nPhone: ${cleanPhone}\n\nSince you're on desktop, please use your phone to send the SMS manually.`);
    }
  };

  // SMS Templates state
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [showSmsTemplateModal, setShowSmsTemplateModal] = useState(false);
  const [selectedSmsTemplate, setSelectedSmsTemplate] = useState(null);
  const [smsTemplateForm, setSmsTemplateForm] = useState({
    name: '',
    title: '',
    message: '',
    isActive: true,
    order: 0
  });



  // Load all data on component mount
  useEffect(() => {
    // Check authentication and role
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    
    if (!token) {
      audioToast.error('Please log in to access System Administration');
      return;
    }
    
    if (role !== 'admin') {
      audioToast.error('Admin access required for System Administration');
      return;
    }
    
    loadAllData();
  }, []);

  // Load referral data when the referral program tab is accessed
  useEffect(() => {
    if (activeTab === 'referral-program' && !dataLoaded['referral-program']) {
      // Load referral-specific data here when needed
      // For now, we'll just mark it as loaded
      setDataLoaded(prev => ({
        ...prev,
        'referral-program': true
      }));
    }
  }, [activeTab, dataLoaded]);

  // Mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth <= 767);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);



  const loadAllData = async () => {
    // Prevent multiple simultaneous calls
    if (loading) return;
    
    try {
      setLoading(true);
      
      try {
        // Load job monitors separately to debug any issues
        try {
          const jobMonitorsRes = await axios.get('/job-monitor/admin/all');
          console.log('Job monitors API response:', jobMonitorsRes);
          setJobMonitors(jobMonitorsRes.data.monitors || []);
          setUsersWithoutMonitors(jobMonitorsRes.data.usersWithoutMonitors || []);
        } catch (jobMonitorError) {
          console.error('Error loading job monitors:', jobMonitorError);
          toast.error('Failed to load job monitor data');
          setJobMonitors([]);
          setUsersWithoutMonitors([]);
        }
        
        // Load data with individual error handling
        const loadData = async (endpoint, setter, name) => {
          try {
            const response = await axios.get(endpoint);
            setter(response.data);
            return response.data;
          } catch (error) {
            console.error(`Error loading ${name}:`, error);
            
            // Check if it's an authentication error
            if (error.response?.status === 403) {
              toast.error(`Access denied for ${name}. Please check your admin permissions.`);
            } else if (error.response?.status === 401) {
              toast.error(`Authentication required for ${name}. Please log in again.`);
            } else {
              toast.error(`Failed to load ${name}`);
            }
            
            setter([]);
            return [];
          }
        };

        // Load all data with individual error handling
        await Promise.all([
          loadData('/admin/users', setUsers, 'users'),
          loadData('/admin/leads', setLeads, 'leads'),
          loadData('/admin/tickets', setTickets, 'tickets'),
          loadData('/admin/banners', setBanners, 'banners'),
          loadData('/admin/system-stats', setSystemStats, 'system stats'),
          loadData('/admin/audit-logs', setAuditLogs, 'audit logs'),
          loadData('/admin/users/pending-licensing', setPendingLicensing, 'pending licensing'),
          loadData('/admin/system-settings', (data) => {
            if (data) {
              setSystemSettings(prev => ({ ...prev, ...data }));
            }
          }, 'system settings'),
          loadData('/admin/driver-diary-summary', setDriverDiaries, 'driver diaries'),
          loadData('/taxi-companies', setTaxiCompanies, 'taxi companies'),
          loadData('/sms-templates', setSmsTemplates, 'SMS templates'),
          loadData('/merchandise/orders', setMerchandiseOrders, 'merchandise orders'),
          loadData('/merchandise/stats', setMerchandiseStats, 'merchandise stats'),
          loadData('/merchandise/products', setProducts, 'products'),
          loadData('/licensing-bodies', setLicensingBodies, 'licensing bodies')
        ]);

        // Data is now loaded individually with error handling above
        
        setLoading(false);
        setInitialLoad(false);
      } catch (error) {
        console.error('Error loading system data:', error);
        toast.error('Failed to load system data');
        setLoading(false);
        setInitialLoad(false);
      }
    } catch (error) {
      console.error('Error in loadAllData:', error);
      setLoading(false);
      setInitialLoad(false);
    }
  };


  // User Management Functions
  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/admin/users', userForm);
      setUsers([...users, response.data]);
      setShowUserModal(false);
      setUserForm(createDefaultUserForm());
      setSelectedItem(null);
      audioToast.success('User created successfully');
    } catch (error) {
      console.error('Error creating user:', error);
      audioToast.error('Failed to create user');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(`/admin/users/${selectedItem._id}`, userForm);
      setUsers(users.map(user => user._id === selectedItem._id ? response.data : user));
      setShowUserModal(false);
      setUserForm(createDefaultUserForm());
      setSelectedItem(null);
      toast.success('User updated successfully');
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.error || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await axios.delete(`/admin/users/${userId}`);
        setUsers(users.filter(user => user._id !== userId));
        toast.success('User deleted successfully');
      } catch (error) {
        console.error('Error deleting user:', error);
        toast.error('Failed to delete user');
      }
    }
  };

  // Bulk Actions Functions
  const handleUserSelection = (userId, isSelected) => {
    if (isSelected) {
      setSelectedUsers(prev => [...prev, userId]);
    } else {
      setSelectedUsers(prev => prev.filter(id => id !== userId));
    }
  };

  const handleSelectAllUsers = (isSelected) => {
    if (isSelected) {
      setSelectedUsers(users.map(user => user._id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleBulkAction = async (e) => {
    e.preventDefault();
    
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }

    if (!bulkActionForm.action) {
      toast.error('Please select an action');
      return;
    }

    try {
      const updates = {};
      
      if (bulkActionForm.action === 'changeStatus' && bulkActionForm.newStatus) {
        updates.subscriptionStatus = bulkActionForm.newStatus;
      } else if (bulkActionForm.action === 'changeRole' && bulkActionForm.newRole) {
        updates.role = bulkActionForm.newRole;
      }

      if (Object.keys(updates).length === 0) {
        toast.error('Please select a valid action and value');
        return;
      }

      // Update users in smaller batches to prevent blocking
      const batchSize = 5; // Reduced batch size
      
      for (let i = 0; i < selectedUsers.length; i += batchSize) {
        const batch = selectedUsers.slice(i, i + batchSize);
        
        // Process batch
        await Promise.all(
          batch.map(userId => 
            axios.put(`/admin/users/${userId}`, updates)
          )
        );
        
        // Yield control to browser between batches
        if (i + batchSize < selectedUsers.length) {
          await new Promise(resolve => setTimeout(resolve, 10));
        }
      }

      // Update local state
      setUsers(prevUsers => 
        prevUsers.map(user => 
          selectedUsers.includes(user._id) 
            ? { ...user, ...updates }
            : user
        )
      );

      toast.success(`Successfully updated ${selectedUsers.length} user(s)`);
      setShowBulkActionsModal(false);
      setSelectedUsers([]);
      setBulkActionForm({ action: '', newStatus: '', newRole: '' });
    } catch (error) {
      console.error('Error performing bulk action:', error);
      toast.error('Failed to update users');
    }
  };

  // Licensing Approval Functions
  const handleApproveLicensing = async (userId) => {
    try {
      await axios.post(`/admin/users/${userId}/approve-licensing`);
      setPendingLicensing(pendingLicensing.filter(user => user._id !== userId));
      toast.success('Licensing approved successfully');
    } catch (error) {
      console.error('Error approving licensing:', error);
      toast.error('Failed to approve licensing');
    }
  };

  const handleRejectLicensing = async (userId, reason) => {
    try {
      await axios.post(`/admin/users/${userId}/reject-licensing`, { reason });
      setPendingLicensing(pendingLicensing.filter(user => user._id !== userId));
      toast.success('Licensing rejected successfully');
    } catch (error) {
      console.error('Error rejecting licensing:', error);
      toast.error('Failed to reject licensing');
    }
  };

  const handleAutoDetectProfiles = async () => {
    try {
      const response = await axios.post('/auth/auto-detect-profiles');
      toast.success(response.data.message);
      loadAllData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to auto-detect profiles');
    }
  };

  // Merchandise Order Management Functions
  const handleUpdateOrderStatus = async (orderId) => {
    try {
      await axios.patch(`/merchandise/orders/${orderId}/status`, orderStatusForm);
      toast.success('Order status updated successfully');
      setShowOrderModal(false);
      setSelectedOrder(null);
      loadAllData();
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('Failed to update order status');
    }
  };

  const handleUpdatePaymentStatus = async (orderId) => {
    try {
      await axios.patch(`/merchandise/orders/${orderId}/payment`, paymentForm);
      toast.success('Payment status updated successfully');
      setShowOrderModal(false);
      setSelectedOrder(null);
      loadAllData();
    } catch (error) {
      console.error('Error updating payment status:', error);
      toast.error('Failed to update payment status');
    }
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setOrderStatusForm({
      status: order.status,
      adminNotes: order.adminNotes || '',
      trackingNumber: order.trackingNumber || ''
    });
    setPaymentForm({
      paymentStatus: order.paymentStatus,
      paymentMethod: order.paymentMethod || ''
    });
    setShowOrderModal(true);
  };

  // Licensing Bodies Management Functions
  const handleAddLicensingBody = () => {
    setSelectedLicensingBody(null);
    setLicensingBodyForm({
      code: '',
      name: '',
      fullName: '',
      region: '',
      country: 'England',
      contactEmail: '',
      contactPhone: '',
      website: '',
      postcodes: [],
      active: true,
      notes: '',
      tariffs: []
    });
    setNewPostcodeInput('');
    setShowLicensingBodyModal(true);
  };

  const handleEditLicensingBody = (body) => {
    setSelectedLicensingBody(body);
    setLicensingBodyForm({
      code: body.code || '',
      name: body.name || '',
      fullName: body.fullName || '',
      region: body.region || '',
      country: body.country || 'England',
      contactEmail: body.contactEmail || '',
      contactPhone: body.contactPhone || '',
      website: body.website || '',
      postcodes: body.postcodes || [],
      active: body.active !== undefined ? body.active : true,
      notes: body.notes || '',
      tariffs: body.tariffs || []
    });
    setNewPostcodeInput('');
    setShowLicensingBodyModal(true);
  };

  const handleSaveLicensingBody = async (e) => {
    e.preventDefault();
    try {
      // Clean tariffs - remove temporary IDs and ensure proper data structure
      const cleanedForm = {
        ...licensingBodyForm,
        tariffs: licensingBodyForm.tariffs.map(tariff => {
          const cleanedTariff = {
            name: tariff.name,
            description: tariff.description || '',
            validFrom: tariff.validFrom || null,
            firstMile: parseFloat(tariff.firstMile),
            subsequentMile: parseFloat(tariff.subsequentMile),
            active: tariff.active !== undefined ? tariff.active : true
          };
          
          // Only include _id if it's a real MongoDB ObjectId (not a temporary ID)
          if (tariff._id && !tariff._id.toString().startsWith('temp_')) {
            cleanedTariff._id = tariff._id;
          }
          
          return cleanedTariff;
        })
      };

      if (selectedLicensingBody) {
        // Update existing
        const response = await axios.put(`/licensing-bodies/${selectedLicensingBody._id}`, cleanedForm);
        setLicensingBodies(licensingBodies.map(body => 
          body._id === selectedLicensingBody._id ? response.data : body
        ));
        toast.success('Licensing body updated successfully');
      } else {
        // Create new
        const response = await axios.post('/licensing-bodies', cleanedForm);
        setLicensingBodies([...licensingBodies, response.data]);
        toast.success('Licensing body created successfully');
      }
      setShowLicensingBodyModal(false);
      setSelectedLicensingBody(null);
      setNewPostcodeInput('');
      setLicensingBodyForm({
        code: '',
        name: '',
        fullName: '',
        region: '',
        country: 'England',
        contactEmail: '',
        contactPhone: '',
        website: '',
        postcodes: [],
        active: true,
        notes: '',
        tariffs: []
      });
    } catch (error) {
      console.error('Error saving licensing body:', error);
      toast.error(error.response?.data?.message || 'Failed to save licensing body');
    }
  };

  const handleAddLicensingBodyPostcode = () => {
    if (!newPostcodeInput.trim()) {
      toast.error('Please enter a postcode');
      return;
    }
    
    const postcode = newPostcodeInput.toUpperCase().trim();
    
    if (licensingBodyForm.postcodes.includes(postcode)) {
      toast.error('This postcode is already added');
      return;
    }
    
    setLicensingBodyForm(prev => ({
      ...prev,
      postcodes: [...prev.postcodes, postcode]
    }));
    setNewPostcodeInput('');
  };

  const handleRemoveLicensingBodyPostcode = (postcodeToRemove) => {
    setLicensingBodyForm(prev => ({
      ...prev,
      postcodes: prev.postcodes.filter(pc => pc !== postcodeToRemove)
    }));
  };

  const handleDeleteLicensingBody = async (bodyId) => {
    if (window.confirm('Are you sure you want to delete this licensing body? This will remove all associated tariffs.')) {
      try {
        await axios.delete(`/licensing-bodies/${bodyId}`);
        setLicensingBodies(licensingBodies.filter(body => body._id !== bodyId));
        toast.success('Licensing body deleted successfully');
      } catch (error) {
        console.error('Error deleting licensing body:', error);
        toast.error(error.response?.data?.message || 'Failed to delete licensing body');
      }
    }
  };

  const handleAddTariff = () => {
    setSelectedTariff(null);
    setTariffForm({
      name: '',
      description: '',
      validFrom: '',
      firstMile: '',
      subsequentMile: '',
      active: true
    });
    setShowTariffModal(true);
  };

  const handleEditTariff = (tariff) => {
    setSelectedTariff(tariff);
    setTariffForm({
      name: tariff.name || '',
      description: tariff.description || '',
      validFrom: tariff.validFrom ? new Date(tariff.validFrom).toISOString().split('T')[0] : '',
      firstMile: tariff.firstMile?.toString() || '',
      subsequentMile: tariff.subsequentMile?.toString() || '',
      active: tariff.active !== undefined ? tariff.active : true
    });
    setShowTariffModal(true);
  };

  const handleSaveTariff = () => {
    if (!tariffForm.name || !tariffForm.firstMile || !tariffForm.subsequentMile) {
      toast.error('Please fill in all required fields');
      return;
    }

    const newTariff = {
      _id: selectedTariff?._id || `temp_${Date.now()}`,
      name: tariffForm.name,
      description: tariffForm.description,
      validFrom: tariffForm.validFrom || null,
      firstMile: parseFloat(tariffForm.firstMile),
      subsequentMile: parseFloat(tariffForm.subsequentMile),
      active: tariffForm.active
    };

    if (selectedTariff) {
      // Update existing tariff
      setLicensingBodyForm(prev => ({
        ...prev,
        tariffs: prev.tariffs.map(t => 
          t._id === selectedTariff._id ? newTariff : t
        )
      }));
    } else {
      // Add new tariff
      setLicensingBodyForm(prev => ({
        ...prev,
        tariffs: [...prev.tariffs, newTariff]
      }));
    }

    setShowTariffModal(false);
    setSelectedTariff(null);
    setTariffForm({
      name: '',
      description: '',
      firstMile: '',
      subsequentMile: '',
      active: true
    });
  };

  const handleDeleteTariff = (tariffId) => {
    if (window.confirm('Are you sure you want to delete this tariff?')) {
      setLicensingBodyForm(prev => ({
        ...prev,
        tariffs: prev.tariffs.filter(t => t._id !== tariffId)
      }));
    }
  };

  // Product Management Functions
  const handleAddProduct = () => {
    setSelectedProduct(null);
    setProductForm({
      name: '',
      description: '',
      price: '',
      category: '',
      image: null,
      imagePreview: '',
      isActive: true,
      stockQuantity: '',
      units: '1',
      specifications: ''
    });
    setProductImageFile(null);
    setShowProductModal(true);
  };

  const handleEditProduct = (product) => {
    setSelectedProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      image: product.image,
      imagePreview: product.image || '', // Show preview for all images
      isActive: product.isActive,
      stockQuantity: product.stockQuantity?.toString() || '',
      units: product.units || '1',
      specifications: product.specifications || ''
    });
    setProductImageFile(null);
    setShowProductModal(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      
      setProductImageFile(file);
      
      // Use requestAnimationFrame to make FileReader operation asynchronous
      requestAnimationFrame(() => {
        const reader = new FileReader();
        reader.onload = (e) => {
          setProductForm(prev => ({ ...prev, imagePreview: e.target.result }));
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    
    try {
      const productData = {
        name: productForm.name,
        description: productForm.description,
        price: parseFloat(productForm.price),
        category: productForm.category,
        isActive: productForm.isActive,
        stockQuantity: parseInt(productForm.stockQuantity) || 0,
        units: productForm.units,
        specifications: productForm.specifications
      };
      
      // Add image if available (base64 data URL)
      if (productForm.imagePreview) {
        productData.image = productForm.imagePreview;
      }

      if (selectedProduct) {
        const response = await axios.put(`/merchandise/products/${selectedProduct._id}`, productData);
        toast.success('Product updated successfully');
      } else {
        const response = await axios.post('/merchandise/products', productData);
        toast.success('Product created successfully');
      }

      setShowProductModal(false);
      setSelectedProduct(null);
      setProductForm({
        name: '',
        description: '',
        price: '',
        category: 'business-cards',
        image: '',
        imagePreview: '',
        isActive: true,
        stockQuantity: '',
        units: '1',
        specifications: ''
      });
      setProductImageFile(null);
      loadAllData();
    } catch (error) {
      console.error('Error saving product:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      toast.error('Failed to save product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await axios.delete(`/merchandise/products/${productId}`);
        toast.success('Product deleted successfully');
        loadAllData();
      } catch (error) {
        console.error('Error deleting product:', error);
        toast.error('Failed to delete product');
      }
    }
  };

  // Driver Diary Functions
  const handleViewDriverDiary = async (driver, filters = {}) => {
    try {
      setDriverDiaryLoading(true);
      setSelectedDriverDiary(driver);
      
      // Use default filter if no filters provided
      const effectiveFilters = Object.keys(filters).length === 0 ? driverDiaryFilter : filters;
      
      // Build query parameters
      const params = new URLSearchParams();
      if (effectiveFilters.status) params.append('status', effectiveFilters.status);
      if (effectiveFilters.startDate) params.append('startDate', effectiveFilters.startDate);
      if (effectiveFilters.endDate) params.append('endDate', effectiveFilters.endDate);
      
      const response = await axios.get(`/admin/driver-diary/${driver._id}?${params.toString()}`);
      const entries = response.data.entries;
      
      // Add driverId to each entry for filtering in driver view
      const entriesWithDriverId = entries.map(entry => ({
        ...entry,
        driverId: driver._id
      }));
      
      setDriverDiaryEntries(entries);
      setSelectedDriverJobs(prev => {
        // Remove existing entries for this driver and add new ones
        const filtered = prev.filter(job => job.driverId !== driver._id);
        return [...filtered, ...entriesWithDriverId];
      });
      setShowDriverDiaryModal(true);
    } catch (error) {
      console.error('Error loading driver diary:', error);
      toast.error('Failed to load driver diary');
    } finally {
      setDriverDiaryLoading(false);
    }
  };

  const handleApplyDiaryFilter = async () => {
    if (selectedDriverDiary) {
      await handleViewDriverDiary(selectedDriverDiary, driverDiaryFilter);
    }
  };

  const handleRefreshDriverDiary = async () => {
    if (selectedDriverDiary) {
      await handleViewDriverDiary(selectedDriverDiary);
    }
  };

  // Lead Management Functions
  const handleCreateLead = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/admin/leads', leadForm);
      setLeads([...leads, response.data]);
      setShowLeadModal(false);
      setLeadForm({
        customerName: '', customerEmail: '', customerPhone: '',
        from: '', to: '', dateTime: '', passengers: 1,
        status: 'pending', returnTrip: false
      });
      setSelectedItem(null);
      toast.success('Lead created successfully');
    } catch (error) {
      console.error('Error creating lead:', error);
      toast.error('Failed to create lead');
    }
  };

  const handleSubmitLead = async (e) => {
    e.preventDefault();
    if (selectedItem) {
      // Update existing lead
      try {
        const response = await axios.put(`/admin/leads/${selectedItem._id}`, leadForm);
        setLeads(leads.map(lead => lead._id === selectedItem._id ? response.data : lead));
        setShowLeadModal(false);
        setLeadForm({
          customerName: '', customerEmail: '', customerPhone: '',
          from: '', to: '', dateTime: '', passengers: 1,
          status: 'pending', returnTrip: false
        });
        setSelectedItem(null);
        toast.success('Lead updated successfully');
      } catch (error) {
        console.error('Error updating lead:', error);
        toast.error('Failed to update lead');
      }
    } else {
      // Create new lead
      await handleCreateLead(e);
    }
  };

  const handleUpdateLead = async (leadId, updates) => {
    try {
      const response = await axios.put(`/admin/leads/${leadId}`, updates);
      setLeads(leads.map(lead => lead._id === leadId ? response.data : lead));
      toast.success('Lead updated successfully');
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error('Failed to update lead');
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (window.confirm('Are you sure you want to delete this lead?')) {
      try {
        await axios.delete(`/admin/leads/${leadId}`);
        setLeads(leads.filter(lead => lead._id !== leadId));
        toast.success('Lead deleted successfully');
      } catch (error) {
        console.error('Error deleting lead:', error);
        toast.error('Failed to delete lead');
      }
    }
  };

  // Taxi Company Management Functions
  const handleCreateTaxiCompany = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/taxi-companies', taxiCompanyForm);
      setTaxiCompanies([...taxiCompanies, response.data]);
      setShowTaxiCompanyModal(false);
      setTaxiCompanyForm({
        company: '',
        phone: '',
        email: '',
        postcodes: [''],
        notes: ''
      });
      setSelectedItem(null);
      toast.success('Taxi company created successfully');
    } catch (error) {
      console.error('Error creating taxi company:', error);
      toast.error(error.response?.data?.message || 'Failed to create taxi company');
    }
  };

  const handleUpdateTaxiCompany = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(`/taxi-companies/${selectedItem._id}`, taxiCompanyForm);
      setTaxiCompanies(taxiCompanies.map(company => company._id === selectedItem._id ? response.data : company));
      setShowTaxiCompanyModal(false);
      setTaxiCompanyForm({
        company: '',
        phone: '',
        email: '',
        postcodes: [''],
        notes: ''
      });
      setSelectedItem(null);
      toast.success('Taxi company updated successfully');
    } catch (error) {
      console.error('Error updating taxi company:', error);
      toast.error(error.response?.data?.message || 'Failed to update taxi company');
    }
  };

  const handleDeleteTaxiCompany = async (companyId) => {
    if (window.confirm('Are you sure you want to delete this taxi company?')) {
      try {
        await axios.delete(`/taxi-companies/${companyId}`);
        setTaxiCompanies(taxiCompanies.filter(company => company._id !== companyId));
        toast.success('Taxi company deleted successfully');
      } catch (error) {
        console.error('Error deleting taxi company:', error);
        toast.error('Failed to delete taxi company');
      }
    }
  };

  const handleEditTaxiCompany = (company) => {
    setSelectedItem(company);
    setTaxiCompanyForm({
      company: company.company,
      phone: company.phone,
      email: company.email || '',
      postcodes: company.postcodes.length > 0 ? company.postcodes : [''],
      notes: company.notes || ''
    });
    setShowTaxiCompanyModal(true);
  };

  const addPostcodeField = () => {
    setTaxiCompanyForm(prev => ({
      ...prev,
      postcodes: [...prev.postcodes, '']
    }));
  };

  const removePostcodeField = (index) => {
    setTaxiCompanyForm(prev => ({
      ...prev,
      postcodes: prev.postcodes.filter((_, i) => i !== index)
    }));
  };

  const updatePostcode = (index, value) => {
    setTaxiCompanyForm(prev => ({
      ...prev,
      postcodes: prev.postcodes.map((pc, i) => i === index ? value : pc)
    }));
  };

  // CSV Import Functions
  const handleCsvFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setCsvFile(file);
      parseCsvFile(file);
    } else {
      toast.error('Please select a valid CSV file');
    }
  };

  const parseCsvFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csv = e.target.result;
        const lines = csv.split('\n');
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        
        // Validate headers
        const requiredHeaders = ['company', 'phone'];
        const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
        
        if (missingHeaders.length > 0) {
          toast.error(`Missing required headers: ${missingHeaders.join(', ')}`);
          return;
        }

        const data = [];
        for (let i = 1; i < lines.length; i++) {
          if (lines[i].trim()) {
            const values = lines[i].split(',').map(v => v.trim());
            const row = {};
            headers.forEach((header, index) => {
              row[header] = values[index] || '';
            });
            data.push(row);
          }
        }

        setCsvPreview(data.slice(0, 5)); // Show first 5 rows as preview
        toast.success(`CSV parsed successfully. ${data.length} rows found.`);
      } catch (error) {
        console.error('Error parsing CSV:', error);
        toast.error('Error parsing CSV file');
      }
    };
    reader.readAsText(file);
  };

  const handleCsvImport = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file first');
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const csv = e.target.result;
          const lines = csv.split('\n');
          const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
          
          const data = [];
          for (let i = 1; i < lines.length; i++) {
            if (lines[i].trim()) {
              const values = lines[i].split(',').map(v => v.trim());
              const row = {};
              headers.forEach((header, index) => {
                row[header] = values[index] || '';
              });
              data.push(row);
            }
          }

          const response = await axios.post('/taxi-companies/import-csv', { csvData: data });
          setImportResults(response.data);
          
          if (response.data.results.success.length > 0) {
            // Refresh the taxi companies list
            const updatedResponse = await axios.get('/taxi-companies');
            setTaxiCompanies(updatedResponse.data);
          }
          
          toast.success(response.data.message);
        } catch (error) {
          console.error('Error importing CSV:', error);
          toast.error(error.response?.data?.message || 'Failed to import CSV');
        }
      };
      reader.readAsText(csvFile);
    } catch (error) {
      console.error('Error reading CSV file:', error);
      toast.error('Error reading CSV file');
    }
  };

    // Banner Management Functions
  const handleCreateBanner = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      Object.keys(bannerForm).forEach(key => {
        if (key === 'imageFile' && bannerForm[key]) {
          formData.append('image', bannerForm[key]);
        } else {
          formData.append(key, bannerForm[key]);
        }
      });

      const response = await axios.post('/admin/banners', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setBanners([...banners, response.data]);
      setShowBannerModal(false);
      setBannerForm({
        title: '', message: '', audience: 'all',
        startsAt: '', endsAt: '', isActive: true, priority: 0,
        targetPostcodes: '', rotationInterval: 5, imageFile: null,
        imagePreview: null, imageLoading: false
      });
      toast.success('Banner created successfully');
    } catch (error) {
      console.error('Error creating banner:', error);
      toast.error('Failed to create banner');
    }
  };

  // Image resizing utility function
  const resizeImageToBannerSize = (file) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Target dimensions (430mm x 175mm at 96 DPI)
        const targetWidth = 1622; // 430mm * 96 DPI / 25.4
        const targetHeight = 661;  // 175mm * 96 DPI / 25.4
        
        // Calculate aspect ratio
        const aspectRatio = img.width / img.height;
        const targetAspectRatio = targetWidth / targetHeight;
        
        let finalWidth, finalHeight;
        
        if (aspectRatio > targetAspectRatio) {
          // Image is wider than target - fit to height
          finalHeight = targetHeight;
          finalWidth = targetHeight * aspectRatio;
        } else {
          // Image is taller than target - fit to width
          finalWidth = targetWidth;
          finalHeight = targetWidth / aspectRatio;
        }
        
        // Set canvas size
        canvas.width = finalWidth;
        canvas.height = finalHeight;
        
        // Draw resized image
        ctx.drawImage(img, 0, 0, finalWidth, finalHeight);
        
        // Convert to blob
        canvas.toBlob((blob) => {
          // Create new file with resized image
          const resizedFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now()
          });
          resolve(resizedFile);
        }, file.type, 0.9);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const handleEditBanner = (banner) => {
    setSelectedItem(banner);
    setBannerForm({
      title: banner.title,
      message: banner.message,
      audience: banner.audience,
      startsAt: banner.startsAt ? new Date(banner.startsAt).toISOString().slice(0, 16) : '',
      endsAt: banner.endsAt ? new Date(banner.endsAt).toISOString().slice(0, 16) : '',
      isActive: banner.isActive,
      priority: banner.priority,
      targetPostcodes: Array.isArray(banner.targetPostcodes) ? banner.targetPostcodes.join(', ') : banner.targetPostcodes || '',
      rotationInterval: banner.rotationInterval || 5,
      imageFile: null,
      imagePreview: null,
      imageLoading: false
    });
    setShowBannerModal(true);
  };

  const handleUpdateBanner = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      Object.keys(bannerForm).forEach(key => {
        if (key === 'imageFile' && bannerForm[key]) {
          formData.append('image', bannerForm[key]);
        } else {
          formData.append(key, bannerForm[key]);
        }
      });

      const response = await axios.put(`/admin/banners/${selectedItem._id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setBanners(banners.map(banner => banner._id === selectedItem._id ? response.data : banner));
      setShowBannerModal(false);
      setBannerForm({
        title: '', message: '', audience: 'all',
        startsAt: '', endsAt: '', isActive: true, priority: 0,
        targetPostcodes: '', rotationInterval: 5, imageFile: null,
        imagePreview: null, imageLoading: false
      });
      setSelectedItem(null);
      toast.success('Banner updated successfully');
    } catch (error) {
      console.error('Error updating banner:', error);
      toast.error('Failed to update banner');
    }
  };

  const handleDeleteBanner = async (bannerId) => {
    if (window.confirm('Are you sure you want to delete this banner?')) {
      try {
        await axios.delete(`/admin/banners/${bannerId}`);
        setBanners(banners.filter(banner => banner._id !== bannerId));
        toast.success('Banner deleted successfully');
      } catch (error) {
        console.error('Error deleting banner:', error);
        toast.error('Failed to delete banner');
      }
    }
  };

  // System Settings Functions
  const handleUpdateSystemSettings = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put('/admin/system-settings', systemSettings);
      setSystemSettings(response.data);
      setShowSystemSettingsModal(false);
      toast.success('System settings updated successfully');
    } catch (error) {
      console.error('Error updating system settings:', error);
      toast.error('Failed to update system settings');
    }
  };

  // Ticket Management Functions
  const handleViewTicket = (ticket) => {
    setSelectedItem(ticket);
    setTicketForm({
      title: ticket.title,
      description: ticket.description,
      status: ticket.status,
      priority: ticket.priority,
      response: ''
    });
    setTicketResponses(ticket.responses || []);
    setShowTicketModal(true);
  };

  const handleUpdateTicket = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(`/admin/tickets/${selectedItem._id}`, ticketForm);
      setTickets(tickets.map(ticket => ticket._id === selectedItem._id ? response.data : ticket));
      
      // Update the responses if a new response was added
      if (ticketForm.response && ticketForm.response.trim() !== '') {
        setTicketResponses(response.data.responses || []);
        setTicketForm(prev => ({ ...prev, response: '' }));
      } else {
        setShowTicketModal(false);
        setTicketForm({
          title: '',
          description: '',
          status: 'open',
          priority: 'medium',
          response: ''
        });
        setSelectedItem(null);
        setTicketResponses([]);
      }
      
      toast.success('Ticket updated successfully');
    } catch (error) {
      console.error('Error updating ticket:', error);
      toast.error('Failed to update ticket');
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    if (window.confirm('Are you sure you want to delete this ticket?')) {
      try {
        await axios.delete(`/admin/tickets/${ticketId}`);
        setTickets(tickets.filter(ticket => ticket._id !== ticketId));
        toast.success('Ticket deleted successfully');
      } catch (error) {
        console.error('Error deleting ticket:', error);
        toast.error('Failed to delete ticket');
      }
    }
  };

  // Job Monitor Management Functions
  const handleViewJobMonitor = async (user) => {
    try {
      const response = await axios.get(`/job-monitor/admin/user/${user._id}`);
      setSelectedJobMonitor(response.data);
      setJobMonitorForm({
        isActive: response.data.monitor?.isActive || false,
        postcodeAreas: response.data.monitor?.postcodeAreas || [],
        alertSettings: response.data.monitor?.alertSettings || {
          screenFlash: true,
          soundAlert: true,
          emailAlert: false,
          checkInterval: 5,
          autoStopAfterMinutes: 30
        }
      });
      setShowJobMonitorModal(true);
    } catch (error) {
      console.error('Error loading job monitor:', error);
      toast.error('Failed to load job monitor settings');
    }
  };

  const handleCreateJobMonitor = async (user) => {
    try {
      const response = await axios.post(`/job-monitor/admin/user/${user._id}`, {
        isActive: true,
        postcodeAreas: user.postcodeSectorsCovered || [user.mainPostcode].filter(Boolean),
        alertSettings: {
          screenFlash: true,
          soundAlert: true,
          emailAlert: false,
          checkInterval: 5,
          autoStopAfterMinutes: 30
        }
      });
      
      // Refresh job monitors data
      const monitorsResponse = await axios.get('/job-monitor/admin/all');
      setJobMonitors(monitorsResponse.data.monitors || []);
      setUsersWithoutMonitors(monitorsResponse.data.usersWithoutMonitors || []);
      
      toast.success(`Job monitor created for ${user.name}`);
    } catch (error) {
      console.error('Error creating job monitor:', error);
      toast.error('Failed to create job monitor');
    }
  };

  const handleUpdateJobMonitor = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`/job-monitor/admin/user/${selectedJobMonitor.user._id}`, jobMonitorForm);
      
      // Refresh job monitors data
      const monitorsResponse = await axios.get('/job-monitor/admin/all');
      setJobMonitors(monitorsResponse.data.monitors || []);
      setUsersWithoutMonitors(monitorsResponse.data.usersWithoutMonitors || []);
      
      setShowJobMonitorModal(false);
      setSelectedJobMonitor(null);
      toast.success('Job monitor settings updated successfully');
    } catch (error) {
      console.error('Error updating job monitor:', error);
      toast.error('Failed to update job monitor settings');
    }
  };

  const handleDeleteJobMonitor = async (userId) => {
    if (window.confirm('Are you sure you want to delete this job monitor?')) {
      try {
        await axios.delete(`/job-monitor/admin/user/${userId}`);
        
        // Refresh job monitors data
        const monitorsResponse = await axios.get('/job-monitor/admin/all');
        setJobMonitors(monitorsResponse.data.monitors || []);
        setUsersWithoutMonitors(monitorsResponse.data.usersWithoutMonitors || []);
        
        toast.success('Job monitor deleted successfully');
      } catch (error) {
        console.error('Error deleting job monitor:', error);
        toast.error('Failed to delete job monitor');
      }
    }
  };



  const handleAddPostcode = () => {
    if (newPostcode.trim() && !jobMonitorForm.postcodeAreas.includes(newPostcode.trim().toUpperCase())) {
      setJobMonitorForm(prev => ({
        ...prev,
        postcodeAreas: [...prev.postcodeAreas, newPostcode.trim().toUpperCase()]
      }));
      setNewPostcode('');
    }
  };

  const handleRemovePostcode = (index) => {
    setJobMonitorForm(prev => ({
      ...prev,
      postcodeAreas: prev.postcodeAreas.filter((_, i) => i !== index)
    }));
  };

  // Copy Monitor Areas Functions
  const handleCopyMonitorAreas = (sourceMonitor) => {
    setSourceDriver(sourceMonitor);
    setSelectedTargetDrivers([]);
    setShowCopyMonitorModal(true);
  };

  const handleSelectTargetDriver = (driverId) => {
    setSelectedTargetDrivers(prev => {
      if (prev.includes(driverId)) {
        return prev.filter(id => id !== driverId);
      } else {
        return [...prev, driverId];
      }
    });
  };

  const handleSelectAllTargetDrivers = () => {
    const allDriverIds = [...jobMonitors, ...usersWithoutMonitors]
      .filter(driver => {
        const driverId = driver._id || driver.userId?._id;
        return driverId && driverId !== sourceDriver?._id;
      })
      .map(driver => {
        const isJobMonitor = driver.userId !== undefined;
        return isJobMonitor ? driver.userId._id : driver._id;
      });
    
    setSelectedTargetDrivers(allDriverIds);
  };

  const handleClearTargetDrivers = () => {
    setSelectedTargetDrivers([]);
  };

  const handleExecuteCopy = async () => {
    if (!sourceDriver || selectedTargetDrivers.length === 0) {
      toast.error('Please select a source driver and at least one target driver');
      return;
    }

    setCopyInProgress(true);
    try {
      // Get the source driver's monitored areas
      const sourceMonitor = jobMonitors.find(m => m.userId._id === sourceDriver.userId._id);
      const sourceAreas = sourceMonitor?.postcodeAreas || [];

      if (sourceAreas.length === 0) {
        toast.error('Source driver has no monitored areas to copy');
        return;
      }

      // Copy to each target driver
      const copyPromises = selectedTargetDrivers.map(async (driverId) => {
        try {
          // Check if driver already has a monitor
          const existingMonitor = jobMonitors.find(m => m.userId._id === driverId);
          
          if (existingMonitor) {
            // Update existing monitor
            await axios.post(`/job-monitor/admin/user/${driverId}`, {
              postcodeAreas: sourceAreas,
              isActive: existingMonitor.isActive,
              alertSettings: existingMonitor.alertSettings
            });
          } else {
            // Create new monitor
            const targetDriver = usersWithoutMonitors.find(u => u._id === driverId);
            await axios.post(`/job-monitor/admin/user/${driverId}`, {
              isActive: true,
              postcodeAreas: sourceAreas,
              alertSettings: {
                screenFlash: true,
                soundAlert: true,
                emailAlert: false,
                checkInterval: 5,
                autoStopAfterMinutes: 30
              }
            });
          }
          return { success: true, driverId };
        } catch (error) {
          console.error(`Error copying to driver ${driverId}:`, error);
          return { success: false, driverId, error: error.message };
        }
      });

      const results = await Promise.all(copyPromises);
      const successful = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success).length;

      // Refresh job monitors data
      const monitorsResponse = await axios.get('/job-monitor/admin/all');
      setJobMonitors(monitorsResponse.data.monitors || []);
      setUsersWithoutMonitors(monitorsResponse.data.usersWithoutMonitors || []);

      // Show results
      if (successful > 0) {
        toast.success(`Successfully copied monitored areas to ${successful} driver(s)`);
      }
      if (failed > 0) {
        toast.error(`Failed to copy to ${failed} driver(s)`);
      }

      setShowCopyMonitorModal(false);
      setSourceDriver(null);
      setSelectedTargetDrivers([]);
    } catch (error) {
      console.error('Error copying monitored areas:', error);
      toast.error('Failed to copy monitored areas');
    } finally {
      setCopyInProgress(false);
    }
  };

  // Utility Functions
  const formatDate = (dateString) => {
    try {
      if (!dateString) return '-';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.warn('Invalid date in formatDate:', dateString);
        return 'Invalid Date';
      }
      return date.toLocaleString('en-GB');
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return 'Invalid Date';
    }
  };

  // Driver Diary View Helper Functions
  const formatWhen = (iso) => {
    try {
      const date = new Date(iso);
      if (isNaN(date.getTime())) {
        console.warn('Invalid date in formatWhen:', iso);
        return 'Invalid Date';
      }
      const parts = new Intl.DateTimeFormat('en-GB', {
        hour: 'numeric', minute: '2-digit', hour12: true,
        weekday: 'short', day: 'numeric', month: 'short',
        timeZone: 'Europe/London'
      }).formatToParts(date);
      const p = Object.fromEntries(parts.map(x => [x.type, x.value]));
      return `${p.hour}:${p.minute} ${p.dayPeriod?.toLowerCase()}, ${p.weekday} ${p.day} ${p.month}`;
    } catch (error) {
      console.warn('Error formatting date:', iso, error);
      return 'Invalid Date';
    }
  };

  const extractTowns = (a, b) => {
    const town = s => s.split(',').slice(-2, -1)[0]?.trim() || s;
    return `${town(a)} → ${town(b)}`;
  };

  const organizeJobsByTimePeriod = (jobs) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);
    const restOfWeek = new Date(today);
    restOfWeek.setDate(restOfWeek.getDate() + 2);

    return {
      yesterday: jobs.filter(job => {
        const jobDate = new Date(job.dateTime);
        const jobDay = new Date(jobDate.getFullYear(), jobDate.getMonth(), jobDate.getDate());
        return jobDay.getTime() === today.getTime() - 24 * 60 * 60 * 1000;
      }),
      today: jobs.filter(job => {
        const jobDate = new Date(job.dateTime);
        const jobDay = new Date(jobDate.getFullYear(), jobDate.getMonth(), jobDate.getDate());
        return jobDay.getTime() === today.getTime();
      }),
      tomorrow: jobs.filter(job => {
        const jobDate = new Date(job.dateTime);
        const jobDay = new Date(jobDate.getFullYear(), jobDate.getMonth(), jobDate.getDate());
        return jobDay.getTime() === tomorrow.getTime();
      }),
      restOfWeek: jobs.filter(job => {
        const jobDate = new Date(job.dateTime);
        const jobDay = new Date(jobDate.getFullYear(), jobDate.getMonth(), jobDate.getDate());
        return jobDay.getTime() >= restOfWeek.getTime() && jobDay.getTime() < nextWeek.getTime();
      }),
      nextWeek: jobs.filter(job => {
        const jobDate = new Date(job.dateTime);
        const jobDay = new Date(jobDate.getFullYear(), jobDate.getMonth(), jobDate.getDate());
        return jobDay.getTime() >= nextWeek.getTime() && jobDay.getTime() < nextWeek.getTime() + 7 * 24 * 60 * 60 * 1000;
      }),
      future: jobs.filter(job => {
        const jobDate = new Date(job.dateTime);
        const jobDay = new Date(jobDate.getFullYear(), jobDate.getMonth(), jobDate.getDate());
        return jobDay.getTime() >= nextWeek.getTime() + 7 * 24 * 60 * 60 * 1000;
      })
    };
  };

  const renderDriverJobItem = (job, idx) => {
    return (
      <Accordion.Item eventKey={String(idx)} key={job._id}>
        <Accordion.Header>
          <div className="d-flex flex-column w-100">
            <div className="d-flex align-items-center gap-2">
              <strong>{formatWhen(job.dateTime)}</strong>
              {job.status === 'confirmed' && (
                <span className="badge bg-success" title="Confirmed with customer">
                  ✅ Confirmed
                </span>
              )}
            </div>
            <small>{extractTowns(job.from, job.to)}</small>
          </div>
        </Accordion.Header>
        <Accordion.Body>
          <p><strong>Customer:</strong> {job.customerName}</p>
          <p><strong>Route:</strong> {job.from} → {job.to}</p>
          {job.mileage && <p><strong>Distance:</strong> {job.mileage} miles</p>}
          {job.duration && <p><strong>Duration:</strong> {job.duration} minutes</p>}
          <p><strong>Passengers:</strong> {job.passengers}</p>
          <p>
            <strong>Phone:</strong>{' '}
            <div className="btn-group">
              <a href={`tel:${job.customerPhone}`} className="btn btn-sm btn-outline-primary">
                📞 {job.customerPhone}
              </a>
              <button 
                onClick={() => sendSMSWithFallback(job.customerPhone, 'Hello! This is regarding your taxi booking.', 'Quick Message')}
                className="btn btn-sm btn-outline-secondary"
              >
                ✉️
              </button>
            </div>
          </p>
          <p><strong>Price:</strong> £{job.fareAmount}</p>
          {(job.dog || job.disabled || job.notes) && (
            <div className="mt-2">
              {job.dog && <span className="badge bg-warning me-2">🐕 Dog/Pet</span>}
              {job.disabled && <span className="badge bg-info me-2">♿ Disabled Access</span>}
              {job.notes && (
                <div className="mt-2">
                  <strong>Notes:</strong> <span className="text-muted">{job.notes}</span>
                </div>
              )}
            </div>
          )}
          <div className="mt-3">
            <strong>Status:</strong> {getStatusBadge(job.status)}
          </div>
        </Accordion.Body>
      </Accordion.Item>
    );
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'success',
      inactive: 'secondary',
      pending: 'warning',
      completed: 'success',
      cancelled: 'danger',
      open: 'danger',
      'in-progress': 'warning',
      resolved: 'success',
      closed: 'secondary'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status.toUpperCase()}</Badge>;
  };

  const getRoleBadge = (role) => {
    const variants = {
      admin: 'danger',
      professional: 'primary',
      listing_only: 'info',
      dispatch: 'warning',
      user: 'secondary'
    };
    return <Badge bg={variants[role] || 'secondary'}>{role.toUpperCase()}</Badge>;
  };

  // User sorting functions
  const requestUserSort = (key) => {
    setUserSortConfig(cfg => ({
      key,
      direction: cfg.key === key && cfg.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getUserSortIcon = (key) => {
    if (userSortConfig.key === key) {
      return userSortConfig.direction === 'asc' ? ' ▲' : ' ▼';
    }
    return ' ⇅';
  };

  const getSortedUsers = () => {
    const sorted = [...users].sort((a, b) => {
      const { key, direction } = userSortConfig;
      let aVal = a[key];
      let bVal = b[key];

      // Handle name field (can be firstName + surname or just name)
      if (key === 'name') {
        aVal = a.firstName && a.surname ? `${a.firstName} ${a.surname}` : a.name || '';
        bVal = b.firstName && b.surname ? `${b.firstName} ${b.surname}` : b.name || '';
      }

      // Handle empty/null values
      if (!aVal) aVal = '';
      if (!bVal) bVal = '';

      // Convert to lowercase strings for comparison
      const aStr = aVal.toString().toLowerCase();
      const bStr = bVal.toString().toLowerCase();

      if (aStr < bStr) return direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  };

  // Search and bulk action functions
  const filteredLeads = leads.filter(lead => {
    const searchLower = leadSearchTerm.toLowerCase();
    return (
      lead.customerName?.toLowerCase().includes(searchLower) ||
      lead.customerEmail?.toLowerCase().includes(searchLower) ||
      lead.customerPhone?.toLowerCase().includes(searchLower) ||
      lead.from?.toLowerCase().includes(searchLower) ||
      lead.to?.toLowerCase().includes(searchLower) ||
      lead.status?.toLowerCase().includes(searchLower) ||
      lead.acceptedBy?.name?.toLowerCase().includes(searchLower) ||
      lead.acceptedBy?.email?.toLowerCase().includes(searchLower)
    );
  });

  // Sort filtered leads
  const sortedLeads = [...filteredLeads].sort((a, b) => {
    let aValue = a[leadSortField];
    let bValue = b[leadSortField];
    
    // Handle date fields
    if (leadSortField === 'dateTime') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }
    
    // Handle null/undefined values
    if (aValue === null || aValue === undefined) aValue = '';
    if (bValue === null || bValue === undefined) bValue = '';
    
    // Convert to strings for comparison
    aValue = String(aValue).toLowerCase();
    bValue = String(bValue).toLowerCase();
    
    if (leadSortDirection === 'asc') {
      return aValue.localeCompare(bValue);
    } else {
      return bValue.localeCompare(aValue);
    }
  });

  // Column sorting functions
  const handleSort = (field) => {
    if (leadSortField === field) {
      setLeadSortDirection(leadSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setLeadSortField(field);
      setLeadSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (leadSortField !== field) {
      return <span className="text-muted">↕️</span>;
    }
    return leadSortDirection === 'asc' ? <span>↑</span> : <span>↓</span>;
  };

  // Column management functions
  const availableColumns = [
    { key: 'customerName', label: 'Customer Name', type: 'text' },
    { key: 'customerEmail', label: 'Customer Email', type: 'text' },
    { key: 'customerPhone', label: 'Customer Phone', type: 'text' },
    { key: 'from', label: 'From Location', type: 'text' },
    { key: 'to', label: 'To Location', type: 'text' },
    { key: 'dateTime', label: 'Date/Time', type: 'date' },
    { key: 'status', label: 'Status', type: 'status' },
    { key: 'passengers', label: 'Passengers', type: 'number' },
    { key: 'returnTrip', label: 'Return Trip', type: 'boolean' },
    { key: 'fareAmount', label: 'Fare Amount', type: 'text' },
    { key: 'paymentMethod', label: 'Payment Method', type: 'text' },
    { key: 'dog', label: 'Dog', type: 'boolean' },
    { key: 'disabled', label: 'Disabled', type: 'boolean' },
    { key: 'notes', label: 'Notes', type: 'text' },
    { key: 'statusNote', label: 'Status Note', type: 'text' },
    { key: 'mileage', label: 'Mileage', type: 'number' },
    { key: 'duration', label: 'Duration', type: 'number' },
    { key: 'acceptedBy', label: 'Driver/Company Name', type: 'user' },
    { key: 'acceptedAt', label: 'Accepted At', type: 'date' },
    { key: 'dateSubmitted', label: 'Date Submitted', type: 'date' }
  ];

  const handleColumnToggle = (columnKey) => {
    setVisibleColumns(prev => 
      prev.includes(columnKey)
        ? prev.filter(col => col !== columnKey)
        : [...prev, columnKey]
    );
  };

  const handleSelectAllColumns = () => {
    setVisibleColumns(availableColumns.map(col => col.key));
  };

  const handleDeselectAllColumns = () => {
    setVisibleColumns([]);
  };

  const getColumnValue = (lead, columnKey) => {
    const value = lead[columnKey];
    
    switch (columnKey) {
      case 'dateTime':
      case 'acceptedAt':
      case 'dateSubmitted':
        return value ? formatDate(value) : '-';
      case 'returnTrip':
      case 'dog':
      case 'disabled':
        return value ? 'Yes' : 'No';
      case 'passengers':
      case 'mileage':
      case 'duration':
        return value || '-';
      case 'fareAmount':
        return value ? `£${value}` : '-';
      case 'notes':
      case 'statusNote':
        return value ? (value.length > 30 ? `${value.substring(0, 30)}...` : value) : '-';
      case 'acceptedBy':
        return value ? (value.name || value.email || 'Unknown Driver') : '-';
      default:
        return value || '-';
    }
  };

  const handleLeadSelection = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleSelectAllLeads = () => {
    if (selectAllLeads) {
      setSelectedLeads([]);
      setSelectAllLeads(false);
    } else {
      setSelectedLeads(filteredLeads.map(lead => lead._id));
      setSelectAllLeads(true);
    }
  };

  const handleBulkDeleteLeads = async () => {
    if (selectedLeads.length === 0) {
      toast.warning('Please select leads to delete');
      return;
    }

    if (window.confirm(`Are you sure you want to delete ${selectedLeads.length} selected leads?`)) {
      try {
        await Promise.all(selectedLeads.map(leadId => axios.delete(`/admin/leads/${leadId}`)));
        toast.success(`${selectedLeads.length} leads deleted successfully`);
        setSelectedLeads([]);
        setSelectAllLeads(false);
        loadAllData(); // Reload data
      } catch (error) {
        toast.error('Failed to delete some leads');
        console.error('Error deleting leads:', error);
      }
    }
  };

  const handleBulkStatusUpdate = async (newStatus) => {
    if (selectedLeads.length === 0) {
      toast.warning('Please select leads to update');
      return;
    }

    try {
      await Promise.all(selectedLeads.map(leadId => 
        axios.put(`/admin/leads/${leadId}`, { status: newStatus })
      ));
      toast.success(`${selectedLeads.length} leads updated to ${newStatus}`);
      setSelectedLeads([]);
      setSelectAllLeads(false);
      loadAllData(); // Reload data
    } catch (error) {
      toast.error('Failed to update some leads');
      console.error('Error updating leads:', error);
    }
  };

  // Email Template Handlers
  const handleSaveEmailTemplate = async () => {
    try {
      setSavingTemplate(true);
      await axios.post('/admin/email-templates/referral', emailTemplate);
      toast.success('Email template saved successfully');
    } catch (error) {
      toast.error('Failed to save email template');
      console.error('Error saving email template:', error);
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleResetEmailTemplate = () => {
    setEmailTemplate({
      subject: '🚕 Your Taxi-Local.uk Invitation - 1 Month for £1!',
      fromName: 'Taxi-Local',
      content: `<h2>🚕 You've been invited to try Taxi-Local.uk!</h2>
<p>Hi {referredName},</p>
<p>Your colleague {referrerName} thinks you'd love Taxi-Local.uk - the platform that connects drivers with customers.</p>
<p><strong>Special Offer: Try Taxi-Local.uk for 1 month for just £1!</strong></p>
<p>This exclusive invitation gives you access to:</p>
<ul>
  <li>Direct customer bookings</li>
  <li>Real-time job notifications</li>
  <li>Professional driver tools</li>
  <li>And much more!</li>
</ul>
<p><a href="{signupUrl}" style="background-color: #007bff; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px; display: inline-block;">Get Started for £1</a></p>
<p>This invitation expires on {expiryDate}.</p>
<p>Best regards,<br>The Taxi-Local Team</p>`
    });
    toast.info('Email template reset to default');
  };

  const handleSendTestEmail = async () => {
    if (!testEmail.email) {
      toast.warning('Please enter a test email address');
      return;
    }

    const requestData = {
      email: testEmail.email,
      referredName: testEmail.referredName,
      referrerName: testEmail.referrerName,
      company: testEmail.company,
      subject: emailTemplate.subject,
      fromName: emailTemplate.fromName,
      content: emailTemplate.content
    };

    console.log('Sending test email with data:', requestData);
    console.log('Email template:', emailTemplate);

    try {
      setSendingTestEmail(true);
      const response = await axios.post('/admin/email-templates/test-referral', requestData);
      console.log('Test email response:', response.data);
      toast.success('Test email sent successfully');
    } catch (error) {
      console.error('Error sending test email:', error);
      console.error('Error response:', error.response?.data);
      toast.error(`Failed to send test email: ${error.response?.data?.error || error.message}`);
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handlePreviewEmail = () => {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + 30);
    
    const previewContent = emailTemplate.content
      .replace(/{referredName}/g, testEmail.referredName || 'John Doe')
      .replace(/{referrerName}/g, testEmail.referrerName || 'Jane Smith')
      .replace(/{signupUrl}/g, 'https://taxi-local-crm-b56b33078dda.herokuapp.com/referral-signup?token=test-token')
      .replace(/{expiryDate}/g, expiryDate.toLocaleDateString());

    const previewWindow = window.open('', '_blank', 'width=800,height=600');
    previewWindow.document.write(`
      <html>
        <head>
          <title>Email Preview</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .email-container { max-width: 600px; margin: 0 auto; }
            .email-header { background: #f8f9fa; padding: 20px; border-radius: 5px; margin-bottom: 20px; }
            .email-content { line-height: 1.6; }
          </style>
        </head>
        <body>
          <div class="email-container">
            <div class="email-header">
              <h3>Email Preview</h3>
              <p><strong>To:</strong> ${testEmail.email || 'test@example.com'}</p>
              <p><strong>Subject:</strong> ${emailTemplate.subject}</p>
              <p><strong>From:</strong> ${emailTemplate.fromName}</p>
            </div>
            <div class="email-content">
              ${previewContent}
            </div>
          </div>
        </body>
      </html>
    `);
    previewWindow.document.close();
  };

  // SMS Template Handlers
  const handleSaveSmsTemplate = async () => {
    try {
      if (selectedSmsTemplate) {
        await axios.put(`/sms-templates/${selectedSmsTemplate._id}`, smsTemplateForm);
        setSmsTemplates(smsTemplates.map(t => t._id === selectedSmsTemplate._id ? { ...t, ...smsTemplateForm } : t));
        toast.success('SMS template updated successfully');
      } else {
        const response = await axios.post('/sms-templates', smsTemplateForm);
        setSmsTemplates([...smsTemplates, response.data.template]);
        toast.success('SMS template created successfully');
      }
      setShowSmsTemplateModal(false);
      setSelectedSmsTemplate(null);
      setSmsTemplateForm({ name: '', title: '', message: '', isActive: true, order: 0 });
    } catch (error) {
      toast.error('Failed to save SMS template');
      console.error('Error saving SMS template:', error);
    }
  };

  const handleDeleteSmsTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this SMS template?')) {
      return;
    }
    try {
      await axios.delete(`/sms-templates/${templateId}`);
      setSmsTemplates(smsTemplates.filter(t => t._id !== templateId));
      toast.success('SMS template deleted successfully');
    } catch (error) {
      toast.error('Failed to delete SMS template');
      console.error('Error deleting SMS template:', error);
    }
  };

  const handleToggleSmsTemplate = async (templateId) => {
    try {
      const response = await axios.patch(`/sms-templates/${templateId}/toggle`);
      setSmsTemplates(smsTemplates.map(t => t._id === templateId ? response.data.template : t));
      toast.success('SMS template status updated');
    } catch (error) {
      toast.error('Failed to update SMS template status');
      console.error('Error toggling SMS template:', error);
    }
  };

  const openSmsTemplateModal = (template = null) => {
    if (template) {
      setSelectedSmsTemplate(template);
      setSmsTemplateForm({
        name: template.name,
        title: template.title,
        message: template.message,
        isActive: template.isActive,
        order: template.order
      });
    } else {
      setSelectedSmsTemplate(null);
      setSmsTemplateForm({ name: '', title: '', message: '', isActive: true, order: 0 });
    }
    setShowSmsTemplateModal(true);
  };

  // Audio Management Functions
  const loadAudioFiles = async () => {
    try {
      console.log('📁 Loading audio files...');
      const response = await axios.get('/admin/audio-files');
      console.log('📁 Audio files response:', response.data);
      console.log('📁 Audio files count:', response.data.length);
      setAudioFiles(response.data);
      
      if (response.data.length === 0) {
        console.log('📁 No audio files found');
        toast.info('No audio files found. Upload some audio files to get started.');
      } else {
        console.log('📁 Audio files loaded successfully');
        toast.success(`Loaded ${response.data.length} audio files`);
      }
    } catch (error) {
      console.error('❌ Error loading audio files:', error);
      console.error('❌ Error response:', error.response);
      toast.error('Failed to load audio files');
    }
  };

  const handleAudioUpload = async (e) => {
    e.preventDefault();
    const file = e.target.files[0];
    
    console.log('📁 Audio upload started');
    console.log('📁 Selected file:', file);
    
    if (!file) {
      console.log('❌ No file selected');
      return;
    }
    
    console.log('📁 File details:', {
      name: file.name,
      type: file.type,
      size: file.size
    });
    
    // Check file type
    if (!file.type.startsWith('audio/')) {
      console.log('❌ Invalid file type:', file.type);
      toast.error('Please select an audio file');
      return;
    }
    
    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      console.log('❌ File too large:', file.size);
      toast.error('File size must be less than 10MB');
      return;
    }
    
    setAudioUploading(true);
    console.log('📁 Starting upload...');
    
    try {
      const formData = new FormData();
      formData.append('audioFile', file);
      
      console.log('📁 FormData created, sending request...');
      const response = await axios.post('/admin/audio-files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('📁 Upload response:', response.data);
      setAudioFiles(prev => [...prev, response.data]);
      toast.success('Audio file uploaded successfully');
      console.log('✅ Audio file uploaded and added to state');
    } catch (error) {
      console.error('❌ Error uploading audio file:', error);
      console.error('❌ Error response:', error.response);
      toast.error('Failed to upload audio file');
    } finally {
      setAudioUploading(false);
      console.log('📁 Upload process completed');
    }
  };

  const handleDeleteAudio = async (audioId) => {
    if (window.confirm('Are you sure you want to delete this audio file?')) {
      try {
        await axios.delete(`/admin/audio-files/${audioId}`);
        setAudioFiles(prev => prev.filter(file => file._id !== audioId));
        toast.success('Audio file deleted successfully');
      } catch (error) {
        console.error('Error deleting audio file:', error);
        toast.error('Failed to delete audio file');
      }
    }
  };

  const handleTestAudio = (audioFile) => {
    setSelectedAudioFile(audioFile);
    setAudioTestPlaying(true);
    
    // Try multiple URL formats to ensure file is reached
    let audioUrl;
    
    // Option 1: Check for backend URL, use as-is or convert 
    if (audioFile.url && typeof audioFile.url === 'string' && audioFile.url !== '') {
      console.log('🔧 Backend provided URL check:', audioFile.url);
      
      // Check if URL is using /audio path correctly
      if (audioFile.url.startsWith('/audio/') || audioFile.url.startsWith('/uploads/audio/') || audioFile.url.startsWith('/api/uploads/audio/')) {
        // Convert to /audio path since server serves files there
        audioUrl = audioFile.url.replace(/^\/uploads\/audio\//, '/audio/').replace(/^\/api\/uploads\/audio\//, '/audio/');
        console.log('🔗 Using /audio path as-is:', audioUrl);
      } else {
        audioUrl = audioFile.url;
        console.log('🔗 Using backend URL as-is untouched:', audioUrl);
      }
    }
    // Option 2: Construct filename with /audio path 
    else if (audioFile.filename) {
      audioUrl = `/audio/${audioFile.filename}`;
      console.log('🔗 Building /audio audio URL:', audioUrl);
    }
    // Option 3: Fallback to filename based /audio path
    else {  
      audioUrl = `/audio/${audioFile._id}`;
      console.warn('⚠️ Using ID fallback for audio path:', audioUrl);
    }
    
    console.log('🎵 Testing audio with URL:', audioUrl);
    
    const audio = new Audio(audioUrl);
    
    // Add timeout to prevent hanging
    const timeoutId = setTimeout(() => {
      console.error('⏰ Audio test timeout after 10 seconds');
      setAudioTestPlaying(false);
      toast.error('Audio playback timed out');
    }, 10000);
    
    audio.onended = () => {
      clearTimeout(timeoutId);
      setAudioTestPlaying(false);
      console.log('✅ Audio test completed successfully');
    };
    
    audio.onerror = (error) => {
      clearTimeout(timeoutId);
      console.error('❌ Audio test error:', error);
      setAudioTestPlaying(false);
      // Check if this is an audio path access issue
      if (audioUrl.includes('audioFile-')) {
        toast.error(`Audio file not found at simplified path - ${audioUrl}`);
      } else {
        toast.error(`Failed to play audio file: ${audioUrl}`);
      }
    };
    
    audio.oncanplaythrough = () => {
      console.log('🎵 Audio file can play completely');
      clearTimeout(timeoutId);
    };
    
    audio.onloaderror = (error) => {
      clearTimeout(timeoutId);
      console.error('❌ Audio load error:', error);
      setAudioTestPlaying(false);
      // Provide specific error message for missing files
      if (audioUrl.includes('audioFile-')) {
        toast.error('Audio file missing - please re-upload');
      } else {
        toast.error('Failed to load audio file');
      }
    };
    
    audio.play().catch(error => {
      clearTimeout(timeoutId);
      console.error('Error playing audio:', error);
      setAudioTestPlaying(false);
      // Check if this is likely an Heroku ephemeral file issue
      if (audioUrl.includes('audioFile-')) {
        toast.error('Audio file lost - please re-upload the file');
      } else {
        toast.error('Failed to play audio file');
      }
    });
  };

  // Test audio mapping for NewWeb/NewPool debugging
  const testNewJobAudioMappings = async () => {
    console.log('🧪 Testing NewWeb (MyLink) and NewPool audio mapping debug...');
    
    try {
      // Test NewPool (Available Jobs)
      console.log('🔊 Testing NewPool notification audio mapping...');
      await audioNotificationService.playNewJobAudio('pool');
      
      console.log('🔊 Testing NewWeb (MyLink) notification audio mapping...');
      await audioNotificationService.playNewJobAudio('mylink');
      
      console.log('🧪 Test completed - check logs above for mapping results');
    } catch (error) {
      console.error('❌ Audio mapping test failed:', error);
      toast.error('Audio mapping test failed - check console for details');
    }
  };

  // System function categories for audio mapping
         const systemFunctions = [
           { id: 'sleep_mode', name: 'Sleep Mode Alert', description: 'Audio played when app goes to sleep/background' },
           { id: 'new_job_pool', name: 'New Job Pool Notification', description: 'Audio played when new job from Pool is available' },
           { id: 'new_job_mylink', name: 'New Job MyLink Notification', description: 'Audio played when new job from MyLink is available' },
           { id: 'new_job_general', name: 'New Job General Notification', description: 'Audio played when new job (any source) is available' },
           { id: 'job_accepted_pool', name: 'Job Accepted (Pool)', description: 'Audio played when Pool job is accepted' },
           { id: 'job_accepted_mylink', name: 'Job Accepted (MyLink)', description: 'Audio played when MyLink job is accepted' },
           { id: 'job_accepted_general', name: 'Job Accepted (General)', description: 'Audio played when any job is accepted' },
           { id: 'job_completed', name: 'Job Completed', description: 'Audio played when job is marked complete' },
           { id: 'location_update', name: 'Location Update', description: 'Audio played during location tracking' },
           { id: 'diary_reminder', name: 'Diary Reminder', description: 'Audio played for diary reminders' },
           { id: 'system_error', name: 'System Error', description: 'Audio played for system errors' },
           { id: 'booking_confirmation', name: 'Booking Confirmation', description: 'Audio played for booking confirmations' },
           { id: 'job_rejected', name: 'Job Rejected', description: 'Audio played when job is rejected' },
           { id: 'job_timeout', name: 'Job Timeout', description: 'Audio played when job times out' },
           { id: 'driver_assigned', name: 'Driver Assigned', description: 'Audio played when driver is assigned to job' },
           { id: 'customer_cancelled', name: 'Customer Cancelled', description: 'Audio played when customer cancels booking' },
           { id: 'running_man_start', name: 'Running Man - Start Tracking (Online)', description: 'Audio played when Running Man (Go) button is pressed' },
           { id: 'standing_man_stop', name: 'Standing Man - Stop Tracking (Offline)', description: 'Audio played when Standing Man (Stop) button is pressed' },
           { id: 'driver_online', name: 'Driver Online State', description: 'Audio played when driver goes online (working)' },
           { id: 'driver_offline', name: 'Driver Offline State', description: 'Audio played when driver goes offline (not working)' }
         ];

  // Helper function to get audio file for specific job source
  const getAudioForJobSource = async (jobSource, eventType) => {
    try {
      let functionId;
      
      // Determine function ID based on job source and event type
      if (eventType === 'new_job') {
        if (jobSource === 'pool') {
          functionId = 'new_job_pool';
        } else if (jobSource === 'mylink') {
          functionId = 'new_job_mylink';
        } else {
          functionId = 'new_job_general';
        }
      } else if (eventType === 'job_accepted') {
        if (jobSource === 'pool') {
          functionId = 'job_accepted_pool';
        } else if (jobSource === 'mylink') {
          functionId = 'job_accepted_mylink';
        } else {
          functionId = 'job_accepted_general';
        }
      }
      
      if (functionId) {
        const response = await axios.get(`/admin/audio-mappings/function/${functionId}`);
        return response.data.audioFile;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting audio for job source:', error);
      return null;
    }
  };

  // Function to play audio based on job source and event type
  const playAudioForJobEvent = async (jobSource, eventType) => {
    try {
      const audioFile = await getAudioForJobSource(jobSource, eventType);
      
      if (audioFile && audioFile.url) {
        const audio = new Audio(audioFile.url);
        audio.volume = 0.8; // Set volume to 80%
        
        audio.play().then(() => {
          console.log(`🎵 Playing audio for ${jobSource} ${eventType}:`, audioFile.originalName);
        }).catch(error => {
          console.error('Error playing job audio:', error);
        });
        
        return true;
      } else {
        console.log(`🔇 No audio mapping found for ${jobSource} ${eventType}`);
        return false;
      }
    } catch (error) {
      console.error('Error playing audio for job event:', error);
      return false;
    }
  };

  // Load audio mappings
  const loadAudioMappings = async () => {
    try {
      const response = await axios.get('/admin/audio-mappings');
      // Ensure we always set an array to prevent map errors
      setAudioMappings(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading audio mappings:', error);
      toast.error('Failed to load audio mappings');
      // Set empty array on error to prevent map errors
      setAudioMappings([]);
    }
  };

  // Create or update audio mapping
  const handleSaveMapping = async (mappingData) => {
    try {
      if (selectedMapping) {
        // Update existing mapping
        await axios.put(`/admin/audio-mappings/${selectedMapping._id}`, mappingData);
        setAudioMappings(prev => 
          Array.isArray(prev) ? prev.map(mapping => 
            mapping._id === selectedMapping._id 
              ? { ...mapping, ...mappingData }
              : mapping
          ) : []
        );
        toast.success('Audio mapping updated successfully');
      } else {
        // Create new mapping - if it exists, suggest updating instead
        const response = await axios.post('/admin/audio-mappings', mappingData);
        setAudioMappings(prev => Array.isArray(prev) ? [...prev, response.data] : [response.data]);
        toast.success('Audio mapping created successfully');
      }
      setShowMappingModal(false);
      setSelectedMapping(null);
    } catch (error) {
      console.error('Error saving audio mapping:', error);
      
      // Check if it's a duplicate mapping error (400) 
      if (error.response?.status === 400 && error.response?.data?.error?.includes('already exists')) {
        toast.error('Mapping already exists for this function. Select the existing mapping to update it instead.', {
          autoClose: 5000
        });
      } else {
        toast.error('Failed to save audio mapping');
      }
    }
  };

  // Delete audio mapping
  const handleDeleteMapping = async (mappingId) => {
    if (window.confirm('Are you sure you want to delete this audio mapping?')) {
      try {
        await axios.delete(`/admin/audio-mappings/${mappingId}`);
        setAudioMappings(prev => Array.isArray(prev) ? prev.filter(mapping => mapping._id !== mappingId) : []);
        toast.success('Audio mapping deleted successfully');
      } catch (error) {
        console.error('Error deleting audio mapping:', error);
        toast.error('Failed to delete audio mapping');
      }
    }
  };

  // Edit audio mapping
  const handleEditMapping = (mapping) => {
    setSelectedMapping(mapping);
    setShowMappingModal(true);
  };

  // Email Logs Functions
  const loadEmailLogs = async () => {
    try {
      setEmailLogsLoading(true);
      const params = new URLSearchParams({
        page: emailLogsPagination.page,
        limit: emailLogsPagination.limit,
        ...emailLogsFilter
      });
      
      const response = await axios.get(`/admin/email-logs?${params}`);
      if (response.data.success) {
        setEmailLogs(response.data.logs);
        setEmailLogsPagination(prev => ({
          ...prev,
          total: response.data.total,
          hasMore: response.data.hasMore
        }));
      }
    } catch (error) {
      console.error('Error loading email logs:', error);
      toast.error('Failed to load email logs');
    } finally {
      setEmailLogsLoading(false);
    }
  };

  const loadEmailLogsSummary = async () => {
    try {
      const params = new URLSearchParams({
        startDate: emailLogsFilter.startDate,
        endDate: emailLogsFilter.endDate
      });
      
      const response = await axios.get(`/admin/email-logs/summary?${params}`);
      if (response.data.success) {
        setEmailLogsSummary(response.data.drivers);
      }
    } catch (error) {
      console.error('Error loading email logs summary:', error);
      toast.error('Failed to load email logs summary');
    }
  };

  const handleEmailLogsFilterChange = (field, value) => {
    setEmailLogsFilter(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const applyEmailLogsFilter = () => {
    setEmailLogsPagination(prev => ({ ...prev, page: 1 }));
    loadEmailLogs();
  };

  const loadDriverEmailLogs = async (driverId) => {
    try {
      setEmailLogsLoading(true);
      const params = new URLSearchParams({
        page: 1,
        limit: 50,
        ...emailLogsFilter
      });
      
      const response = await axios.get(`/admin/email-logs/driver/${driverId}?${params}`);
      if (response.data.success) {
        setSelectedDriverEmailLogs({
          driverId,
          logs: response.data.logs,
          total: response.data.total
        });
        setShowEmailLogModal(true);
      }
    } catch (error) {
      console.error('Error loading driver email logs:', error);
      toast.error('Failed to load driver email logs');
    } finally {
      setEmailLogsLoading(false);
    }
  };

  const showFullEmailContent = (emailLog) => {
    setSelectedEmailContent({
      subject: emailLog.subject,
      recipientEmail: emailLog.recipientEmail,
      recipientName: emailLog.recipientName,
      driverName: emailLog.driverName,
      sentAt: emailLog.sentAt,
      status: emailLog.status,
      content: emailLog.fullContent || emailLog.contentPreview
    });
    setShowFullEmailModal(true);
  };

  if (initialLoad || loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">{initialLoad ? 'Initializing System Administration...' : 'Loading data...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <style>{modernTabStyles}</style>
      {/* Header */}
      <div className="sticky-top bg-white border-bottom pb-3" style={{ zIndex: 1020, top: '56px' }}>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3">
          <div className="mb-2 mb-md-0">
            <h2 className="mb-0">System Administration</h2>
            <p className="text-muted mb-0 d-none d-md-block">Full system management and control</p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={() => !loading && loadAllData()} 
              disabled={loading}
              className="mb-1 modern-btn"
              style={{
                borderRadius: '8px',
                border: '2px solid #007bff',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                boxShadow: '0 2px 4px rgba(0,123,255,0.1)',
                transition: 'all 0.3s ease',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 8px rgba(0,123,255,0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 4px rgba(0,123,255,0.1)';
              }}
            >
              {loading ? <Spinner size="sm" className="me-1" /> : <i className="bi bi-arrow-clockwise me-1"></i>}
              Refresh
            </Button>
            <Button 
              variant="outline-secondary" 
              size="sm"
              onClick={() => setShowSystemSettingsModal(true)}
              className="mb-1 modern-btn"
              style={{
                borderRadius: '8px',
                border: '2px solid #6c757d',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                boxShadow: '0 2px 4px rgba(108,117,125,0.1)',
                transition: 'all 0.3s ease',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 8px rgba(108,117,125,0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 4px rgba(108,117,125,0.1)';
              }}
            >
              <i className="bi bi-gear me-1"></i>
              Settings
            </Button>
          </div>
        </div>

        {/* Statistics Dashboard */}
        <Row className="mb-3 g-2">
          <Col xs={6} sm={4} md={2}>
            <Card className="text-center h-100 modern-card" style={{
              borderRadius: '12px',
              border: 'none',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
              transition: 'all 0.3s ease'
            }}>
              <Card.Body className="py-3 px-2">
                <h5 className="mb-1 fw-bold">{users.length}</h5>
                <small className="opacity-75">Users</small>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} sm={4} md={2}>
            <Card className="text-center h-100 modern-card" style={{
              borderRadius: '12px',
              border: 'none',
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              boxShadow: '0 4px 15px rgba(240, 147, 251, 0.3)',
              transition: 'all 0.3s ease'
            }}>
              <Card.Body className="py-3 px-2">
                <h5 className="mb-1 fw-bold">{leads.length}</h5>
                <small className="opacity-75">Leads</small>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} sm={4} md={2}>
            <Card className="text-center h-100 modern-card" style={{
              borderRadius: '12px',
              border: 'none',
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white',
              boxShadow: '0 4px 15px rgba(79, 172, 254, 0.3)',
              transition: 'all 0.3s ease'
            }}>
              <Card.Body className="py-3 px-2">
                <h5 className="mb-1 fw-bold">{tickets.length}</h5>
                <small className="opacity-75">Tickets</small>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} sm={4} md={2}>
            <Card className="text-center h-100 modern-card" style={{
              borderRadius: '12px',
              border: 'none',
              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
              color: 'white',
              boxShadow: '0 4px 15px rgba(67, 233, 123, 0.3)',
              transition: 'all 0.3s ease'
            }}>
              <Card.Body className="py-3 px-2">
                <h5 className="mb-1 fw-bold">{banners.length}</h5>
                <small className="opacity-75">Banners</small>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} sm={4} md={2}>
            <Card className="text-center h-100 modern-card" style={{
              borderRadius: '12px',
              border: 'none',
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
              color: 'white',
              boxShadow: '0 4px 15px rgba(250, 112, 154, 0.3)',
              transition: 'all 0.3s ease'
            }}>
              <Card.Body className="py-3 px-2">
                <h5 className="mb-1 fw-bold">{systemStats.activeUsers || 0}</h5>
                <small className="opacity-75">Active</small>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} sm={4} md={2}>
            <Card className="text-center h-100 modern-card" style={{
              borderRadius: '12px',
              border: 'none',
              background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
              color: '#333',
              boxShadow: '0 4px 15px rgba(168, 237, 234, 0.3)',
              transition: 'all 0.3s ease'
            }}>
              <Card.Body className="py-3 px-2">
                <h5 className="mb-1 fw-bold">{systemStats.pendingLeads || 0}</h5>
                <small className="opacity-75">Pending</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Main Content */}
      <Tabs 
        activeKey={activeTab} 
        onSelect={(k) => {
          // Use requestAnimationFrame to defer the state update for better performance
          requestAnimationFrame(() => {
            setActiveTab(k);
            
            // Mark this tab as loaded if it hasn't been loaded yet
            if (!dataLoaded[k]) {
              setDataLoaded(prev => ({
                ...prev,
                [k]: true
              }));
              
              // Load specific data for certain tabs
              if (k === 'audio') {
                loadAudioFiles();
                loadAudioMappings();
              } else if (k === 'email-logs') {
                loadEmailLogs();
                loadEmailLogsSummary();
              }
            }
          });
        }} 
        className="mt-4 modern-tabs"
        fill
        justify
        variant="pills"
        style={{
          '--bs-nav-pills-border-radius': '12px',
          '--bs-nav-pills-link-active-bg': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          '--bs-nav-pills-link-active-color': '#fff'
        }}
      >
        {/* Dashboard Tab */}
        <Tab eventKey="dashboard" title="📊 Dashboard">
          <Row>
            <Col md={6}>
              <Card>
                <Card.Header>
                  <h5>Recent Activity</h5>
                </Card.Header>
                <Card.Body>
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {auditLogs.slice(0, 10).map((log, index) => (
                      <div key={index} className="border-bottom pb-2 mb-2">
                        <div className="d-flex justify-content-between">
                          <strong>{log.action}</strong>
                          <small className="text-muted">{formatDate(log.createdAt)}</small>
                        </div>
                        <small className="text-muted">{log.userEmail}</small>
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card>
                <Card.Header>
                  <h5>System Health</h5>
                </Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <strong>Database Status:</strong> 
                    <Badge bg="success" className="ms-2">Connected</Badge>
                  </div>
                  <div className="mb-3">
                    <strong>Email Service:</strong> 
                    <Badge bg="success" className="ms-2">Active</Badge>
                  </div>
                  <div className="mb-3">
                    <strong>WebSocket:</strong> 
                    <Badge bg="success" className="ms-2">Connected</Badge>
                  </div>
                  <div className="mb-3">
                    <strong>Storage:</strong> 
                    <Badge bg="warning" className="ms-2">75% Used</Badge>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        {/* Admin Override Login Tab - Support Access */}
        <Tab eventKey="support-access" title="🔐 Support Access">
          <Card>
            <Card.Header>
              <h5>System Administrator Support Access</h5>
              <small className="text-muted">Log in as any driver to provide support</small>
            </Card.Header>
            <Card.Body>
              <Alert variant="warning" className="mb-4">
                <strong>⚠️ Security Notice:</strong> This feature allows system administrators to log in as any driver's account for support purposes. 
                Use this feature responsibly and only when providing direct support to drivers.
              </Alert>
              
              <Form
                onSubmit={async (e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const adminPassword = formData.get('adminPassword');
                  const driverEmail = formData.get('driverEmail');

                  if (!adminPassword || !driverEmail) {
                    toast.error('Please fill in all fields');
                    return;
                  }

                  try {
                    setLoading(true);
                    const { data } = await axios.post(
                      `${process.env.REACT_APP_API_URL}/api/auth/admin-override-login`,
                      { adminPassword, driverEmail }
                    );

                    // Store the token and user data
                    localStorage.setItem('token', data.token);
                    localStorage.setItem('role', data.user.role);
                    localStorage.setItem('userId', data.user.id);
                    localStorage.setItem('adminOverride', 'true'); // Flag to indicate admin override

                    toast.success(`Successfully logged in as ${data.user.name || data.user.email}`);
                    
                    // Redirect based on driver role
                    setTimeout(() => {
                      if (data.user.role === 'listing_only') {
                        window.location.href = '/dashboard';
                      } else {
                        window.location.href = '/dashboard/accepted-jobs';
                      }
                    }, 1500);
                  } catch (err) {
                    toast.error(
                      err.response?.data?.error || 
                      'Failed to perform admin override login. Please check your credentials.'
                    );
                    setLoading(false);
                  }
                }}
              >
                <Form.Group className="mb-3">
                  <Form.Label>
                    <strong>System Administration Password</strong>
                  </Form.Label>
                  <Form.Control
                    type="password"
                    name="adminPassword"
                    placeholder="Enter system administration password"
                    required
                    autoComplete="off"
                  />
                  <Form.Text className="text-muted">
                    This is the system-wide administration password configured in the server environment.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    <strong>Driver Email Address</strong>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="driverEmail"
                    placeholder="Enter driver's email address"
                    required
                    autoComplete="off"
                  />
                  <Form.Text className="text-muted">
                    Enter the email address of the driver you want to log in as.
                  </Form.Text>
                </Form.Group>

                <Button 
                  type="submit" 
                  variant="primary" 
                  disabled={loading}
                  className="w-100"
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Logging in...
                    </>
                  ) : (
                    '🔐 Log In as Driver'
                  )}
                </Button>
              </Form>

              <hr className="my-4" />

              <div>
                <h6>Quick Driver Lookup</h6>
                <Form.Group className="mb-3">
                  <Form.Control
                    type="text"
                    placeholder="Search drivers by name, email, or company..."
                    value={supportAccessSearch}
                    onChange={(e) => setSupportAccessSearch(e.target.value)}
                  />
                </Form.Group>
                
                <Table striped bordered hover size="sm" className="mt-3">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Company</th>
                      <th>Role</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users
                      .filter(user => {
                        // Filter by role
                        if (user.role !== 'professional' && user.role !== 'listing_only') {
                          return false;
                        }
                        // Filter by search term
                        if (supportAccessSearch) {
                          const searchLower = supportAccessSearch.toLowerCase();
                          return (
                            user.email?.toLowerCase().includes(searchLower) ||
                            user.name?.toLowerCase().includes(searchLower) ||
                            user.companyName?.toLowerCase().includes(searchLower)
                          );
                        }
                        return true;
                      })
                      .slice(0, 20)
                      .map((user) => (
                        <tr key={user._id}>
                          <td>{user.name || 'N/A'}</td>
                          <td>{user.email}</td>
                          <td>{user.companyName || 'N/A'}</td>
                          <td>
                            <Badge bg={user.role === 'professional' ? 'primary' : 'secondary'}>
                              {user.role}
                            </Badge>
                          </td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => {
                                const emailInput = document.querySelector('input[name="driverEmail"]');
                                if (emailInput) {
                                  emailInput.value = user.email;
                                  emailInput.focus();
                                }
                              }}
                            >
                              Use Email
                            </Button>
                          </td>
                        </tr>
                      ))}
                    {users.filter(user => {
                      if (user.role !== 'professional' && user.role !== 'listing_only') return false;
                      if (supportAccessSearch) {
                        const searchLower = supportAccessSearch.toLowerCase();
                        return (
                          user.email?.toLowerCase().includes(searchLower) ||
                          user.name?.toLowerCase().includes(searchLower) ||
                          user.companyName?.toLowerCase().includes(searchLower)
                        );
                      }
                      return true;
                    }).length === 0 && (
                      <tr>
                        <td colSpan="5" className="text-center text-muted">
                          {supportAccessSearch ? 'No drivers found matching your search' : 'No drivers available'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Tab>

        {/* Users Tab */}
        <Tab eventKey="users" title="👥 Users">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3">
            <h4 className="mb-2 mb-md-0">User Management</h4>
            <div className="d-flex flex-wrap gap-2">
              {selectedUsers.length > 0 && (
                <Button 
                  variant="warning" 
                  onClick={() => setShowBulkActionsModal(true)}
                  className="me-2"
                >
                  Bulk Actions ({selectedUsers.length})
                </Button>
              )}
              <Button variant="primary" onClick={() => setShowUserModal(true)}>
                Create New User
              </Button>
            </div>
          </div>
          {isMobileView ? (
            // Mobile Accordion View
            <Accordion flush>
              {getSortedUsers().map((user, index) => (
                <Accordion.Item eventKey={String(index)} key={user._id}>
                  <Accordion.Header>
                    <div className="d-flex justify-content-between align-items-center w-100 pe-3">
                      <div>
                        <Form.Check
                          type="checkbox"
                          checked={selectedUsers.includes(user._id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleUserSelection(user._id, e.target.checked);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="me-2 d-inline-block"
                        />
                        <strong>{user.firstName && user.surname ? `${user.firstName} ${user.surname}` : user.name}</strong>
                        <br />
                        <small className="text-muted">{user.companyName || 'N/A'}</small>
                      </div>
                      {getStatusBadge(user.subscriptionStatus)}
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className="mb-2">
                      <strong>Email:</strong> {user.email}
                    </div>
                    <div className="mb-2">
                      <strong>Role:</strong> {getRoleBadge(user.role)}
                    </div>
                    <div className="mb-2">
                      <strong>Phone:</strong> {user.phoneNumber || '-'}
                    </div>
                    <div className="mb-2">
                      <strong>Job Card Vehicle:</strong> {user.preferredVehicleForJobs === 'spare' && hasVehicleData(user.spareVehicle) ? 'Car No2 (Spare Vehicle)' : 'Car No1 (Primary Vehicle)'}
                    </div>
                    <div className="mb-2">
                      <strong>Created:</strong> {formatDate(user.createdAt)}
                    </div>
                    <div className="d-grid gap-2">
                      <Button 
                        variant="primary" 
                        size="sm"
                        onClick={() => {
                          setSelectedItem(user);
                          setUserForm(mapUserToForm(user));
                          setShowUserModal(true);
                        }}
                      >
                        ✏️ Edit Profile
                      </Button>
                      <Button 
                        variant="danger" 
                        size="sm"
                        onClick={() => handleDeleteUser(user._id)}
                      >
                        🗑️ Delete
                      </Button>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          ) : (
            // Desktop Table View with Sorting
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>
                    <Form.Check
                      type="checkbox"
                      checked={selectedUsers.length === users.length && users.length > 0}
                      onChange={(e) => handleSelectAllUsers(e.target.checked)}
                    />
                  </th>
                  <th onClick={() => requestUserSort('name')} style={{ cursor: 'pointer' }}>
                    Name{getUserSortIcon('name')}
                  </th>
                  <th onClick={() => requestUserSort('email')} style={{ cursor: 'pointer' }}>
                    Email{getUserSortIcon('email')}
                  </th>
                  <th onClick={() => requestUserSort('role')} style={{ cursor: 'pointer' }}>
                    Role{getUserSortIcon('role')}
                  </th>
                  <th onClick={() => requestUserSort('companyName')} style={{ cursor: 'pointer' }}>
                    Company{getUserSortIcon('companyName')}
                  </th>
                  <th onClick={() => requestUserSort('phoneNumber')} style={{ cursor: 'pointer' }}>
                    Phone{getUserSortIcon('phoneNumber')}
                  </th>
                  <th>
                    Job Vehicle
                  </th>
                  <th onClick={() => requestUserSort('subscriptionStatus')} style={{ cursor: 'pointer' }}>
                    Status{getUserSortIcon('subscriptionStatus')}
                  </th>
                  <th onClick={() => requestUserSort('createdAt')} style={{ cursor: 'pointer' }}>
                    Created{getUserSortIcon('createdAt')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {getSortedUsers().map(user => (
                  <tr key={user._id}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={selectedUsers.includes(user._id)}
                        onChange={(e) => handleUserSelection(user._id, e.target.checked)}
                      />
                    </td>
                    <td>{user.firstName && user.surname ? `${user.firstName} ${user.surname}` : user.name}</td>
                    <td>{user.email}</td>
                    <td>{getRoleBadge(user.role)}</td>
                    <td>{user.companyName || '-'}</td>
                    <td>{user.phoneNumber || '-'}</td>
                    <td>
                      {user.preferredVehicleForJobs === 'spare' && hasVehicleData(user.spareVehicle)
                        ? 'Car No2 (Spare)'
                        : 'Car No1 (Primary)'}
                    </td>
                    <td>{getStatusBadge(user.subscriptionStatus)}</td>
                    <td>{formatDate(user.createdAt)}</td>
                    <td>
                      <Dropdown>
                        <Dropdown.Toggle size="sm">Actions</Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => {
                            setSelectedItem(user);
                            setUserForm(mapUserToForm(user));
                            setShowUserModal(true);
                          }}>
                            Edit Profile
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => handleDeleteUser(user._id)}>
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Tab>

        {/* Licensing Approvals Tab */}
        <Tab eventKey="licensing" title="📋 Licensing">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>Licensing Approvals</h4>
            <div className="d-flex gap-2 align-items-center">
              <Badge bg="warning" className="fs-6">
                {pendingLicensing.length} Pending
              </Badge>
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={handleAutoDetectProfiles}
                title="Auto-detect profile completion for existing users"
              >
                🔍 Auto-Detect Profiles
              </Button>
            </div>
          </div>
          
          {pendingLicensing.length === 0 ? (
            <Card>
              <Card.Body className="text-center py-5">
                <i className="bi bi-check-circle text-success" style={{ fontSize: '3rem' }}></i>
                <h5 className="mt-3">No Pending Approvals</h5>
                <p className="text-muted">All licensing submissions have been reviewed.</p>
              </Card.Body>
            </Card>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Company</th>
                  <th>License Type</th>
                  <th>Licensing Authority</th>
                  <th>Badge Number</th>
                  <th>Registration</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingLicensing.map(user => (
                  <tr key={user._id}>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td>{user.companyName || '-'}</td>
                    <td>{user.licenseType || '-'}</td>
                    <td>{user.licensingAuthority || '-'}</td>
                    <td>{user.badgeNumber || '-'}</td>
                    <td>{user.regNumber || '-'}</td>
                    <td>{formatDate(user.licensingSubmittedAt)}</td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button 
                          variant="success" 
                          size="sm"
                          onClick={() => handleApproveLicensing(user._id)}
                        >
                          ✓ Approve
                        </Button>
                        <Button 
                          variant="danger" 
                          size="sm"
                          onClick={() => {
                            const reason = prompt('Please provide a reason for rejection:');
                            if (reason) {
                              handleRejectLicensing(user._id, reason);
                            }
                          }}
                        >
                          ✗ Reject
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Tab>

        {/* Leads Tab */}
        <Tab eventKey="leads" title="🎯 Leads">
                     <div className="d-flex justify-content-between align-items-center mb-3">
             <h4>Lead Management</h4>
             <div className="d-flex gap-2">
               <Button 
                 variant="outline-secondary" 
                 onClick={() => setShowColumnSelector(true)}
                 title="Manage visible columns"
               >
                 📊 Columns ({visibleColumns.length})
               </Button>
               <Button variant="success" onClick={() => setShowLeadModal(true)}>
                 ➕ Add Job
               </Button>
             </div>
           </div>

          {/* Search Bar with inline clear */}
          <div className="mb-3 position-relative" style={{ maxWidth: 500 }}>
            <input
              type="text"
              className="form-control"
              placeholder="Search leads by customer name, email, phone, from, to, or status..."
              value={leadSearchTerm}
              onChange={(e) => setLeadSearchTerm(e.target.value)}
              style={{ 
                paddingRight: '2rem',
                fontSize: '0.875rem'
              }}
            />
            {leadSearchTerm && (
              <button
                type="button"
                className="btn btn-link position-absolute top-50 end-0 translate-middle-y text-muted pe-3"
                onClick={() => setLeadSearchTerm('')}
                aria-label="Clear search"
                style={{ fontSize: '1.5rem', lineHeight: 1, textDecoration: 'none' }}
              >
                ×
              </button>
            )}
          </div>

                     {/* Sorting Info and Bulk Action Button */}
           <div className="d-flex justify-content-between align-items-center mb-3">
             <div className="text-muted small">
               <span className="me-3">
                 📊 Sorted by: <strong>{leadSortField}</strong> ({leadSortDirection === 'asc' ? 'A-Z' : 'Z-A'})
               </span>
               <span>
                 📋 Showing {sortedLeads.length} of {leads.length} leads
               </span>
             </div>
             <Button 
               variant="outline-primary" 
               onClick={() => {
                 if (selectedLeads.length === 0) {
                   toast.warning('Please select leads first');
                   return;
                 }
                 // Show bulk actions dropdown or modal
                 setShowBulkActionsModal(true);
               }}
               disabled={selectedLeads.length === 0}
             >
               🗂️ Bulk Actions ({selectedLeads.length})
             </Button>
           </div>

          {/* Bulk Actions */}
          {selectedLeads.length > 0 && (
            <div className="mb-3 p-3 bg-light border rounded">
              <div className="d-flex justify-content-between align-items-center">
                <span className="fw-bold">
                  {selectedLeads.length} lead{selectedLeads.length !== 1 ? 's' : ''} selected
                </span>
                <div className="d-flex gap-2">
                  <Dropdown>
                    <Dropdown.Toggle variant="outline-primary" size="sm">
                      Update Status
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => handleBulkStatusUpdate('pending')}>
                        Set to Pending
                      </Dropdown.Item>
                      <Dropdown.Item onClick={() => handleBulkStatusUpdate('completed')}>
                        Set to Completed
                      </Dropdown.Item>
                      <Dropdown.Item onClick={() => handleBulkStatusUpdate('cancelled')}>
                        Set to Cancelled
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                  <Button 
                    variant="outline-danger" 
                    size="sm"
                    onClick={handleBulkDeleteLeads}
                  >
                    Delete Selected
                  </Button>
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    onClick={() => {
                      setSelectedLeads([]);
                      setSelectAllLeads(false);
                    }}
                  >
                    Clear Selection
                  </Button>
                </div>
              </div>
            </div>
          )}

                                <Table striped bordered hover responsive>
             <thead>
               <tr>
                 <th>
                   <Form.Check
                     type="checkbox"
                     checked={selectAllLeads}
                     onChange={handleSelectAllLeads}
                     title="Select all"
                   />
                 </th>
                 {visibleColumns.map(columnKey => {
                   const column = availableColumns.find(col => col.key === columnKey);
                   return (
                     <th 
                       key={columnKey}
                       style={{ cursor: 'pointer' }}
                       onClick={() => handleSort(columnKey)}
                       title={`Click to sort by ${column.label}`}
                     >
                       {column.label} {getSortIcon(columnKey)}
                     </th>
                   );
                 })}
                 <th>Actions</th>
               </tr>
             </thead>
             <tbody>
               {sortedLeads.map(lead => (
                <tr key={lead._id}>
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={selectedLeads.includes(lead._id)}
                      onChange={() => handleLeadSelection(lead._id)}
                    />
                  </td>
                  {visibleColumns.map(columnKey => (
                    <td key={columnKey}>
                      {columnKey === 'status' 
                        ? getStatusBadge(lead[columnKey])
                        : getColumnValue(lead, columnKey)
                      }
                    </td>
                  ))}
                  <td>
                    <Dropdown>
                      <Dropdown.Toggle size="sm">Actions</Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => {
                          setSelectedItem(lead);
                          setLeadForm({
                            customerName: lead.customerName,
                            customerEmail: lead.customerEmail,
                            customerPhone: lead.customerPhone,
                            from: lead.from,
                            to: lead.to,
                            dateTime: new Date(lead.dateTime).toISOString().slice(0, 16),
                            passengers: lead.passengers,
                            status: lead.status,
                            returnTrip: lead.returnTrip
                          });
                          setShowLeadModal(true);
                        }}>
                          Edit
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => handleDeleteLead(lead._id)}>
                          Delete
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
               ))}
             </tbody>
           </Table>
          
                     {sortedLeads.length === 0 && (
             <div className="text-center py-4">
               <p className="text-muted">
                 {leadSearchTerm ? 'No leads found matching your search.' : 'No leads available.'}
               </p>
             </div>
           )}
        </Tab>

        {/* Tickets Tab */}
        <Tab eventKey="tickets" title="🎫 Tickets">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>Support Tickets</h4>
            <Badge bg="info" className="fs-6">
              {tickets.length} Tickets
            </Badge>
          </div>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Title</th>
                <th>User</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map(ticket => (
                <tr key={ticket._id}>
                  <td>{ticket.title}</td>
                  <td>{ticket.userName}</td>
                  <td>{getStatusBadge(ticket.status)}</td>
                  <td>{getStatusBadge(ticket.priority)}</td>
                  <td>{formatDate(ticket.createdAt)}</td>
                  <td>
                      <Dropdown>
                      <Dropdown.Toggle size="sm" variant="outline-primary">
                          Actions
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                        <Dropdown.Item onClick={() => handleViewTicket(ticket)}>
                          👁️ View
                        </Dropdown.Item>
                          <Dropdown.Item onClick={() => handleViewTicket(ticket)}>
                            📝 Respond
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => {
                            const newStatus = prompt('Enter new status (open, in-progress, resolved, closed):', ticket.status);
                            if (newStatus && ['open', 'in-progress', 'resolved', 'closed'].includes(newStatus)) {
                              setSelectedItem(ticket);
                              setTicketForm({
                                title: ticket.title,
                                description: ticket.description,
                                status: newStatus,
                                priority: ticket.priority,
                                response: ticket.response || ''
                              });
                              handleUpdateTicket({ preventDefault: () => {} });
                            }
                          }}>
                            🔄 Change Status
                          </Dropdown.Item>
                          <Dropdown.Item 
                            onClick={() => handleDeleteTicket(ticket._id)}
                            className="text-danger"
                          >
                            🗑️ Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {tickets.length === 0 && (
            <div className="text-center py-4">
              <p className="text-muted">No support tickets found.</p>
            </div>
          )}
        </Tab>

        {/* Banners Tab */}
        <Tab eventKey="banners" title="📢 Banners">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>Banner Management</h4>
            <div className="d-flex gap-2">
              <Button 
                variant="outline-info" 
                onClick={() => window.open('/MYLINK_BANNER_IMAGE_SPECIFICATIONS.html', '_blank')}
                title="View banner image specifications and best practices"
              >
                📄 Image Specifications Guide
              </Button>
              <Button variant="primary" onClick={() => {
                setSelectedItem(null);
                setBannerForm({
                  title: '', message: '', audience: 'all',
                  startsAt: '', endsAt: '', isActive: true, priority: 0,
                  targetPostcodes: '', rotationInterval: 5, imageFile: null,
                  imagePreview: null, imageLoading: false
                });
                setShowBannerModal(true);
              }}>
                Create New Banner
              </Button>
            </div>
          </div>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Title</th>
                <th>Audience</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Target Postcodes</th>
                <th>Impressions</th>
                <th>Clicks</th>
                <th>Click Rate</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {banners.map(banner => (
                <tr key={banner._id}>
                  <td>{banner.title}</td>
                  <td>{banner.audience}</td>
                  <td>{getStatusBadge(banner.isActive ? 'active' : 'inactive')}</td>
                  <td>{banner.priority}</td>
                  <td>
                    {banner.targetPostcodes && banner.targetPostcodes.length > 0 
                      ? banner.targetPostcodes.join(', ') 
                      : <span className="text-muted">All areas</span>
                    }
                  </td>
                  <td>{banner.impressions || 0}</td>
                  <td>{banner.clicks || 0}</td>
                  <td>{banner.clickRate ? `${banner.clickRate.toFixed(1)}%` : '0%'}</td>
                  <td>{formatDate(banner.createdAt)}</td>
                  <td>
                    <Dropdown>
                      <Dropdown.Toggle size="sm" variant="outline-primary">
                        Actions
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => handleEditBanner(banner)}>
                          ✏️ Edit
                        </Dropdown.Item>
                        <Dropdown.Item 
                        onClick={() => handleDeleteBanner(banner._id)}
                          className="text-danger"
                        >
                          🗑️ Delete
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {banners.length === 0 && (
            <div className="text-center py-4">
              <p className="text-muted">No banners found.</p>
            </div>
          )}
        </Tab>

        {/* Driver Diaries Tab */}
        <Tab eventKey="driver-diaries" title="📅 Diaries">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>Driver Diaries</h4>
            <div className="d-flex gap-2">
              <Badge bg="info" className="fs-6">
                {driverDiaries.length} Drivers
              </Badge>
              <Button variant="outline-primary" onClick={() => !loading && loadAllData()} disabled={loading}>
                Refresh
              </Button>
            </div>
          </div>

          {/* View Mode Toggle */}
          <div className="mb-3">
            <div className="btn-group" role="group">
              <Button
                variant={driverDiaryViewMode === 'summary' ? 'primary' : 'outline-primary'}
                onClick={() => setDriverDiaryViewMode('summary')}
              >
                <i className="bi bi-list-ul me-1"></i>
                Summary View
              </Button>
              <Button
                variant={driverDiaryViewMode === 'diary' ? 'primary' : 'outline-primary'}
                onClick={() => setDriverDiaryViewMode('diary')}
              >
                <i className="bi bi-calendar-check me-1"></i>
                Driver View
              </Button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mb-3 position-relative" style={{ maxWidth: 500 }}>
            <Form.Control
              type="text"
              placeholder="Search drivers by name, email, or company..."
              value={driverDiarySearch}
              onChange={(e) => setDriverDiarySearch(e.target.value)}
              size="sm"
              style={{ paddingRight: '2rem' }}
            />
            {driverDiarySearch && (
              <Button
                variant="link"
                className="position-absolute top-50 end-0 translate-middle-y text-muted pe-3"
                onClick={() => setDriverDiarySearch('')}
                aria-label="Clear search"
                tabIndex={0}
                style={{ fontSize: '1.5rem', lineHeight: 1, textDecoration: 'none' }}
              >
                ×
              </Button>
            )}
          </div>
          
          {driverDiaries.length === 0 ? (
            <Card>
              <Card.Body className="text-center py-5">
                <i className="bi bi-calendar-x text-muted" style={{ fontSize: '3rem' }}></i>
                <h5 className="mt-3">No Drivers Found</h5>
                <p className="text-muted">No professional drivers are currently registered.</p>
              </Card.Body>
            </Card>
          ) : driverDiaryViewMode === 'summary' ? (
            // Summary View (Original Table View)
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Driver Name</th>
                  <th>Email</th>
                  <th>Company</th>
                  <th>Status</th>
                  <th>Diary Summary</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {driverDiaries
                  .filter(driver => {
                    if (!driverDiarySearch) return true;
                    const searchLower = driverDiarySearch.toLowerCase();
                    return (
                      driver.name?.toLowerCase().includes(searchLower) ||
                      driver.email?.toLowerCase().includes(searchLower) ||
                      driver.companyName?.toLowerCase().includes(searchLower)
                    );
                  })
                  .map(driver => (
                  <tr key={driver._id}>
                    <td>{driver.name}</td>
                    <td>{driver.email}</td>
                    <td>{driver.companyName || '-'}</td>
                    <td>{getStatusBadge(driver.subscriptionStatus)}</td>
                    <td>
                      <div className="d-flex gap-2">
                        <Badge bg="primary" title="Accepted Jobs">
                          {driver.diarySummary?.accepted || 0}
                        </Badge>
                        <Badge bg="success" title="Completed Jobs">
                          {driver.diarySummary?.completed || 0}
                        </Badge>
                        <Badge bg="warning" title="Future Jobs">
                          {driver.diarySummary?.future || 0}
                        </Badge>
                        <Badge bg="info" title="Total Jobs">
                          {driver.diarySummary?.total || 0}
                        </Badge>
                      </div>
                    </td>
                    <td>
                      <Dropdown>
                        <Dropdown.Toggle 
                        variant="outline-primary" 
                        size="sm"
                        disabled={driverDiaryLoading}
                      >
                        {driverDiaryLoading && selectedDriverDiary?._id === driver._id ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Loading...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-calendar-check me-1"></i>
                              Actions
                          </>
                        )}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => handleViewDriverDiary(driver)}>
                            📅 View Diary
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            // Driver View (Accordion Style)
            <div>
              {driverDiaries
                .filter(driver => {
                  if (!driverDiarySearch) return true;
                  const searchLower = driverDiarySearch.toLowerCase();
                  return (
                    driver.name?.toLowerCase().includes(searchLower) ||
                    driver.email?.toLowerCase().includes(searchLower) ||
                    driver.companyName?.toLowerCase().includes(searchLower)
                  );
                })
                .map(driver => {
                  // Get driver's jobs (this would need to be loaded)
                  const driverJobs = selectedDriverJobs.filter(job => job.driverId === driver._id) || [];
                  const organizedJobs = organizeJobsByTimePeriod(driverJobs);
                  
                  return (
                    <Card key={driver._id} className="mb-4">
                      <Card.Header>
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h5 className="mb-0">{driver.name}</h5>
                            <small className="text-muted">
                              {driver.email} • {driver.companyName || 'No Company'}
                            </small>
                          </div>
                          <div className="d-flex gap-2">
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              onClick={() => handleViewDriverDiary(driver)}
                              disabled={driverDiaryLoading}
                            >
                              {driverDiaryLoading && selectedDriverDiary?._id === driver._id ? (
                                <>
                                  <Spinner animation="border" size="sm" className="me-2" />
                                  Loading...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-calendar-check me-1"></i>
                                  Load Diary
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </Card.Header>
                      <Card.Body>
                        {driverJobs.length === 0 ? (
                          <div className="text-center py-3">
                            <p className="text-muted">Click "Load Diary" to view this driver's jobs</p>
                          </div>
                        ) : (
                          <div>
                            {/* Jobs organized by time period - exactly like driver sees */}
                            {organizedJobs.yesterday.length > 0 && (
                              <>
                                <h6 className="bg-dark text-warning text-center">YESTERDAY</h6>
                                <Accordion alwaysOpen={false}>
                                  {organizedJobs.yesterday.map((job, idx) => renderDriverJobItem(job, idx))}
                                </Accordion>
                              </>
                            )}
                            {organizedJobs.today.length > 0 && (
                              <>
                                <h6 className="bg-dark text-warning text-center">TODAY</h6>
                                <Accordion alwaysOpen={false}>
                                  {organizedJobs.today.map((job, idx) => renderDriverJobItem(job, idx))}
                                </Accordion>
                              </>
                            )}
                            {organizedJobs.tomorrow.length > 0 && (
                              <>
                                <h6 className="bg-dark text-warning text-center">TOMORROW</h6>
                                <Accordion alwaysOpen={false}>
                                  {organizedJobs.tomorrow.map((job, idx) => renderDriverJobItem(job, idx))}
                                </Accordion>
                              </>
                            )}
                            {organizedJobs.restOfWeek.length > 0 && (
                              <>
                                <h6 className="bg-dark text-warning text-center">REST OF WEEK (Start Mondays)</h6>
                                <Accordion alwaysOpen={false}>
                                  {organizedJobs.restOfWeek.map((job, idx) => renderDriverJobItem(job, idx))}
                                </Accordion>
                              </>
                            )}
                            {organizedJobs.nextWeek.length > 0 && (
                              <>
                                <h6 className="bg-dark text-warning text-center">NEXT WEEK</h6>
                                <Accordion alwaysOpen={false}>
                                  {organizedJobs.nextWeek.map((job, idx) => renderDriverJobItem(job, idx))}
                                </Accordion>
                              </>
                            )}
                            {organizedJobs.future.length > 0 && (
                              <>
                                <h6 className="bg-dark text-warning text-center">FUTURE</h6>
                                <Accordion alwaysOpen={false}>
                                  {organizedJobs.future.map((job, idx) => renderDriverJobItem(job, idx))}
                                </Accordion>
                              </>
                            )}
                            {Object.values(organizedJobs).every(jobs => jobs.length === 0) && (
                              <div className="text-center py-3">
                                <p className="text-muted">No jobs found for this driver</p>
                              </div>
                            )}
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  );
                })}
            </div>
          )}
        </Tab>

        {/* Audit Logs Tab */}
        <Tab eventKey="audit" title="📝 Audit">
          <h4>System Audit Logs</h4>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Action</th>
                <th>User</th>
                <th>Target</th>
                <th>Details</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.map((log, index) => (
                <tr key={index}>
                  <td>{log.action}</td>
                  <td>{log.userEmail}</td>
                  <td>{log.target}</td>
                  <td>{log.details}</td>
                  <td>{formatDate(log.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Tab>

        {/* Main Domain Tab */}
        <Tab eventKey="main-domain" title="🌐 Main Domain">
          <MainDomainBookings />
        </Tab>

        {/* Taxi Companies Tab */}
        <Tab eventKey="taxi-companies" title="🚕 Companies">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>Taxi Companies (Listing Void)</h4>
            <div>
              <Button variant="outline-primary" className="me-2" onClick={() => setShowCsvImportModal(true)}>
                Import CSV
              </Button>
              <Button variant="primary" onClick={() => {
                setSelectedItem(null);
                setTaxiCompanyForm({
                  company: '',
                  phone: '',
                  email: '',
                  postcodes: [''],
                  notes: ''
                });
                setShowTaxiCompanyModal(true);
              }}>
                Add Taxi Company
              </Button>
            </div>
          </div>
          
          {/* Search Bar */}
          <div className="mb-3">
            <Form.Control
              type="text"
              placeholder="Search taxi companies..."
              value={taxiCompanySearch}
              onChange={(e) => setTaxiCompanySearch(e.target.value)}
            />
          </div>

          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Company</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Postcodes</th>
                <th>Notes</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {taxiCompanies
                .filter(company => 
                  company.company.toLowerCase().includes(taxiCompanySearch.toLowerCase()) ||
                  company.phone.includes(taxiCompanySearch) ||
                  (company.email && company.email.toLowerCase().includes(taxiCompanySearch.toLowerCase())) ||
                  company.postcodes.some(pc => pc.toLowerCase().includes(taxiCompanySearch.toLowerCase()))
                )
                .map(company => (
                <tr key={company._id}>
                  <td>{company.company}</td>
                  <td>{company.phone}</td>
                  <td>{company.email || '-'}</td>
                  <td>
                    <div className="d-flex flex-wrap gap-1">
                      {company.postcodes.map((postcode, index) => (
                        <Badge key={index} bg="secondary" className="text-wrap">
                          {postcode}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td>{company.notes || '-'}</td>
                  <td>{formatDate(company.createdAt)}</td>
                  <td>
                    <Dropdown>
                      <Dropdown.Toggle size="sm">Actions</Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => handleEditTaxiCompany(company)}>
                          Edit
                        </Dropdown.Item>
                        <Dropdown.Item 
                          onClick={() => handleDeleteTaxiCompany(company._id)}
                          className="text-danger"
                        >
                          Delete
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Tab>

        {/* Job Monitoring Tab */}
        <Tab eventKey="job-monitoring" title="⚡ Jobs">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>Job Monitor Management</h4>
            <Badge bg="info" className="fs-6">
              {jobMonitors.length} Active Monitors
            </Badge>
          </div>

          {/* Users with Job Monitors */}
          <Card className="mb-4">
            <Card.Header>
              <h5>Drivers with Job Monitors</h5>
            </Card.Header>
            <Card.Body>
              {jobMonitors.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">No job monitors configured yet.</p>
                </div>
              ) : (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Driver Name</th>
                      <th>Email</th>
                      <th>Company</th>
                      <th>Status</th>
                      <th>Monitored Areas</th>
                      <th>Current Jobs</th>
                      <th>Last Check</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobMonitors.map(monitor => (
                      <tr key={monitor._id}>
                        <td>{monitor.userId.name}</td>
                        <td>{monitor.userId.email}</td>
                        <td>{monitor.userId.companyName || '-'}</td>
                        <td>{getStatusBadge(monitor.isActive ? 'active' : 'inactive')}</td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {monitor.postcodeAreas.map((area, index) => (
                              <Badge key={index} bg="secondary" className="text-wrap">
                                {area}
                              </Badge>
                            ))}
                          </div>
                        </td>
                        <td className="text-center">
                          <Badge bg="primary">{monitor.currentJobCount}</Badge>
                        </td>
                        <td>
                          {monitor.lastCheckTime ? formatDate(monitor.lastCheckTime) : 'Never'}
                        </td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle size="sm">Actions</Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => handleViewJobMonitor(monitor.userId)}>
                                Edit Settings
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => handleCopyMonitorAreas(monitor)}>
                                Copy Areas
                              </Dropdown.Item>
                              <Dropdown.Item 
                                onClick={() => handleDeleteJobMonitor(monitor.userId._id)}
                                className="text-danger"
                              >
                                Delete Monitor
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>

          {/* Users without Job Monitors */}
          <Card>
            <Card.Header>
              <h5>Drivers without Job Monitors</h5>
            </Card.Header>
            <Card.Body>
              {usersWithoutMonitors.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">All drivers have job monitors configured.</p>
                </div>
              ) : (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Driver Name</th>
                      <th>Email</th>
                      <th>Company</th>
                      <th>Coverage Areas</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersWithoutMonitors.map(user => (
                      <tr key={user._id}>
                        <td>{user.name}</td>
                        <td>{user.email}</td>
                        <td>{user.companyName || '-'}</td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {user.postcodeSectorsCovered?.map((area, index) => (
                              <Badge key={index} bg="info" className="text-wrap">
                                {area}
                              </Badge>
                            )) || (user.mainPostcode && (
                              <Badge bg="info">{user.mainPostcode}</Badge>
                            )) || '-'}
                          </div>
                        </td>
                        <td>
                          <Button 
                            variant="outline-primary" 
                            size="sm"
                            onClick={() => handleCreateJobMonitor(user)}
                          >
                            Create Monitor
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* SMS Templates Tab */}
        <Tab eventKey="sms-templates" title="📱 SMS">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>SMS Template Management</h4>
            <Badge bg="info" className="fs-6">
              📱 Preset SMS Messages
            </Badge>
          </div>

          <Alert variant="info">
            <h5>📱 SMS Template Overview</h5>
            <p className="mb-0">
              Manage preset SMS messages that drivers can use to quickly send updates to customers.
              These templates support placeholders like {'{driverName}'}, {'{driverPhone}'}, {'{from}'}, {'{to}'}, etc.
            </p>
          </Alert>

          {/* SMS Templates List */}
          <Card className="mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5>SMS Templates</h5>
              <Button variant="primary" size="sm" onClick={() => openSmsTemplateModal()}>
                <i className="fas fa-plus"></i> Add Template
              </Button>
            </Card.Header>
            <Card.Body>
              {smsTemplates.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">No SMS templates found. Create your first template to get started.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Order</th>
                        <th>Title</th>
                        <th>Message Preview</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {smsTemplates
                        .sort((a, b) => a.order - b.order)
                        .map((template) => (
                          <tr key={template._id}>
                            <td>{template.order}</td>
                            <td>
                              <strong>{template.title}</strong>
                              <br />
                              <small className="text-muted">{template.name}</small>
                            </td>
                            <td>
                              <div style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {template.message.substring(0, 100)}
                                {template.message.length > 100 && '...'}
                              </div>
                            </td>
                            <td>
                              <Badge bg={template.isActive ? 'success' : 'secondary'}>
                                {template.isActive ? 'Active' : 'Inactive'}
                              </Badge>
                            </td>
                            <td>
                              <Dropdown>
                                <Dropdown.Toggle size="sm" variant="outline-primary">
                                  Actions
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item onClick={() => openSmsTemplateModal(template)}>
                                    ✏️ Edit
                                  </Dropdown.Item>
                                  <Dropdown.Item onClick={() => handleToggleSmsTemplate(template._id)}>
                                    {template.isActive ? '👁️‍🗨️ Deactivate' : '👁️ Activate'}
                                  </Dropdown.Item>
                                  <Dropdown.Item 
                                  onClick={() => handleDeleteSmsTemplate(template._id)}
                                    className="text-danger"
                                  >
                                    🗑️ Delete
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* Referral Program Tab */}
        <Tab eventKey="referral-program" title="🤝 Referrals">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>Referral Program Management</h4>
            <Badge bg="success" className="fs-6">
              🚕 Refer a Friend System
            </Badge>
          </div>

          <Alert variant="info">
            <h5>💰 Referral Program Overview</h5>
            <p className="mb-0">
              Drivers can invite colleagues to try Taxi-Local.uk for 1 month for just £1. 
              When confirmed as full Member/Subscription, the referrer receives a £25 reward.
            </p>
          </Alert>

          {/* Referral Statistics */}
          <Card className="mb-4">
            <Card.Header>
              <h5>Referral Statistics</h5>
            </Card.Header>
            <Card.Body>
              {dataLoaded['referral-program'] ? (
                <Row>
                  <Col md={3}>
                    <div className="text-center">
                      <h3 className="text-primary">{systemStats.totalReferrals || 0}</h3>
                      <small className="text-muted">Total Referrals</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center">
                      <h3 className="text-info">{systemStats.invitedReferrals || 0}</h3>
                      <small className="text-muted">Invitations Sent</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center">
                      <h3 className="text-success">{systemStats.completedReferrals || 0}</h3>
                      <small className="text-muted">Completed Referrals</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center">
                      <h3 className="text-warning">£{(systemStats.totalRewardsPaid || 0) * 25}</h3>
                      <small className="text-muted">Total Rewards Paid</small>
                    </div>
                  </Col>
                </Row>
              ) : (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                  <p className="mt-2 text-muted">Loading referral statistics...</p>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Email Template Editor */}
          <Card className="mb-4">
            <Card.Header>
              <h5>📧 Email Template Editor</h5>
            </Card.Header>
            <Card.Body>
              <Form>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email Subject</Form.Label>
                      <Form.Control
                        type="text"
                        value={emailTemplate.subject}
                        onChange={(e) => setEmailTemplate(prev => ({ ...prev, subject: e.target.value }))}
                        placeholder="🚕 Your Taxi-Local.uk Invitation - 1 Month for £1!"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>From Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={emailTemplate.fromName}
                        onChange={(e) => setEmailTemplate(prev => ({ ...prev, fromName: e.target.value }))}
                        placeholder="Taxi-Local"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Form.Group className="mb-3">
                  <Form.Label>Email Content (HTML)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={12}
                    value={emailTemplate.content}
                    onChange={(e) => setEmailTemplate(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Enter HTML email content..."
                  />
                  <Form.Text className="text-muted">
                    Available variables: {'{referredName}, {referrerName}, {signupUrl}, {expiryDate}'}
                  </Form.Text>
                </Form.Group>

                <div className="d-flex gap-2">
                  <Button 
                    variant="primary" 
                    onClick={handleSaveEmailTemplate}
                    disabled={savingTemplate}
                  >
                    {savingTemplate ? <Spinner animation="border" size="sm" /> : '💾 Save Template'}
                  </Button>
                  <Button 
                    variant="outline-secondary" 
                    onClick={handleResetEmailTemplate}
                  >
                    🔄 Reset to Default
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>

          {/* Test Email Section */}
          <Card className="mb-4">
            <Card.Header>
              <h5>🧪 Test Email</h5>
            </Card.Header>
            <Card.Body>
              <Form>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Test Email Address</Form.Label>
                      <Form.Control
                        type="email"
                        value={testEmail.email}
                        onChange={(e) => setTestEmail(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="test@example.com"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Test Referred Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={testEmail.referredName}
                        onChange={(e) => setTestEmail(prev => ({ ...prev, referredName: e.target.value }))}
                        placeholder="John Doe"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Test Referrer Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={testEmail.referrerName}
                        onChange={(e) => setTestEmail(prev => ({ ...prev, referrerName: e.target.value }))}
                        placeholder="Jane Smith"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Test Company</Form.Label>
                      <Form.Control
                        type="text"
                        value={testEmail.company}
                        onChange={(e) => setTestEmail(prev => ({ ...prev, company: e.target.value }))}
                        placeholder="ABC Taxis"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Button 
                  variant="success" 
                  onClick={handleSendTestEmail}
                  disabled={sendingTestEmail}
                  className="me-2"
                >
                  {sendingTestEmail ? <Spinner animation="border" size="sm" /> : '📤 Send Test Email'}
                </Button>
                
                <Button 
                  variant="outline-info" 
                  onClick={handlePreviewEmail}
                >
                  👁️ Preview Email
                </Button>
              </Form>
            </Card.Body>
          </Card>

          {/* All Referrals Table */}
          <Card>
            <Card.Header>
              <h5>All Referrals</h5>
            </Card.Header>
            <Card.Body>
              <div className="text-center py-4">
                <p className="text-muted">
                  Referral data will be displayed here once the system is fully integrated.
                </p>
                <p className="text-muted">
                  This will show all referral invitations, their status, and reward claims.
                </p>
              </div>
            </Card.Body>
          </Card>
        </Tab>

        {/* Dispatcher Management Tab */}
        <Tab eventKey="dispatcher-management" title="👨‍💼 Dispatch">
          <DispatcherManagement />
        </Tab>

        {/* Merchandise Orders Tab */}
        <Tab eventKey="merchandise-orders" title="🛍️ Merchandise">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>Merchandise Management</h4>
            <div className="d-flex gap-2">
              <Badge bg="info" className="fs-6">
                {merchandiseOrders.length} Orders
              </Badge>
              <Badge bg="success" className="fs-6">
                {products.length} Products
              </Badge>
              <Button variant="outline-primary" onClick={() => !loading && loadAllData()} disabled={loading}>
                Refresh
              </Button>
            </div>
          </div>

          {/* Product Management Section */}
          <Card className="mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Product Management</h5>
              <Button variant="primary" onClick={handleAddProduct}>
                <i className="fas fa-plus me-2"></i>Add New Product
              </Button>
            </Card.Header>
            <Card.Body>
              <Table responsive striped hover>
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Product Name</th>
                    <th>Category</th>
                    <th>Price</th>
                    <th>Stock</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr key={product._id}>
                      <td>
                        {product.image ? (
                          <img 
                            src={product.image} 
                            alt={product.name}
                            style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
                          />
                        ) : (
                          <div 
                            style={{ 
                              width: '50px', 
                              height: '50px', 
                              backgroundColor: '#f8f9fa', 
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#6c757d'
                            }}
                          >
                            <i className="fas fa-image"></i>
                          </div>
                        )}
                      </td>
                      <td>
                        <div>
                          <strong>{product.name}</strong>
                          <br />
                          <small className="text-muted">{product.description}</small>
                        </div>
                      </td>
                      <td>
                        <Badge bg="secondary">{product.category}</Badge>
                      </td>
                      <td>£{product.price.toFixed(2)}</td>
                      <td>
                        <Badge bg={product.stockQuantity > 10 ? 'success' : product.stockQuantity > 0 ? 'warning' : 'danger'}>
                          {product.stockQuantity}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={product.isActive ? 'success' : 'secondary'}>
                          {product.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td>
                        <Dropdown>
                          <Dropdown.Toggle size="sm" variant="outline-primary">
                            Actions
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item onClick={() => handleEditProduct(product)}>
                              ✏️ Edit
                            </Dropdown.Item>
                            <Dropdown.Item 
                            onClick={() => handleDeleteProduct(product._id)}
                              className="text-danger"
                            >
                              🗑️ Delete
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {products.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-muted">No products found. Click "Add New Product" to create your first product.</p>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Statistics Cards */}
          {merchandiseStats.overall && (
            <Row className="mb-4">
              <Col md={3}>
                <Card className="text-center">
                  <Card.Body>
                    <h5 className="text-primary">{merchandiseStats.overall.totalOrders}</h5>
                    <small className="text-muted">Total Orders</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center">
                  <Card.Body>
                    <h5 className="text-success">£{merchandiseStats.overall.totalRevenue?.toFixed(2) || '0.00'}</h5>
                    <small className="text-muted">Total Revenue</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center">
                  <Card.Body>
                    <h5 className="text-warning">{merchandiseStats.overall.pendingOrders}</h5>
                    <small className="text-muted">Pending Orders</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center">
                  <Card.Body>
                    <h5 className="text-info">{merchandiseStats.overall.deliveredOrders}</h5>
                    <small className="text-muted">Delivered</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Orders Table */}
          <Table responsive striped hover>
            <thead>
              <tr>
                <th>Order #</th>
                <th>Customer</th>
                <th>Product</th>
                <th>Quantity</th>
                <th>Total</th>
                <th>Status</th>
                <th>Payment</th>
                <th>Order Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {merchandiseOrders.map((order) => (
                <tr key={order._id}>
                  <td>
                    <Badge bg="secondary">{order.orderNumber}</Badge>
                  </td>
                  <td>
                    <div>
                      <strong>{order.userId?.name || 'Unknown'}</strong>
                      <br />
                      <small className="text-muted">{order.userId?.email}</small>
                    </div>
                  </td>
                  <td>
                    <div>
                      <strong>{order.productName}</strong>
                      <br />
                      <small className="text-muted">{order.productId}</small>
                    </div>
                  </td>
                  <td>{order.quantity}</td>
                  <td>£{order.totalPrice.toFixed(2)}</td>
                  <td>
                    <Badge bg={
                      order.status === 'pending' ? 'warning' :
                      order.status === 'confirmed' ? 'info' :
                      order.status === 'in_production' ? 'primary' :
                      order.status === 'shipped' ? 'secondary' :
                      order.status === 'delivered' ? 'success' :
                      order.status === 'cancelled' ? 'danger' : 'secondary'
                    }>
                      {order.status.replace('_', ' ').toUpperCase()}
                    </Badge>
                  </td>
                  <td>
                    <Badge bg={
                      order.paymentStatus === 'pending' ? 'warning' :
                      order.paymentStatus === 'paid' ? 'success' :
                      order.paymentStatus === 'refunded' ? 'danger' : 'secondary'
                    }>
                      {order.paymentStatus.toUpperCase()}
                    </Badge>
                  </td>
                  <td>
                    {new Date(order.orderDate).toLocaleDateString('en-GB')}
                    <br />
                    <small className="text-muted">
                      {new Date(order.orderDate).toLocaleTimeString('en-GB')}
                    </small>
                  </td>
                  <td>
                    <Dropdown>
                      <Dropdown.Toggle size="sm" variant="outline-primary">
                        Actions
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => handleViewOrder(order)}>
                          ⚙️ Manage Order
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {merchandiseOrders.length === 0 && (
            <div className="text-center py-4">
              <p className="text-muted">No merchandise orders found.</p>
            </div>
          )}
        </Tab>

        {/* Driver Locations Tab */}
        <Tab eventKey="driver-locations" title="📍 Locations">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>Driver Location Map</h4>
            <div className="d-flex gap-2">
                    <Button
                      variant="outline-primary"
                      size="sm"
                onClick={() => {
                  const publicUrl = `${window.location.origin}/public-driver-locations`;
                  navigator.clipboard.writeText(publicUrl).then(() => {
                    toast.success('Public link copied to clipboard!');
                  }).catch(() => {
                    // Fallback for older browsers
                    const textArea = document.createElement('textarea');
                    textArea.value = publicUrl;
                    document.body.appendChild(textArea);
                    textArea.select();
                    document.execCommand('copy');
                    document.body.removeChild(textArea);
                    toast.success('Public link copied to clipboard!');
                  });
                }}
                title="Copy public link to share the map without login"
              >
                <i className="bi bi-link-45deg me-1"></i>
                Copy Public Link
                    </Button>
              <Badge bg="info" className="fs-6">
                Real-time Driver Locations
              </Badge>
            </div>
          </div>
          
          <DriverLocationMap />
        </Tab>

        {/* Email Logs Tab */}
        <Tab eventKey="email-logs" title="📧 Email Logs">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>Driver Email Logs</h4>
            <div className="d-flex gap-2">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => { loadEmailLogs(); loadEmailLogsSummary(); }}
                disabled={emailLogsLoading}
              >
                {emailLogsLoading ? <Spinner size="sm" className="me-1" /> : <i className="bi bi-arrow-clockwise me-1"></i>}
                Refresh
              </Button>
            </div>
          </div>

          {/* Email Logs Summary */}
          <Row className="mb-4">
            <Col md={12}>
              <Card>
                <Card.Header>
                  <h5>Email Statistics Summary</h5>
                </Card.Header>
                <Card.Body>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Driver</th>
                        <th>Company</th>
                        <th>Total Emails</th>
                        <th>Sent</th>
                        <th>Failed</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {emailLogsSummary.map((driver) => (
                        <tr key={driver.driverId}>
                          <td>
                            <div>
                              <strong>{driver.driverName}</strong>
                              <br />
                              <small className="text-muted">{driver.driverEmail}</small>
                            </div>
                          </td>
                          <td>{driver.companyName || 'N/A'}</td>
                          <td>
                            <Badge bg="primary">{driver.total}</Badge>
                          </td>
                          <td>
                            <Badge bg="success">{driver.sent}</Badge>
                          </td>
                          <td>
                            <Badge bg="danger">{driver.failed}</Badge>
                          </td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => loadDriverEmailLogs(driver.driverId)}
                            >
                              <i className="bi bi-eye me-1"></i>
                              View Logs
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Email Logs Filters */}
          <Row className="mb-4">
            <Col md={12}>
              <Card>
                <Card.Header>
                  <h5>Email Logs Filter</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Driver</Form.Label>
                        <Form.Select
                          value={emailLogsFilter.driverId}
                          onChange={(e) => handleEmailLogsFilterChange('driverId', e.target.value)}
                        >
                          <option value="">All Drivers</option>
                          {users.filter(u => u.role === 'professional').map(user => (
                            <option key={user._id} value={user._id}>
                              {user.name} ({user.email})
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label>Email Type</Form.Label>
                        <Form.Select
                          value={emailLogsFilter.emailType}
                          onChange={(e) => handleEmailLogsFilterChange('emailType', e.target.value)}
                        >
                          <option value="">All Types</option>
                          <option value="diary">Diary</option>
                          <option value="booking_confirmation">Booking Confirmation</option>
                          <option value="lead_notification">Lead Notification</option>
                          <option value="welcome">Welcome</option>
                          <option value="test">Test</option>
                          <option value="other">Other</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label>Status</Form.Label>
                        <Form.Select
                          value={emailLogsFilter.status}
                          onChange={(e) => handleEmailLogsFilterChange('status', e.target.value)}
                        >
                          <option value="">All Status</option>
                          <option value="sent">Sent</option>
                          <option value="failed">Failed</option>
                          <option value="pending">Pending</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label>Start Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={emailLogsFilter.startDate}
                          onChange={(e) => handleEmailLogsFilterChange('startDate', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label>End Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={emailLogsFilter.endDate}
                          onChange={(e) => handleEmailLogsFilterChange('endDate', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={1}>
                      <Form.Group>
                        <Form.Label>&nbsp;</Form.Label>
                        <div>
                          <Button
                            variant="primary"
                            onClick={applyEmailLogsFilter}
                            disabled={emailLogsLoading}
                          >
                            <i className="bi bi-search"></i>
                          </Button>
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Email Logs Table */}
          <Row>
            <Col md={12}>
              <Card>
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center">
                    <h5>Email Logs</h5>
                    <div>
                      <Badge bg="info" className="me-2">
                        Total: {emailLogsPagination.total}
                      </Badge>
                      {emailLogsPagination.hasMore && (
                        <Badge bg="warning">
                          More available
                        </Badge>
                      )}
                    </div>
                  </div>
                </Card.Header>
                <Card.Body>
                  {emailLogsLoading ? (
                    <div className="text-center">
                      <Spinner animation="border" />
                      <p className="mt-2">Loading email logs...</p>
                    </div>
                  ) : (
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Date & Time</th>
                          <th>Driver</th>
                          <th>Recipient</th>
                          <th>Subject</th>
                          <th>Type</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {emailLogs.map((log) => (
                          <tr 
                            key={log._id}
                            style={{ cursor: 'pointer' }}
                            onClick={() => showFullEmailContent(log)}
                            className="email-log-row"
                          >
                            <td>
                              <div>
                                <strong>{new Date(log.sentAt).toLocaleDateString()}</strong>
                                <br />
                                <small className="text-muted">
                                  {new Date(log.sentAt).toLocaleTimeString()}
                                </small>
                              </div>
                            </td>
                            <td>
                              <div>
                                <strong>{log.driverName}</strong>
                                <br />
                                <small className="text-muted">{log.driverEmail}</small>
                              </div>
                            </td>
                            <td>
                              <div>
                                {log.recipientName && (
                                  <strong>{log.recipientName}</strong>
                                )}
                                <br />
                                <small className="text-muted">{log.recipientEmail}</small>
                              </div>
                            </td>
                            <td>
                              <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {log.subject}
                              </div>
                            </td>
                            <td>
                              <Badge bg="secondary">{log.emailType}</Badge>
                            </td>
                            <td>
                              <Badge bg={log.status === 'sent' ? 'success' : log.status === 'failed' ? 'danger' : 'warning'}>
                                {log.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        {/* New Driver Features Guide Tab */}
        <Tab eventKey="driver-features-guide" title="📋 Driver Guide">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>New Driver Features Guide</h4>
            <div className="d-flex gap-2">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => window.open('/new-driver-features-guide.html', '_blank')}
              >
                <i className="bi bi-eye me-1"></i>
                View Guide
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  const textarea = document.getElementById('guide-editor');
                  if (textarea) {
                    textarea.focus();
                  }
                }}
              >
                <i className="bi bi-pencil me-1"></i>
                Edit Content
              </Button>
              <Button
                variant="outline-success"
                size="sm"
                onClick={() => {
                  const textarea = document.getElementById('guide-editor');
                  if (textarea) {
                    // Save the content (in a real implementation, this would save to backend)
                    alert('Guide content saved! (This would normally save to the database)');
                  }
                }}
              >
                <i className="bi bi-save me-1"></i>
                Save Changes
              </Button>
            </div>
          </div>

          <Row>
            <Col md={12}>
              <Card>
                <Card.Header>
                  <h5>Edit New Driver Features Guide</h5>
                  <small className="text-muted">
                    This guide is shown to new drivers to help them understand the benefits of the system.
                  </small>
                </Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <label htmlFor="guide-editor" className="form-label">
                      <strong>Guide Content (HTML)</strong>
                    </label>
                    <textarea
                      id="guide-editor"
                      className="form-control"
                      rows="20"
                      style={{ fontFamily: 'monospace', fontSize: '12px' }}
                      defaultValue={`<!-- New Driver Features Guide Content -->
<!-- This content is used to generate the interactive guide for new drivers -->
<!-- Edit this content to update the guide -->

<h1>🚗 Taxi-Local CRM: Complete Feature Guide for New Drivers</h1>

<h2>🎯 Why Choose Taxi-Local CRM?</h2>

<h3>💰 Increased Earnings</h3>
<ul>
  <li>Direct Lead Generation: Get jobs directly from customers</li>
  <li>Booking Links: Create your own booking links for repeat customers</li>
  <li>Real-time Job Alerts: Never miss a booking opportunity</li>
  <li>Financial Tracking: Complete earnings analysis and reporting</li>
</ul>

<h3>📱 Modern Technology</h3>
<ul>
  <li>Mobile-First Design: Works perfectly on phones and tablets</li>
  <li>Real-time Updates: Instant notifications and job status changes</li>
  <li>Location Tracking: GPS integration for better customer service</li>
  <li>Cloud-Based: Access your diary from anywhere, anytime</li>
</ul>

<!-- Add more content here as needed -->`}
                    />
                  </div>
                  
                  <div className="alert alert-info">
                    <h6><i className="bi bi-info-circle me-2"></i>How to Use This Editor</h6>
                    <ul className="mb-0">
                      <li><strong>Edit Content:</strong> Modify the HTML content above to update the guide</li>
                      <li><strong>View Guide:</strong> Click "View Guide" to see how it looks to new drivers</li>
                      <li><strong>Save Changes:</strong> Click "Save Changes" to update the guide (requires backend implementation)</li>
                      <li><strong>HTML Format:</strong> The content uses HTML formatting for rich text display</li>
                    </ul>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        {/* Audio Management Tab */}
        <Tab eventKey="audio" title="🎵 Audio">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>Audio File Management</h4>
            <div className="d-flex gap-2">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => { loadAudioFiles(); loadAudioMappings(); }}
                disabled={loading}
              >
                {loading ? <Spinner size="sm" className="me-1" /> : <i className="bi bi-arrow-clockwise me-1"></i>}
                Refresh
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => document.getElementById('audioFileInput').click()}
                disabled={audioUploading}
              >
                {audioUploading ? <Spinner size="sm" className="me-1" /> : <i className="bi bi-upload me-1"></i>}
                Upload Audio
              </Button>
               <Button
                 variant="success"
                 size="sm"
                 onClick={() => { setSelectedMapping(null); setShowMappingModal(true); }}
               >
                 <i className="bi bi-link me-1"></i>
                 Map Audio
               </Button>
               <Button
                 variant="info"
                 size="sm"
                 onClick={() => window.open('/admin/audio-test', '_blank')}
               >
                 <i className="bi bi-volume-up me-1"></i>
                 Test Audio
               </Button>
               <Button
                 variant="warning"
                 size="sm"
                 onClick={testNewJobAudioMappings}
               >
                 <i className="bi bi-bug me-1"></i>
                 Debug NewWeb/NewPool Mapping
               </Button>
               <Button
                 variant="success"
                 size="sm"
                 onClick={() => {
                   if (window.setupAudioMappings) {
                     window.setupAudioMappings();
                   } else {
                     toast.error('Setup script not loaded. Please refresh the page.');
                   }
                 }}
               >
                 <i className="bi bi-gear me-1"></i>
                 Setup Audio Mappings
               </Button>
               <Button
                 variant="info"
                 size="sm"
                 onClick={() => {
                   if (window.checkAndFixAudio) {
                     window.checkAndFixAudio();
                   } else {
                     toast.error('Check script not loaded. Please refresh the page.');
                   }
                 }}
               >
                 <i className="bi bi-search me-1"></i>
                 Check & Fix Audio
               </Button>
               <Button
                 variant="secondary"
                 size="sm"
                 onClick={() => {
                   if (window.testAudioNotifications) {
                     window.testAudioNotifications();
                   } else {
                     toast.error('Test script not loaded. Please refresh the page.');
                   }
                 }}
               >
                 <i className="bi bi-play me-1"></i>
                 Test Audio
               </Button>
            </div>
          </div>

          <input
            type="file"
            id="audioFileInput"
            accept="audio/*"
            onChange={handleAudioUpload}
            style={{ display: 'none' }}
          />

          <Card>
            <Card.Header>
              <h5 className="mb-0">Audio Files</h5>
            </Card.Header>
            <Card.Body>
              {audioFiles.length === 0 ? (
                <div className="text-center py-4">
                  <i className="bi bi-music-note-beamed display-4 text-muted"></i>
                  <p className="text-muted mt-2">No audio files uploaded yet</p>
                </div>
              ) : (
                <Table responsive striped hover>
                  <thead>
                    <tr>
                      <th>Filename</th>
                      <th>Size</th>
                      <th>Upload Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audioFiles.map((file) => (
                      <tr key={file._id}>
                        <td>
                          <div className="d-flex align-items-center">
                            <i className="bi bi-file-earmark-music me-2 text-primary"></i>
                            <span className="fw-medium">{file.originalName}</span>
                          </div>
                        </td>
                        <td>
                          <Badge bg="info">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </Badge>
                        </td>
                        <td>{formatDate(file.uploadedAt)}</td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => handleTestAudio(file)}
                              disabled={audioTestPlaying}
                            >
                              {audioTestPlaying && selectedAudioFile?._id === file._id ? (
                                <>
                                  <Spinner size="sm" className="me-1" />
                                  Playing...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-play-fill me-1"></i>
                                  Test
                                </>
                              )}
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteAudio(file._id)}
                            >
                              <i className="bi bi-trash me-1"></i>
                              Delete
                            </Button>
                          </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
              )}
            </Card.Body>
          </Card>

          {/* Audio Mappings Section */}
          <Card className="mt-4">
            <Card.Header>
              <h5 className="mb-0">Audio Function Mappings</h5>
              <small className="text-muted">Map audio files to specific system functions</small>
            </Card.Header>
            <Card.Body>
              {audioMappings.length === 0 ? (
            <div className="text-center py-4">
                  <i className="bi bi-link display-4 text-muted"></i>
                  <p className="text-muted mt-2">No audio mappings configured yet</p>
                  <Button
                    variant="primary"
                    onClick={() => { setSelectedMapping(null); setShowMappingModal(true); }}
                  >
                    <i className="bi bi-plus me-1"></i>
                    Create First Mapping
                  </Button>
            </div>
              ) : (
                <Table responsive striped hover>
                  <thead>
                    <tr>
                      <th>System Function</th>
                      <th>Audio File</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(audioMappings) && audioMappings.map((mapping) => {
                      const systemFunction = systemFunctions.find(f => f.id === mapping.functionId);
                      const audioFile = audioFiles.find(f => f._id === mapping.audioFileId);
                      return (
                        <tr key={mapping._id}>
                          <td>
                            <div>
                              <strong>{systemFunction?.name || 'Unknown Function'}</strong>
                              <br />
                              <small className="text-muted">{systemFunction?.description}</small>
                            </div>
                          </td>
                          <td>
                            {audioFile ? (
                              <div className="d-flex align-items-center">
                                <i className="bi bi-file-earmark-music me-2 text-primary"></i>
                                <span>{audioFile.originalName}</span>
                              </div>
                            ) : (
                              <span className="text-danger">Audio file not found</span>
                            )}
                          </td>
                          <td>
                            <Badge bg={mapping.isActive ? 'success' : 'secondary'}>
                              {mapping.isActive ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleEditMapping(mapping)}
                              >
                                <i className="bi bi-pencil me-1"></i>
                                Edit
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeleteMapping(mapping._id)}
                              >
                                <i className="bi bi-trash me-1"></i>
                                Delete
                              </Button>
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={async () => {
                                  try {
                                    console.log(`🧪 Testing audio for function: ${mapping.functionId}`);
                                    const audioFile = await audioNotificationService.getAudioForFunction(mapping.functionId);
                                    if (audioFile) {
                                      await audioNotificationService.playAudio(audioFile);
                                      toast.success(`✅ Tested audio for ${systemFunction?.name || mapping.functionId}`);
                                    } else {
                                      toast.error(`❌ No audio found for ${systemFunction?.name || mapping.functionId}`);
                                    }
                                  } catch (error) {
                                    console.error('Audio test failed:', error);
                                    toast.error(`❌ Audio test failed: ${error.message}`);
                                  }
                                }}
                              >
                                <i className="bi bi-play me-1"></i>
                                Test
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>

          {/* Audio Testing Section */}
          <Card className="mt-4">
            <Card.Header>
              <h5 className="mb-0">Audio Testing</h5>
              <small className="text-muted">Test audio mappings for different job sources</small>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <h6>Job Source Testing</h6>
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => playAudioForJobEvent('pool', 'new_job')}
                    >
                      <i className="bi bi-play me-1"></i>
                      Test Pool New Job
                    </Button>
                    <Button
                      variant="outline-success"
                      size="sm"
                      onClick={() => playAudioForJobEvent('mylink', 'new_job')}
                    >
                      <i className="bi bi-play me-1"></i>
                      Test MyLink New Job
                    </Button>
                    <Button
                      variant="outline-info"
                      size="sm"
                      onClick={() => playAudioForJobEvent('general', 'new_job')}
                    >
                      <i className="bi bi-play me-1"></i>
                      Test General New Job
                    </Button>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      variant="outline-warning"
                      size="sm"
                      onClick={() => playAudioForJobEvent('pool', 'job_accepted')}
                    >
                      <i className="bi bi-play me-1"></i>
                      Test Pool Job Accepted
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => playAudioForJobEvent('mylink', 'job_accepted')}
                    >
                      <i className="bi bi-play me-1"></i>
                      Test MyLink Job Accepted
                    </Button>
                  </div>
                </Col>
                <Col md={6}>
                  <h6>System Event Testing</h6>
                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={async () => {
                        try {
                          const response = await axios.get('/admin/audio-mappings/function/sleep_mode');
                          if (response.data && response.data.audioFile) {
                            const audio = new Audio(response.data.audioFile.url);
                            audio.volume = 0.8;
                            audio.play().then(() => {
                              console.log('✅ Sleep mode test audio played successfully');
                            }).catch(error => {
                              console.error('❌ Sleep mode test audio failed:', error);
                            });
                          } else {
                            console.log('🔇 No mapped audio found for sleep mode');
                            toast.warning('No audio mapping found for Sleep Mode');
                          }
                        } catch (error) {
                          console.error('❌ Error testing sleep mode audio:', error);
                          toast.error('Failed to test sleep mode audio');
                        }
                      }}
                    >
                      <i className="bi bi-play me-1"></i>
                      Test Sleep Mode
                    </Button>
                    <Button
                      variant="outline-dark"
                      size="sm"
                      onClick={async () => {
                        try {
                          const response = await axios.get('/admin/audio-mappings/function/system_error');
                          if (response.data && response.data.audioFile) {
                            const audio = new Audio(response.data.audioFile.url);
                            audio.volume = 0.8;
                            audio.play().then(() => {
                              console.log('✅ System error test audio played successfully');
                            }).catch(error => {
                              console.error('❌ System error test audio failed:', error);
                            });
                          } else {
                            console.log('🔇 No mapped audio found for system error');
                            toast.warning('No audio mapping found for System Error');
                          }
                        } catch (error) {
                          console.error('❌ Error testing system error audio:', error);
                          toast.error('Failed to test system error audio');
                        }
                      }}
                    >
                      <i className="bi bi-play me-1"></i>
                      Test System Error
                    </Button>
                  </div>
                </Col>
              </Row>
              <Alert variant="info" className="mt-3">
                <i className="bi bi-info-circle me-2"></i>
                <strong>Usage:</strong> Use these buttons to test your audio mappings. 
                Make sure you have created mappings for the job sources you want to test.
              </Alert>
            </Card.Body>
          </Card>

          {/* Audio Test Modal */}
          <Modal show={showAudioModal} onHide={() => setShowAudioModal(false)}>
            <Modal.Header closeButton>
              <Modal.Title>Audio Test</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {selectedAudioFile && (
                <div className="text-center">
                  <h5>{selectedAudioFile.originalName}</h5>
                  <p className="text-muted">
                    Size: {(selectedAudioFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <audio
                    controls
                    className="w-100"
                    src={`/uploads/audio/${selectedAudioFile.filename}`}
                  >
                    Your browser does not support the audio element.
                  </audio>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowAudioModal(false)}>
                Close
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Audio Mapping Modal */}
          <Modal show={showMappingModal} onHide={() => { setShowMappingModal(false); setSelectedMapping(null); }} size="lg">
            <Modal.Header closeButton>
              <Modal.Title>{selectedMapping ? 'Edit Audio Mapping' : 'Create Audio Mapping'}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const mappingData = {
                functionId: formData.get('functionId'),
                audioFileId: formData.get('audioFileId'),
                isActive: formData.get('isActive') === 'on'
              };
              handleSaveMapping(mappingData);
            }}>
              <Modal.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>System Function</Form.Label>
                      <Form.Select name="functionId" defaultValue={selectedMapping?.functionId || ''} required>
                        <option value="">Select a system function</option>
                        {systemFunctions.map(func => (
                          <option key={func.id} value={func.id}>
                            {func.name}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Choose which system function this audio will be used for
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Audio File</Form.Label>
                      <Form.Select name="audioFileId" defaultValue={selectedMapping?.audioFileId || ''} required>
                        <option value="">Select an audio file</option>
                        {audioFiles.map(file => (
                          <option key={file._id} value={file._id}>
                            {file.originalName} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Choose which audio file to use for this function
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        name="isActive"
                        label="Active"
                        defaultChecked={selectedMapping?.isActive !== false}
                      />
                      <Form.Text className="text-muted">
                        Enable this audio mapping
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                {selectedMapping && (
                  <Alert variant="info">
                    <i className="bi bi-info-circle me-2"></i>
                    Editing existing mapping. Changes will take effect immediately.
                  </Alert>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => { setShowMappingModal(false); setSelectedMapping(null); }}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit">
                  {selectedMapping ? 'Update Mapping' : 'Create Mapping'}
                </Button>
              </Modal.Footer>
            </Form>
          </Modal>
        </Tab>

        {/* Fare Tariffs Tab */}
        <Tab eventKey="fare-tariffs" title="💷 Fare Tariffs">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>Licensing Bodies & Tariff Information</h4>
            <Button variant="primary" onClick={handleAddLicensingBody}>
              <i className="bi bi-plus-lg me-1"></i>
              Add Licensing Body
            </Button>
          </div>

          {licensingBodies.length === 0 ? (
            <Card>
              <Card.Body className="text-center py-5">
                <i className="bi bi-building" style={{ fontSize: '3rem', color: '#ccc' }}></i>
                <h5 className="mt-3">No Licensing Bodies</h5>
                <p className="text-muted">Start by adding a licensing body and their tariff information.</p>
                <Button variant="primary" onClick={handleAddLicensingBody}>
                  <i className="bi bi-plus-lg me-1"></i>
                  Add First Licensing Body
                </Button>
              </Card.Body>
            </Card>
          ) : (
            <Row>
              {licensingBodies.map((body) => (
                <Col md={12} lg={6} xl={4} key={body._id} className="mb-3">
                  <Card className="h-100">
                    <Card.Header className="d-flex justify-content-between align-items-center bg-primary text-white">
                      <div>
                        <strong>{body.code}</strong> - {body.name}
                      </div>
                      <div>
                        {body.active ? (
                          <Badge bg="success">Active</Badge>
                        ) : (
                          <Badge bg="secondary">Inactive</Badge>
                        )}
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <div className="mb-3">
                        <h6 className="text-muted mb-2">Basic Information</h6>
                        <small className="d-block"><strong>Full Name:</strong> {body.fullName || body.name}</small>
                        {body.region && <small className="d-block"><strong>Region:</strong> {body.region}</small>}
                        {body.country && <small className="d-block"><strong>Country:</strong> {body.country}</small>}
                      </div>

                      {(body.contactEmail || body.contactPhone || body.website) && (
                        <div className="mb-3">
                          <h6 className="text-muted mb-2">Contact Information</h6>
                          {body.contactEmail && <small className="d-block"><strong>Email:</strong> {body.contactEmail}</small>}
                          {body.contactPhone && <small className="d-block"><strong>Phone:</strong> {body.contactPhone}</small>}
                          {body.website && <small className="d-block"><strong>Website:</strong> <a href={body.website} target="_blank" rel="noopener noreferrer">{body.website}</a></small>}
                        </div>
                      )}

                      {body.postcodes && body.postcodes.length > 0 && (
                        <div className="mb-3">
                          <h6 className="text-muted mb-2">Postcodes ({body.postcodes.length})</h6>
                          <div className="d-flex flex-wrap gap-1">
                            {body.postcodes.map((postcode, idx) => (
                              <Badge key={idx} bg="info" className="text-white">{postcode}</Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      <div>
                        <h6 className="text-muted mb-2">Tariffs ({body.tariffs?.length || 0})</h6>
                        {body.tariffs && body.tariffs.length > 0 ? (
                          <div className="tariff-list">
                            {body.tariffs.map((tariff, idx) => (
                              <div key={tariff._id || idx} className="border rounded p-2 mb-2" style={{ backgroundColor: tariff.active ? '#f8f9fa' : '#fff3cd' }}>
                                <div className="d-flex justify-content-between align-items-start">
                                  <div className="flex-grow-1">
                                    <strong>{tariff.name}</strong>
                                    {!tariff.active && <Badge bg="warning" className="ms-2">Inactive</Badge>}
                                    {tariff.description && <div><small className="text-muted">{tariff.description}</small></div>}
                                    <div className="mt-1">
                                      <small>
                                        <strong>1st Mile:</strong> £{tariff.firstMile?.toFixed(2)} | 
                                        <strong> Subsequent:</strong> £{tariff.subsequentMile?.toFixed(2)}
                                      </small>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <small className="text-muted">No tariffs defined</small>
                        )}
                      </div>

                      {body.notes && (
                        <div className="mt-3">
                          <h6 className="text-muted mb-2">Notes</h6>
                          <small>{body.notes}</small>
                        </div>
                      )}
                    </Card.Body>
                    <Card.Footer className="d-flex gap-2 justify-content-end">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEditLicensingBody(body)}
                      >
                        <i className="bi bi-pencil me-1"></i>
                        Edit
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteLicensingBody(body._id)}
                      >
                        <i className="bi bi-trash me-1"></i>
                        Delete
                      </Button>
                    </Card.Footer>
                  </Card>
                </Col>
              ))}
            </Row>
          )}

          {/* Licensing Body Modal */}
          <Modal show={showLicensingBodyModal} onHide={() => setShowLicensingBodyModal(false)} size="lg">
            <Modal.Header closeButton>
              <Modal.Title>
                {selectedLicensingBody ? 'Edit Licensing Body' : 'Add New Licensing Body'}
              </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSaveLicensingBody}>
              <Modal.Body>
                <h6 className="border-bottom pb-2 mb-3">Basic Information</h6>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Code *</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g., EDDC"
                        value={licensingBodyForm.code}
                        onChange={(e) => setLicensingBodyForm({...licensingBodyForm, code: e.target.value.toUpperCase()})}
                        required
                      />
                      <Form.Text className="text-muted">Short code identifier</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={8}>
                    <Form.Group className="mb-3">
                      <Form.Label>Name *</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g., East Devon"
                        value={licensingBodyForm.name}
                        onChange={(e) => setLicensingBodyForm({...licensingBodyForm, name: e.target.value})}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Full Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g., East Devon District Council"
                        value={licensingBodyForm.fullName}
                        onChange={(e) => setLicensingBodyForm({...licensingBodyForm, fullName: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Region</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g., Devon"
                        value={licensingBodyForm.region}
                        onChange={(e) => setLicensingBodyForm({...licensingBodyForm, region: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Country</Form.Label>
                      <Form.Control
                        type="text"
                        value={licensingBodyForm.country}
                        onChange={(e) => setLicensingBodyForm({...licensingBodyForm, country: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <h6 className="border-bottom pb-2 mb-3 mt-4">Contact Information</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Contact Email</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="email@example.com"
                        value={licensingBodyForm.contactEmail}
                        onChange={(e) => setLicensingBodyForm({...licensingBodyForm, contactEmail: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Contact Phone</Form.Label>
                      <Form.Control
                        type="tel"
                        placeholder="01234 567890"
                        value={licensingBodyForm.contactPhone}
                        onChange={(e) => setLicensingBodyForm({...licensingBodyForm, contactPhone: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Website</Form.Label>
                      <Form.Control
                        type="url"
                        placeholder="https://example.com"
                        value={licensingBodyForm.website}
                        onChange={(e) => setLicensingBodyForm({...licensingBodyForm, website: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <h6 className="border-bottom pb-2 mb-3 mt-4">Postcode Districts</h6>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Add Postcode District</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="text"
                          placeholder="e.g., EX12, EX13, EX14"
                          value={newPostcodeInput}
                          onChange={(e) => setNewPostcodeInput(e.target.value.toUpperCase())}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddLicensingBodyPostcode();
                            }
                          }}
                        />
                        <Button 
                          variant="outline-primary" 
                          onClick={handleAddLicensingBodyPostcode}
                        >
                          <i className="bi bi-plus-lg me-1"></i>
                          Add
                        </Button>
                      </InputGroup>
                      <Form.Text className="text-muted">
                        Enter postcode districts (e.g., EX12, EX13) that this licensing body covers. Press Enter or click Add.
                      </Form.Text>
                    </Form.Group>
                    
                    {licensingBodyForm.postcodes.length > 0 && (
                      <div className="mb-3">
                        <div className="d-flex flex-wrap gap-2">
                          {licensingBodyForm.postcodes.map((postcode, idx) => (
                            <Badge 
                              key={idx} 
                              bg="info" 
                              className="d-flex align-items-center gap-1"
                              style={{ fontSize: '0.9rem', padding: '0.5rem' }}
                            >
                              {postcode}
                              <i 
                                className="bi bi-x-circle" 
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleRemoveLicensingBodyPostcode(postcode)}
                              ></i>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </Col>
                </Row>

                <h6 className="border-bottom pb-2 mb-3 mt-4">
                  Tariffs
                  <Button
                    variant="outline-success"
                    size="sm"
                    className="ms-3"
                    onClick={handleAddTariff}
                  >
                    <i className="bi bi-plus me-1"></i>
                    Add Tariff
                  </Button>
                </h6>

                {licensingBodyForm.tariffs.length === 0 ? (
                  <Alert variant="info">
                    <i className="bi bi-info-circle me-2"></i>
                    No tariffs added yet. Click "Add Tariff" to define tariff rates.
                  </Alert>
                ) : (
                  <div className="tariff-list mb-3">
                    {licensingBodyForm.tariffs.map((tariff, idx) => (
                      <Card key={tariff._id || idx} className="mb-2">
                        <Card.Body className="py-2">
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="flex-grow-1">
                              <strong>{tariff.name}</strong>
                              {!tariff.active && <Badge bg="warning" className="ms-2">Inactive</Badge>}
                              {tariff.description && <div><small className="text-muted">{tariff.description}</small></div>}
                              {tariff.validFrom && (
                                <div>
                                  <small className="text-muted">
                                    <i className="bi bi-calendar-check me-1"></i>
                                    Valid from: {new Date(tariff.validFrom).toLocaleDateString('en-GB')}
                                  </small>
                                </div>
                              )}
                              <div className="mt-1">
                                <small>
                                  <strong>1st Mile:</strong> £{tariff.firstMile?.toFixed(2)} | 
                                  <strong> Subsequent:</strong> £{tariff.subsequentMile?.toFixed(2)}
                                </small>
                              </div>
                            </div>
                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleEditTariff(tariff)}
                              >
                                <i className="bi bi-pencil"></i>
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeleteTariff(tariff._id)}
                              >
                                <i className="bi bi-trash"></i>
                              </Button>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                )}

                <h6 className="border-bottom pb-2 mb-3 mt-4">Additional Information</h6>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Notes</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Additional notes or information..."
                        value={licensingBodyForm.notes}
                        onChange={(e) => setLicensingBodyForm({...licensingBodyForm, notes: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Check
                        type="checkbox"
                        label="Active"
                        checked={licensingBodyForm.active}
                        onChange={(e) => setLicensingBodyForm({...licensingBodyForm, active: e.target.checked})}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowLicensingBodyModal(false)}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit">
                  <i className="bi bi-save me-1"></i>
                  {selectedLicensingBody ? 'Update' : 'Create'} Licensing Body
                </Button>
              </Modal.Footer>
            </Form>
          </Modal>

          {/* Tariff Modal */}
          <Modal show={showTariffModal} onHide={() => setShowTariffModal(false)}>
            <Modal.Header closeButton>
              <Modal.Title>
                {selectedTariff ? 'Edit Tariff' : 'Add New Tariff'}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Valid From</Form.Label>
                <Form.Control
                  type="date"
                  value={tariffForm.validFrom}
                  onChange={(e) => setTariffForm({...tariffForm, validFrom: e.target.value})}
                />
                <Form.Text className="text-muted">Date when this tariff became/becomes valid</Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Tariff Name *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., Tariff 1, Weekday Rate"
                  value={tariffForm.name}
                  onChange={(e) => setTariffForm({...tariffForm, name: e.target.value})}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="e.g., Weekday daytime rate (7am-7pm)"
                  value={tariffForm.description}
                  onChange={(e) => setTariffForm({...tariffForm, description: e.target.value})}
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>First Mile (£) *</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="5.96"
                      value={tariffForm.firstMile}
                      onChange={(e) => setTariffForm({...tariffForm, firstMile: e.target.value})}
                      required
                    />
                    <Form.Text className="text-muted">Rate for first mile</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Subsequent Mile (£) *</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="2.73"
                      value={tariffForm.subsequentMile}
                      onChange={(e) => setTariffForm({...tariffForm, subsequentMile: e.target.value})}
                      required
                    />
                    <Form.Text className="text-muted">Rate per additional mile</Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group>
                <Form.Check
                  type="checkbox"
                  label="Active"
                  checked={tariffForm.active}
                  onChange={(e) => setTariffForm({...tariffForm, active: e.target.checked})}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowTariffModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSaveTariff}>
                <i className="bi bi-save me-1"></i>
                {selectedTariff ? 'Update' : 'Add'} Tariff
              </Button>
            </Modal.Footer>
          </Modal>
        </Tab>
      </Tabs>

      {/* User Modal */}
      <Modal show={showUserModal} onHide={() => setShowUserModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedItem ? 'Edit User' : 'Create New User'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={selectedItem ? handleUpdateUser : handleCreateUser}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>First Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={userForm.firstName}
                    onChange={(e) => setUserForm({...userForm, firstName: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Surname *</Form.Label>
                  <Form.Control
                    type="text"
                    value={userForm.surname}
                    onChange={(e) => setUserForm({...userForm, surname: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email *</Form.Label>
                  <Form.Control
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Password {!selectedItem && '*'}</Form.Label>
                  <Form.Control
                    type="password"
                    value={userForm.password}
                    onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                    required={!selectedItem}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Role</Form.Label>
                  <Form.Select
                    value={userForm.role}
                    onChange={(e) => setUserForm({...userForm, role: e.target.value})}
                  >
                    <option value="professional">Professional</option>
                    <option value="admin">Admin</option>
                    <option value="listing_only">Listing Only</option>
                    <option value="dispatch">Dispatch</option>
                    <option value="user">User</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    type="tel"
                    value={userForm.phoneNumber}
                    onChange={(e) => setUserForm({...userForm, phoneNumber: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={userForm.subscriptionStatus}
                    onChange={(e) => setUserForm({...userForm, subscriptionStatus: e.target.value})}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* Driver Profile Section */}
            <hr className="my-4" />
            <h5>Driver Profile Information</h5>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Company Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={userForm.companyName}
                    onChange={(e) => setUserForm({...userForm, companyName: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Website</Form.Label>
                  <Form.Control
                    type="url"
                    value={userForm.website}
                    onChange={(e) => setUserForm({...userForm, website: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Company Address</Form.Label>
                  <Form.Control
                    type="text"
                    value={userForm.companyAddress}
                    onChange={(e) => setUserForm({...userForm, companyAddress: e.target.value})}
                    placeholder="Street, Town, Postcode"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Vehicle Information */}
            <h6 className="mt-3 mb-2">Vehicle Information</h6>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Car Make</Form.Label>
                  <Form.Control
                    type="text"
                    value={userForm.carMake}
                    onChange={(e) => setUserForm({...userForm, carMake: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Car Model</Form.Label>
                  <Form.Control
                    type="text"
                    value={userForm.carModel}
                    onChange={(e) => setUserForm({...userForm, carModel: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Number of Seats</Form.Label>
                  <Form.Control
                    type="text"
                    value={userForm.carSeats}
                    onChange={(e) => setUserForm({...userForm, carSeats: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Registration Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={userForm.regNumber}
                    onChange={(e) => setUserForm({...userForm, regNumber: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>License Type</Form.Label>
                  <Form.Select
                    value={userForm.licenseType}
                    onChange={(e) => setUserForm({...userForm, licenseType: e.target.value})}
                  >
                    <option value="Hackney License">Hackney License</option>
                    <option value="Private Hire">Private Hire</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Vehicle Hackney/Private ID</Form.Label>
                  <Form.Control
                    type="text"
                    value={userForm.vehicleLicenseId}
                    onChange={(e) => setUserForm({...userForm, vehicleLicenseId: e.target.value})}
                    placeholder="e.g., Hackney plate number"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Vehicle License Expiry</Form.Label>
                  <Form.Control
                    type="date"
                    value={userForm.vehicleLicenseExpiry ? userForm.vehicleLicenseExpiry.split('T')[0] : ''}
                    onChange={(e) => setUserForm({...userForm, vehicleLicenseExpiry: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>

            <h6 className="mt-3 mb-2">Document Expiry Dates</h6>
            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Road Tax Expiry</Form.Label>
                  <Form.Control
                    type="date"
                    value={userForm.roadTaxExpiry ? userForm.roadTaxExpiry.split('T')[0] : ''}
                    onChange={(e) => setUserForm({...userForm, roadTaxExpiry: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>MOT Expiry</Form.Label>
                  <Form.Control
                    type="date"
                    value={userForm.motExpiry ? userForm.motExpiry.split('T')[0] : ''}
                    onChange={(e) => setUserForm({...userForm, motExpiry: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Insurance Expiry</Form.Label>
                  <Form.Control
                    type="date"
                    value={userForm.insuranceExpiry ? userForm.insuranceExpiry.split('T')[0] : ''}
                    onChange={(e) => setUserForm({...userForm, insuranceExpiry: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Vehicle Inspection Expiry</Form.Label>
                  <Form.Control
                    type="date"
                    value={userForm.vehicleInspectionExpiry ? userForm.vehicleInspectionExpiry.split('T')[0] : ''}
                    onChange={(e) => setUserForm({...userForm, vehicleInspectionExpiry: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-4">
              <Form.Label className="fw-bold">Job Card Vehicle</Form.Label>
              <div className="d-flex flex-column flex-sm-row gap-3">
                <Form.Check
                  type="radio"
                  id="adminPreferredVehiclePrimary"
                  name="adminPreferredVehicleForJobs"
                  value="primary"
                  label="Car No1 (Primary Vehicle)"
                  checked={userForm.preferredVehicleForJobs !== 'spare'}
                  onChange={(e) => setUserForm({...userForm, preferredVehicleForJobs: e.target.value})}
                />
                <Form.Check
                  type="radio"
                  id="adminPreferredVehicleSpare"
                  name="adminPreferredVehicleForJobs"
                  value="spare"
                  label="Car No2 (Spare Vehicle)"
                  checked={userForm.preferredVehicleForJobs === 'spare'}
                  disabled={!adminSpareVehicleHasData}
                  onChange={(e) => setUserForm({...userForm, preferredVehicleForJobs: e.target.value})}
                />
              </div>
              <Form.Text className="text-muted">Car No1 is default.</Form.Text>
            </Form.Group>

            <h6 className="mt-3 mb-2">Spare / Second Vehicle (Optional)</h6>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Car Make</Form.Label>
                  <Form.Control
                    type="text"
                    value={userForm.spareVehicle?.carMake || ''}
                    onChange={(e) => handleAdminSpareVehicleChange('carMake', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Car Model</Form.Label>
                  <Form.Control
                    type="text"
                    value={userForm.spareVehicle?.carModel || ''}
                    onChange={(e) => handleAdminSpareVehicleChange('carModel', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Number of Seats</Form.Label>
                  <Form.Control
                    type="number"
                    value={userForm.spareVehicle?.carSeats || ''}
                    onChange={(e) => handleAdminSpareVehicleChange('carSeats', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Registration Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={userForm.spareVehicle?.regNumber || ''}
                    onChange={(e) => handleAdminSpareVehicleChange('regNumber', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Vehicle Hackney/Private ID</Form.Label>
                  <Form.Control
                    type="text"
                    value={userForm.spareVehicle?.vehicleLicenseId || ''}
                    onChange={(e) => handleAdminSpareVehicleChange('vehicleLicenseId', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Vehicle License Expiry</Form.Label>
                  <Form.Control
                    type="date"
                    value={userForm.spareVehicle?.vehicleLicenseExpiry ? userForm.spareVehicle.vehicleLicenseExpiry.split('T')[0] : ''}
                    onChange={(e) => handleAdminSpareVehicleChange('vehicleLicenseExpiry', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Road Tax Expiry</Form.Label>
                  <Form.Control
                    type="date"
                    value={userForm.spareVehicle?.roadTaxExpiry ? userForm.spareVehicle.roadTaxExpiry.split('T')[0] : ''}
                    onChange={(e) => handleAdminSpareVehicleChange('roadTaxExpiry', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>MOT Expiry</Form.Label>
                  <Form.Control
                    type="date"
                    value={userForm.spareVehicle?.motExpiry ? userForm.spareVehicle.motExpiry.split('T')[0] : ''}
                    onChange={(e) => handleAdminSpareVehicleChange('motExpiry', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Insurance Expiry</Form.Label>
                  <Form.Control
                    type="date"
                    value={userForm.spareVehicle?.insuranceExpiry ? userForm.spareVehicle.insuranceExpiry.split('T')[0] : ''}
                    onChange={(e) => handleAdminSpareVehicleChange('insuranceExpiry', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Vehicle Inspection Expiry</Form.Label>
                  <Form.Control
                    type="date"
                    value={userForm.spareVehicle?.vehicleInspectionExpiry ? userForm.spareVehicle.vehicleInspectionExpiry.split('T')[0] : ''}
                    onChange={(e) => handleAdminSpareVehicleChange('vehicleInspectionExpiry', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Photo / Reference URL</Form.Label>
                  <Form.Control
                    type="text"
                    value={userForm.spareVehicle?.photoOfCar || ''}
                    onChange={(e) => handleAdminSpareVehicleChange('photoOfCar', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Licensing Information */}
            <h6 className="mt-3 mb-2">Licensing Information</h6>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Licensing Authority</Form.Label>
                  <Form.Control
                    type="text"
                    value={userForm.licensingAuthority}
                    onChange={(e) => setUserForm({...userForm, licensingAuthority: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Badge Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={userForm.badgeNumber}
                    onChange={(e) => setUserForm({...userForm, badgeNumber: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Coverage Area */}
            <h6 className="mt-3 mb-2">Coverage Area</h6>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Main Postcode</Form.Label>
                  <Form.Control
                    type="text"
                    value={userForm.mainPostcode}
                    onChange={(e) => setUserForm({...userForm, mainPostcode: e.target.value})}
                    placeholder="e.g., EX12"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Postcode Sectors Covered</Form.Label>
                  <Form.Control
                    type="text"
                    value={userForm.postcodeSectorsCovered}
                    onChange={(e) => setUserForm({...userForm, postcodeSectorsCovered: e.target.value})}
                    placeholder="e.g., EX12, EX13, DT6"
                  />
                </Form.Group>
              </Col>
            </Row>

            <h6 className="mt-3 mb-2">Email & Notification Preferences</h6>
            <Form.Group className="mb-2">
              <Form.Check
                type="checkbox"
                label="Receive new job leads by email"
                checked={!!userForm.leadEmailOptIn}
                onChange={(e) => setUserForm({...userForm, leadEmailOptIn: e.target.checked})}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Check
                type="checkbox"
                label="Email diary each morning at 6am"
                checked={!!userForm.emailDiaryDaily}
                onChange={(e) => setUserForm({...userForm, emailDiaryDaily: e.target.checked})}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Enable audio notifications"
                checked={!!userForm.audioNotifications}
                onChange={(e) => setUserForm({...userForm, audioNotifications: e.target.checked})}
              />
            </Form.Group>

            <h6 className="mt-3 mb-2">Dispatch Settings</h6>
            <Form.Group className="mb-2">
              <Form.Check
                type="checkbox"
                label="Allow Dispatch Module access"
                checked={!!userForm.dispatchAccessAccepted}
                onChange={(e) => setUserForm({...userForm, dispatchAccessAccepted: e.target.checked})}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Dispatcher Email</Form.Label>
              <Form.Control
                type="email"
                value={userForm.dispatchEmail}
                onChange={(e) => setUserForm({...userForm, dispatchEmail: e.target.value})}
                placeholder="dispatcher@example.com"
              />
            </Form.Group>

            <h6 className="mt-3 mb-2">Visual Assets</h6>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Logo URL</Form.Label>
                  <Form.Control
                    type="text"
                    value={userForm.logo}
                    onChange={(e) => setUserForm({...userForm, logo: e.target.value})}
                    placeholder="https://example.com/logo.png"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Car Photo URL</Form.Label>
                  <Form.Control
                    type="text"
                    value={userForm.photoOfCar}
                    onChange={(e) => setUserForm({...userForm, photoOfCar: e.target.value})}
                    placeholder="https://example.com/car.jpg"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Driver ID Badge URL</Form.Label>
                  <Form.Control
                    type="text"
                    value={userForm.photoOfDriver}
                    onChange={(e) => setUserForm({...userForm, photoOfDriver: e.target.value})}
                    placeholder="https://example.com/badge.jpg"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Bank Details */}
            <hr className="my-4" />
            <h5>Bank Details</h5>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Account Type</Form.Label>
                  <Form.Select
                    value={userForm.accountType}
                    onChange={(e) => setUserForm({...userForm, accountType: e.target.value})}
                  >
                    <option value="Personal">Personal</option>
                    <option value="Business">Business</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Account Holder Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={userForm.accountName}
                    onChange={(e) => setUserForm({...userForm, accountName: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Sort Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={userForm.accountSortCode}
                    onChange={(e) => setUserForm({...userForm, accountSortCode: e.target.value})}
                    placeholder="12-34-56"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Account Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={userForm.accountNumber}
                    onChange={(e) => setUserForm({...userForm, accountNumber: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Label className="mt-2">Accepted Payment Methods</Form.Label>
            <div className="d-flex flex-column gap-1 mb-3">
              <Form.Check
                type="checkbox"
                label="Cash"
                checked={userForm.paymentMethods?.includes('Cash')}
                onChange={() => handleUserPaymentMethodToggle('Cash')}
              />
              <Form.Check
                type="checkbox"
                label="Card"
                checked={userForm.paymentMethods?.includes('Card')}
                onChange={() => handleUserPaymentMethodToggle('Card')}
              />
              <Form.Check
                type="checkbox"
                label="Bank Transfer"
                checked={userForm.paymentMethods?.includes('BankTransfer')}
                onChange={() => handleUserPaymentMethodToggle('BankTransfer')}
              />
              <Form.Check
                type="checkbox"
                label="Invoiced"
                checked={userForm.paymentMethods?.includes('Invoiced')}
                onChange={() => handleUserPaymentMethodToggle('Invoiced')}
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowUserModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedItem ? 'Update Profile' : 'Create User'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Lead Modal */}
      <Modal show={showLeadModal} onHide={() => {
        setShowLeadModal(false);
        setSelectedItem(null);
        setLeadForm({
          customerName: '', customerEmail: '', customerPhone: '',
          from: '', to: '', dateTime: '', passengers: 1,
          status: 'pending', returnTrip: false
        });
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedItem ? 'Edit Lead' : 'Create New Lead'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmitLead}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Customer Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={leadForm.customerName}
                    onChange={(e) => setLeadForm({...leadForm, customerName: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Customer Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={leadForm.customerEmail}
                    onChange={(e) => setLeadForm({...leadForm, customerEmail: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Customer Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    value={leadForm.customerPhone}
                    onChange={(e) => setLeadForm({...leadForm, customerPhone: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Passengers</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={leadForm.passengers}
                    onChange={(e) => setLeadForm({...leadForm, passengers: parseInt(e.target.value)})}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>From *</Form.Label>
                  <Form.Control
                    type="text"
                    value={leadForm.from}
                    onChange={(e) => setLeadForm({...leadForm, from: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>To *</Form.Label>
                  <Form.Control
                    type="text"
                    value={leadForm.to}
                    onChange={(e) => setLeadForm({...leadForm, to: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Date & Time *</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={leadForm.dateTime}
                    onChange={(e) => setLeadForm({...leadForm, dateTime: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={leadForm.status}
                    onChange={(e) => setLeadForm({...leadForm, status: e.target.value})}
                  >
                    <option value="pending">Pending</option>
                    <option value="accepted">Accepted</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Return Trip"
                checked={leadForm.returnTrip}
                onChange={(e) => setLeadForm({...leadForm, returnTrip: e.target.checked})}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => {
              setShowLeadModal(false);
              setSelectedItem(null);
              setLeadForm({
                customerName: '', customerEmail: '', customerPhone: '',
                from: '', to: '', dateTime: '', passengers: 1,
                status: 'pending', returnTrip: false
              });
            }}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedItem ? 'Update Lead' : 'Create Lead'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Banner Modal */}
      <Modal show={showBannerModal} onHide={() => {
        setShowBannerModal(false);
        setSelectedItem(null);
        setBannerForm({
          title: '', message: '', audience: 'all',
          startsAt: '', endsAt: '', isActive: true, priority: 0,
          targetPostcodes: '', rotationInterval: 5, imageFile: null,
          imagePreview: null, imageLoading: false
        });
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedItem ? 'Edit Banner' : 'Create New Banner'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={selectedItem ? handleUpdateBanner : handleCreateBanner}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Title *</Form.Label>
                  <Form.Control
                    type="text"
                    value={bannerForm.title}
                    onChange={(e) => setBannerForm({...bannerForm, title: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Audience</Form.Label>
                  <Form.Select
                    value={bannerForm.audience}
                    onChange={(e) => setBannerForm({...bannerForm, audience: e.target.value})}
                  >
                    <option value="all">All Users</option>
                    <option value="guest">Guest Users</option>
                    <option value="driver">Drivers Only</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Message</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={bannerForm.message}
                onChange={(e) => setBannerForm({...bannerForm, message: e.target.value})}
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Target Postcodes</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., SW1A 1AA, W1A 1AA (comma separated)"
                    value={bannerForm.targetPostcodes}
                    onChange={(e) => setBannerForm({...bannerForm, targetPostcodes: e.target.value})}
                  />
                  <Form.Text className="text-muted">Leave empty to show in all areas</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Rotation Interval (seconds)</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="60"
                    value={bannerForm.rotationInterval}
                    onChange={(e) => setBannerForm({...bannerForm, rotationInterval: parseInt(e.target.value)})}
                  />
                  <Form.Text className="text-muted">How often to rotate this banner</Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={bannerForm.startsAt}
                    onChange={(e) => setBannerForm({...bannerForm, startsAt: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={bannerForm.endsAt}
                    onChange={(e) => setBannerForm({...bannerForm, endsAt: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Priority</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={bannerForm.priority}
                    onChange={(e) => setBannerForm({...bannerForm, priority: parseInt(e.target.value)})}
                  />
                  <Form.Text className="text-muted">Higher numbers show first</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Image (430mm x 175mm recommended)</Form.Label>
                  
                  {/* Display current image if editing or new image preview */}
                  {((selectedItem && selectedItem.imageDataUrl) || bannerForm.imagePreview) && (
                    <div className="mb-3">
                      <div className="border rounded p-2 bg-light">
                        {bannerForm.imageLoading ? (
                          <div className="text-center py-3">
                            <div className="spinner-border spinner-border-sm me-2" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            <small className="text-muted">Resizing image...</small>
                          </div>
                        ) : (
                          <>
                            <img
                              src={bannerForm.imagePreview || selectedItem.imageDataUrl}
                              alt="Banner preview"
                              style={{ 
                                maxWidth: '100%', 
                                height: 'auto',
                                maxHeight: '150px',
                                display: 'block',
                                margin: '0 auto'
                              }}
                            />
                            <div className="text-center mt-2">
                              <small className="text-muted">
                                {bannerForm.imagePreview ? 'New banner image (resized to 430mm x 175mm)' : 'Current banner image'}
                              </small>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        // Show loading state
                        setBannerForm(prev => ({...prev, imageFile: file, imageLoading: true}));
                        
                        // Resize and set the file
                        resizeImageToBannerSize(file).then((resizedFile) => {
                          setBannerForm(prev => ({
                            ...prev, 
                            imageFile: resizedFile, 
                            imageLoading: false,
                            imagePreview: URL.createObjectURL(resizedFile)
                          }));
                        });
                      } else {
                        setBannerForm(prev => ({
                          ...prev, 
                          imageFile: null, 
                          imagePreview: null,
                          imageLoading: false
                        }));
                      }
                    }}
                  />
                  <Form.Text className="text-muted">
                    PNG, JPG, or TIFF format. Images will be automatically resized to 430mm x 175mm (1622px x 661px).
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active"
                checked={bannerForm.isActive}
                onChange={(e) => setBannerForm({...bannerForm, isActive: e.target.checked})}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => {
              setShowBannerModal(false);
              setSelectedItem(null);
              setBannerForm({
                title: '', message: '', audience: 'all',
                startsAt: '', endsAt: '', isActive: true, priority: 0,
                targetPostcodes: '', rotationInterval: 5, imageFile: null,
                imagePreview: null, imageLoading: false
              });
            }}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedItem ? 'Update Banner' : 'Create Banner'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* System Settings Modal */}
      <Modal show={showSystemSettingsModal} onHide={() => setShowSystemSettingsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>System Settings</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdateSystemSettings}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Site Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={systemSettings.siteName}
                    onChange={(e) => setSystemSettings({...systemSettings, siteName: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Session Timeout (hours)</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="168"
                    value={systemSettings.sessionTimeout}
                    onChange={(e) => setSystemSettings({...systemSettings, sessionTimeout: parseInt(e.target.value)})}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Customer Lead Timeout (seconds)</Form.Label>
                  <Form.Control
                    type="number"
                    min="15"
                    max="600"
                    step="5"
                    value={systemSettings.leadTimeoutSeconds}
                    onChange={(e) => setSystemSettings({...systemSettings, leadTimeoutSeconds: parseInt(e.target.value) || 0})}
                  />
                  <Form.Text className="text-muted">How long to keep contacting companies before timing out.</Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Max File Upload Size (MB)</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="50"
                    value={systemSettings.maxFileUploadSize}
                    onChange={(e) => setSystemSettings({...systemSettings, maxFileUploadSize: parseInt(e.target.value)})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Default Banner Postcode</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., ZZ01"
                    value={systemSettings.defaultBannerPostcode}
                    onChange={(e) => setSystemSettings({...systemSettings, defaultBannerPostcode: e.target.value})}
                  />
                  <Form.Text className="text-muted">Default postcode used for banner targeting when no postcodes are provided.</Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Maintenance Mode"
                checked={systemSettings.maintenanceMode}
                onChange={(e) => setSystemSettings({...systemSettings, maintenanceMode: e.target.checked})}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Allow New Registrations"
                checked={systemSettings.allowNewRegistrations}
                onChange={(e) => setSystemSettings({...systemSettings, allowNewRegistrations: e.target.checked})}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Email Notifications"
                checked={systemSettings.emailNotifications}
                onChange={(e) => setSystemSettings({...systemSettings, emailNotifications: e.target.checked})}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowSystemSettingsModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Save Settings
            </Button>
          </Modal.Footer>
        </Form>
             </Modal>

               {/* Column Selector Modal */}
        <Modal show={showColumnSelector} onHide={() => setShowColumnSelector(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Manage Table Columns</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-muted">Select columns to display in the table:</span>
                <div className="d-flex gap-2">
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={handleSelectAllColumns}
                  >
                    Select All
                  </Button>
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    onClick={handleDeselectAllColumns}
                  >
                    Deselect All
                  </Button>
                </div>
              </div>
              <div className="row">
                {availableColumns.map(column => (
                  <div key={column.key} className="col-md-6 mb-2">
                    <Form.Check
                      type="checkbox"
                      id={`column-${column.key}`}
                      label={
                        <div>
                          <strong>{column.label}</strong>
                          <small className="text-muted d-block">
                            {column.type} • {visibleColumns.includes(column.key) ? 'Visible' : 'Hidden'}
                          </small>
                        </div>
                      }
                      checked={visibleColumns.includes(column.key)}
                      onChange={() => handleColumnToggle(column.key)}
                    />
                  </div>
                ))}
              </div>
            </div>
            <Alert variant="info">
              <strong>Tip:</strong> You can drag and reorder columns by clicking and dragging the column headers in the table.
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowColumnSelector(false)}>
              Close
            </Button>
            <Button 
              variant="primary" 
              onClick={() => {
                if (visibleColumns.length === 0) {
                  toast.warning('Please select at least one column to display');
                  return;
                }
                setShowColumnSelector(false);
                toast.success(`Table updated with ${visibleColumns.length} columns`);
              }}
            >
              Apply Changes
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Bulk Actions Modal */}
        <Modal show={showBulkActionsModal} onHide={() => setShowBulkActionsModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Bulk Actions - {selectedLeads.length} Selected</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="d-grid gap-2">
              <Button 
                variant="outline-primary" 
                onClick={() => {
                  handleBulkStatusUpdate('pending');
                  setShowBulkActionsModal(false);
                }}
              >
                📋 Set to Pending
              </Button>
              <Button 
                variant="outline-success" 
                onClick={() => {
                  handleBulkStatusUpdate('completed');
                  setShowBulkActionsModal(false);
                }}
              >
                ✅ Set to Completed
              </Button>
              <Button 
                variant="outline-warning" 
                onClick={() => {
                  handleBulkStatusUpdate('cancelled');
                  setShowBulkActionsModal(false);
                }}
              >
                ❌ Set to Cancelled
              </Button>
              <hr />
              <Button 
                variant="outline-danger" 
                onClick={() => {
                  setShowBulkActionsModal(false);
                  handleBulkDeleteLeads();
                }}
              >
                🗑️ Delete Selected Leads
              </Button>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowBulkActionsModal(false)}>
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Driver Diary Modal */}
        <Modal 
          show={showDriverDiaryModal} 
          onHide={() => {
            setShowDriverDiaryModal(false);
            setSelectedDriverDiary(null);
            setDriverDiaryEntries([]);
          }} 
          size="xl"
          fullscreen="lg-down"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {selectedDriverDiary && (
                <>
                  <i className="bi bi-calendar-check me-2"></i>
                  {selectedDriverDiary.name}'s Diary
                  <small className="text-muted d-block">
                    {selectedDriverDiary.email} • {selectedDriverDiary.companyName || 'No Company'}
                  </small>
                </>
              )}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedDriverDiary && (
              <>
                {/* Filter Controls */}
                <Card className="mb-4">
                  <Card.Body>
                    <h6 className="mb-3">Filter Entries</h6>
                    <Row>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Status</Form.Label>
                          <Form.Select
                            value={driverDiaryFilter.status}
                            onChange={(e) => setDriverDiaryFilter(prev => ({ ...prev, status: e.target.value }))}
                          >
                            <option value="">All Statuses</option>
                            <option value="accepted">Accepted</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Start Date</Form.Label>
                          <Form.Control
                            type="date"
                            value={driverDiaryFilter.startDate}
                            onChange={(e) => setDriverDiaryFilter(prev => ({ ...prev, startDate: e.target.value }))}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>End Date</Form.Label>
                          <Form.Control
                            type="date"
                            value={driverDiaryFilter.endDate}
                            onChange={(e) => setDriverDiaryFilter(prev => ({ ...prev, endDate: e.target.value }))}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3} className="d-flex align-items-end">
                        <div className="d-flex gap-2 w-100">
                          <Button 
                            variant="outline-primary" 
                            onClick={handleApplyDiaryFilter}
                            disabled={driverDiaryLoading}
                          >
                            Apply Filter
                          </Button>
                          <Button 
                            variant="outline-secondary" 
                            onClick={() => {
                              setDriverDiaryFilter({ 
                                status: 'accepted', 
                                startDate: new Date().toISOString().split('T')[0], 
                                endDate: '' 
                              });
                              handleViewDriverDiary(selectedDriverDiary, {});
                            }}
                            disabled={driverDiaryLoading}
                          >
                            Reset to Default
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* Driver Summary */}
                <Row className="mb-4">
                  <Col md={3}>
                    <Card className="text-center">
                      <Card.Body className="py-2">
                        <h6 className="mb-0">{driverDiaryEntries.filter(e => e.status === 'accepted').length}</h6>
                        <small className="text-muted">Accepted Jobs</small>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="text-center">
                      <Card.Body className="py-2">
                        <h6 className="mb-0">{driverDiaryEntries.filter(e => e.status === 'completed').length}</h6>
                        <small className="text-muted">Completed Jobs</small>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="text-center">
                      <Card.Body className="py-2">
                        <h6 className="mb-0">{driverDiaryEntries.filter(e => e.status === 'accepted' && new Date(e.dateTime) > new Date()).length}</h6>
                        <small className="text-muted">Future Jobs</small>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="text-center">
                      <Card.Body className="py-2">
                        <h6 className="mb-0">{driverDiaryEntries.length}</h6>
                        <small className="text-muted">Total Entries</small>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {/* Diary Entries */}
                {driverDiaryEntries.length === 0 ? (
                  <Card>
                    <Card.Body className="text-center py-5">
                      <i className="bi bi-calendar-x text-muted" style={{ fontSize: '3rem' }}></i>
                      <h5 className="mt-3">No Diary Entries</h5>
                      <p className="text-muted">This driver has no diary entries yet.</p>
                    </Card.Body>
                  </Card>
                ) : (
                  <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    <Table striped bordered hover responsive>
                      <thead className="sticky-top bg-white">
                        <tr>
                          <th>Date & Time</th>
                          <th>Customer</th>
                          <th>From → To</th>
                          <th>Status</th>
                          <th>Passengers</th>
                          <th>Fare</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {driverDiaryEntries.map(entry => (
                          <tr key={entry._id}>
                            <td>
                              <div>
                                <strong>{formatDate(entry.dateTime)}</strong>
                                <br />
                                <small className="text-muted">
                                  {new Date(entry.dateTime).toLocaleTimeString('en-GB', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </small>
                              </div>
                            </td>
                            <td>
                              <div>
                                <strong>{entry.customerName}</strong>
                                <br />
                                <small className="text-muted">{entry.customerPhone}</small>
                              </div>
                            </td>
                            <td>
                              <div>
                                <strong>{entry.from}</strong>
                                <br />
                                <small className="text-muted">→ {entry.to}</small>
                              </div>
                            </td>
                            <td>{getStatusBadge(entry.status)}</td>
                            <td className="text-center">{entry.passengers}</td>
                            <td>
                              {entry.fareAmount ? `£${entry.fareAmount}` : '-'}
                            </td>
                            <td>
                              {entry.notes ? (
                                <span title={entry.notes}>
                                  {entry.notes.length > 30 ? `${entry.notes.substring(0, 30)}...` : entry.notes}
                                </span>
                              ) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => {
              setShowDriverDiaryModal(false);
              setSelectedDriverDiary(null);
              setDriverDiaryEntries([]);
            }}>
              Close
            </Button>
            <Button 
              variant="outline-primary" 
              onClick={handleRefreshDriverDiary}
              disabled={driverDiaryLoading}
            >
              {driverDiaryLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Refreshing...
                </>
              ) : (
                <>
                  <i className="bi bi-arrow-clockwise me-1"></i>
                  Refresh
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Taxi Company Modal */}
        <Modal show={showTaxiCompanyModal} onHide={() => setShowTaxiCompanyModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{selectedItem ? 'Edit Taxi Company' : 'Add Taxi Company'}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={selectedItem ? handleUpdateTaxiCompany : handleCreateTaxiCompany}>
            <Modal.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Company Name *</Form.Label>
                    <Form.Control
                      type="text"
                      value={taxiCompanyForm.company}
                      onChange={(e) => setTaxiCompanyForm({...taxiCompanyForm, company: e.target.value})}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Phone Number *</Form.Label>
                    <Form.Control
                      type="tel"
                      value={taxiCompanyForm.phone}
                      onChange={(e) => setTaxiCompanyForm({...taxiCompanyForm, phone: e.target.value})}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email Address</Form.Label>
                    <Form.Control
                      type="email"
                      value={taxiCompanyForm.email}
                      onChange={(e) => setTaxiCompanyForm({...taxiCompanyForm, email: e.target.value})}
                      placeholder="optional@example.com"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Postcodes *</Form.Label>
                {taxiCompanyForm.postcodes.map((postcode, index) => (
                  <div key={index} className="d-flex mb-2">
                    <Form.Control
                      type="text"
                      value={postcode}
                      onChange={(e) => updatePostcode(index, e.target.value)}
                      placeholder="Enter postcode"
                      required
                    />
                    {taxiCompanyForm.postcodes.length > 1 && (
                      <Button
                        type="button"
                        variant="outline-danger"
                        className="ms-2"
                        onClick={() => removePostcodeField(index)}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={addPostcodeField}
                >
                  Add Another Postcode
                </Button>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={taxiCompanyForm.notes}
                  onChange={(e) => setTaxiCompanyForm({...taxiCompanyForm, notes: e.target.value})}
                  placeholder="Additional notes about this taxi company..."
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowTaxiCompanyModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {selectedItem ? 'Update' : 'Create'} Taxi Company
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* CSV Import Modal */}
        <Modal show={showCsvImportModal} onHide={() => setShowCsvImportModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Import Taxi Companies from CSV</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="info">
              <strong>CSV Format Requirements:</strong><br/>
              • Required columns: <code>company</code>, <code>phone</code><br/>
              • Optional columns: <code>email</code>, <code>postcodes</code>, <code>notes</code><br/>
              • Postcodes should be comma-separated (e.g., "SW1A 1AA, W1A 1AA")<br/>
              • First row should contain headers
            </Alert>

            <Form.Group className="mb-3">
              <Form.Label>Select CSV File</Form.Label>
              <Form.Control
                type="file"
                accept=".csv"
                onChange={handleCsvFileChange}
              />
            </Form.Group>

            {csvPreview.length > 0 && (
              <div className="mb-3">
                <h6>Preview (First 5 rows):</h6>
                <Table striped bordered size="sm">
                  <thead>
                    <tr>
                      {Object.keys(csvPreview[0] || {}).map(header => (
                        <th key={header}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {csvPreview.map((row, index) => (
                      <tr key={index}>
                        {Object.values(row).map((value, i) => (
                          <td key={i}>{value}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            {importResults && (
              <div className="mb-3">
                <h6>Import Results:</h6>
                <Alert variant={importResults.results.errors.length === 0 ? "success" : "warning"}>
                  {importResults.message}
                </Alert>
                
                {importResults.results.success.length > 0 && (
                  <div className="mb-2">
                    <strong>Successfully imported:</strong>
                    <ul className="mb-0">
                      {importResults.results.success.map((item, index) => (
                        <li key={index}>Row {item.row}: {item.company}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {importResults.results.errors.length > 0 && (
                  <div>
                    <strong>Errors:</strong>
                    <ul className="mb-0">
                      {importResults.results.errors.map((item, index) => (
                        <li key={index}>Row {item.row}: {item.error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => {
              setShowCsvImportModal(false);
              setCsvFile(null);
              setCsvPreview([]);
              setImportResults(null);
            }}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleCsvImport}
              disabled={!csvFile}
            >
              Import CSV
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Ticket Modal */}
        <Modal show={showTicketModal} onHide={() => {
          setShowTicketModal(false);
          setSelectedItem(null);
          setTicketForm({
            title: '',
            description: '',
            status: 'open',
            priority: 'medium',
            response: ''
          });
          setTicketResponses([]);
        }} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Support Ticket - {selectedItem?.title}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleUpdateTicket}>
            <Modal.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      type="text"
                      value={ticketForm.title}
                      onChange={(e) => setTicketForm({...ticketForm, title: e.target.value})}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={ticketForm.status}
                      onChange={(e) => setTicketForm({...ticketForm, status: e.target.value})}
                    >
                      <option value="open">Open</option>
                      <option value="in-progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Priority</Form.Label>
                    <Form.Select
                      value={ticketForm.priority}
                      onChange={(e) => setTicketForm({...ticketForm, priority: e.target.value})}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Submitted By</Form.Label>
                    <Form.Control
                      type="text"
                      value={selectedItem?.userName || ''}
                      disabled
                      className="bg-light"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={ticketForm.description}
                  onChange={(e) => setTicketForm({...ticketForm, description: e.target.value})}
                  required
                />
              </Form.Group>
              {/* Response Thread */}
              {ticketResponses.length > 0 && (
                <div className="mb-4">
                  <Form.Label>Response Thread</Form.Label>
                  <div className="border rounded p-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {ticketResponses.map((response, index) => (
                      <div key={index} className="mb-3 pb-3 border-bottom">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <strong className="text-primary">{response.adminName || 'Admin'}</strong>
                            <small className="text-muted ms-2">
                              {formatDate(response.createdAt)}
                            </small>
                          </div>
                        </div>
                        <div className="bg-light p-2 rounded">
                          {response.response}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Add Admin Response</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={ticketForm.response}
                  onChange={(e) => setTicketForm({...ticketForm, response: e.target.value})}
                  placeholder="Enter your response to the user..."
                />
              </Form.Group>
              {selectedItem && (
                <div className="mb-3">
                  <Form.Label>Ticket Information</Form.Label>
                  <div className="bg-light p-3 rounded">
                    <small className="text-muted">
                      <strong>Created:</strong> {formatDate(selectedItem.createdAt)}<br/>
                      <strong>Last Updated:</strong> {selectedItem.updatedAt ? formatDate(selectedItem.updatedAt) : 'Not updated'}<br/>
                      <strong>Ticket ID:</strong> {selectedItem._id}
                    </small>
                  </div>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => {
                setShowTicketModal(false);
                setSelectedItem(null);
                setTicketForm({
                  title: '',
                  description: '',
                  status: 'open',
                  priority: 'medium',
                  response: ''
                });
                setTicketResponses([]);
              }}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {ticketForm.response && ticketForm.response.trim() !== '' ? 'Add Response & Update' : 'Update Ticket'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Job Monitor Modal */}
        <Modal show={showJobMonitorModal} onHide={() => {
          setShowJobMonitorModal(false);
          setSelectedJobMonitor(null);
          setJobMonitorForm({
            isActive: false,
            postcodeAreas: [],
            alertSettings: {
              screenFlash: true,
              soundAlert: true,
              emailAlert: false,
              checkInterval: 5,
              autoStopAfterMinutes: 30
            }
          });
        }} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {selectedJobMonitor ? `Edit Job Monitor - ${selectedJobMonitor.user.name}` : 'Job Monitor Settings'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleUpdateJobMonitor}>
            <Modal.Body>
              {selectedJobMonitor && (
                <div className="mb-4">
                  <h6>Driver Information</h6>
                  <div className="row">
                    <div className="col-md-6">
                      <p><strong>Name:</strong> {selectedJobMonitor.user.name}</p>
                      <p><strong>Email:</strong> {selectedJobMonitor.user.email}</p>
                      <p><strong>Company:</strong> {selectedJobMonitor.user.companyName || 'N/A'}</p>
                    </div>
                    <div className="col-md-6">
                      <p><strong>Current Jobs:</strong> {selectedJobMonitor.currentJobCount}</p>
                      <p><strong>Monitored Areas:</strong> {selectedJobMonitor.monitoredAreas?.join(', ') || 'None'}</p>
                    </div>
                  </div>
                </div>
              )}

              <hr />

              <h6>Monitor Settings</h6>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Active Monitoring"
                      checked={jobMonitorForm.isActive}
                      onChange={(e) => setJobMonitorForm({
                        ...jobMonitorForm,
                        isActive: e.target.checked
                      })}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <h6>Monitored Postcode Areas</h6>
              <div className="mb-3">
                <div className="d-flex flex-wrap gap-1 mb-2">
                  {jobMonitorForm.postcodeAreas.map((area, index) => (
                    <Badge key={index} bg="info" className="text-wrap">
                      {area}
                      <Button
                        variant="link"
                        size="sm"
                        className="text-white p-0 ms-1"
                        onClick={() => handleRemovePostcode(index)}
                      >
                        ×
                      </Button>
                    </Badge>
                  ))}
                </div>
                <div className="d-flex gap-1">
                  <Form.Control
                    type="text"
                    placeholder="Add postcode area (e.g., EX12)"
                    value={newPostcode}
                    onChange={(e) => setNewPostcode(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddPostcode()}
                  />
                  <Button variant="outline-primary" onClick={handleAddPostcode}>
                    Add
                  </Button>
                </div>
              </div>

              <h6>Alert Settings</h6>
              <Row>
                <Col md={6}>
                  <Form.Check
                    type="checkbox"
                    label="Screen Flash"
                    checked={jobMonitorForm.alertSettings.screenFlash}
                    onChange={(e) => setJobMonitorForm({
                      ...jobMonitorForm,
                      alertSettings: {
                        ...jobMonitorForm.alertSettings,
                        screenFlash: e.target.checked
                      }
                    })}
                  />
                  <Form.Check
                    type="checkbox"
                    label="Sound Alert"
                    checked={jobMonitorForm.alertSettings.soundAlert}
                    onChange={(e) => setJobMonitorForm({
                      ...jobMonitorForm,
                      alertSettings: {
                        ...jobMonitorForm.alertSettings,
                        soundAlert: e.target.checked
                      }
                    })}
                  />
                  <Form.Check
                    type="checkbox"
                    label="Email Alert"
                    checked={jobMonitorForm.alertSettings.emailAlert}
                    onChange={(e) => setJobMonitorForm({
                      ...jobMonitorForm,
                      alertSettings: {
                        ...jobMonitorForm.alertSettings,
                        emailAlert: e.target.checked
                      }
                    })}
                  />
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Check Interval (minutes)</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max="60"
                      value={jobMonitorForm.alertSettings.checkInterval}
                      onChange={(e) => setJobMonitorForm({
                        ...jobMonitorForm,
                        alertSettings: {
                          ...jobMonitorForm.alertSettings,
                          checkInterval: parseInt(e.target.value)
                        }
                      })}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Auto-stop after (minutes)</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max="120"
                      value={jobMonitorForm.alertSettings.autoStopAfterMinutes}
                      onChange={(e) => setJobMonitorForm({
                        ...jobMonitorForm,
                        alertSettings: {
                          ...jobMonitorForm.alertSettings,
                          autoStopAfterMinutes: parseInt(e.target.value)
                        }
                      })}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => {
                setShowJobMonitorModal(false);
                setSelectedJobMonitor(null);
                setJobMonitorForm({
                  isActive: false,
                  postcodeAreas: [],
                  alertSettings: {
                    screenFlash: true,
                    soundAlert: true,
                    emailAlert: false,
                    checkInterval: 5,
                    autoStopAfterMinutes: 30
                  }
                });
              }}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Update Settings
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Copy Monitor Areas Modal */}
        <Modal show={showCopyMonitorModal} onHide={() => {
          setShowCopyMonitorModal(false);
          setSourceDriver(null);
          setSelectedTargetDrivers([]);
        }} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Copy Monitored Areas</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {sourceDriver && (
              <div className="mb-4">
                <h6>Source Driver</h6>
                <div className="alert alert-info">
                  <strong>{sourceDriver.userId.name}</strong> ({sourceDriver.userId.email})
                  <br />
                  <strong>Monitored Areas:</strong> {sourceDriver.postcodeAreas.length > 0 ? (
                    <div className="d-flex flex-wrap gap-1 mt-2">
                      {sourceDriver.postcodeAreas.map((area, index) => (
                        <Badge key={index} bg="info" className="text-wrap">
                          {area}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted">No areas configured</span>
                  )}
                </div>
              </div>
            )}

            <hr />

            <h6>Select Target Drivers</h6>
            <div className="mb-3">
              <div className="d-flex gap-2 mb-2">
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  onClick={handleSelectAllTargetDrivers}
                >
                  Select All
                </Button>
                <Button 
                  variant="outline-secondary" 
                  size="sm" 
                  onClick={handleClearTargetDrivers}
                >
                  Clear All
                </Button>
                <Badge bg="info" className="align-self-center">
                  {selectedTargetDrivers.length} selected
                </Badge>
              </div>
            </div>

            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>
                      <Form.Check
                        type="checkbox"
                        checked={selectedTargetDrivers.length > 0}
                        onChange={handleSelectAllTargetDrivers}
                      />
                    </th>
                    <th>Driver Name</th>
                    <th>Email</th>
                    <th>Company</th>
                    <th>Current Areas</th>
                  </tr>
                </thead>
                <tbody>
                  {[...jobMonitors, ...usersWithoutMonitors]
                    .filter(driver => {
                      const driverId = driver._id || driver.userId?._id;
                      return driverId && driverId !== sourceDriver?._id;
                    })
                    .map(driver => {
                      // Handle different data structures for job monitors vs users without monitors
                      const isJobMonitor = driver.userId !== undefined;
                      const driverId = isJobMonitor ? driver.userId._id : driver._id;
                      const driverName = isJobMonitor ? driver.userId.name : driver.name;
                      const driverEmail = isJobMonitor ? driver.userId.email : driver.email;
                      const driverCompany = isJobMonitor ? driver.userId.companyName : driver.companyName;
                      const currentAreas = isJobMonitor ? driver.postcodeAreas : [];
                      
                      return (
                        <tr key={driverId}>
                          <td>
                            <Form.Check
                              type="checkbox"
                              checked={selectedTargetDrivers.includes(driverId)}
                              onChange={() => handleSelectTargetDriver(driverId)}
                            />
                          </td>
                          <td>{driverName || 'Unknown'}</td>
                          <td>{driverEmail || 'No email'}</td>
                          <td>{driverCompany || '-'}</td>
                          <td>
                            {currentAreas && currentAreas.length > 0 ? (
                              <div className="d-flex flex-wrap gap-1">
                                {currentAreas.map((area, index) => (
                                  <Badge key={index} bg="secondary" className="text-wrap">
                                    {area}
                                  </Badge>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted">No areas</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </Table>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => {
              setShowCopyMonitorModal(false);
              setSourceDriver(null);
              setSelectedTargetDrivers([]);
            }}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleExecuteCopy}
              disabled={copyInProgress || selectedTargetDrivers.length === 0}
            >
              {copyInProgress ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Copying...
                </>
              ) : (
                `Copy to ${selectedTargetDrivers.length} Driver(s)`
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* SMS Template Modal */}
        <Modal show={showSmsTemplateModal} onHide={() => setShowSmsTemplateModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {selectedSmsTemplate ? 'Edit SMS Template' : 'Create SMS Template'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Template Name (unique identifier)</Form.Label>
                    <Form.Control
                      type="text"
                      value={smsTemplateForm.name}
                      onChange={(e) => setSmsTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., taxi_confirmation"
                      disabled={selectedSmsTemplate !== null}
                    />
                    <Form.Text className="text-muted">
                      Used internally, cannot be changed after creation
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Display Title</Form.Label>
                    <Form.Control
                      type="text"
                      value={smsTemplateForm.title}
                      onChange={(e) => setSmsTemplateForm(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g., Taxi Confirmation"
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Order</Form.Label>
                    <Form.Control
                      type="number"
                      value={smsTemplateForm.order}
                      onChange={(e) => setSmsTemplateForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                      placeholder="0"
                    />
                    <Form.Text className="text-muted">
                      Lower numbers appear first in the list
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Check
                      type="switch"
                      id="sms-template-active"
                      label="Active (available to drivers)"
                      checked={smsTemplateForm.isActive}
                      onChange={(e) => setSmsTemplateForm(prev => ({ ...prev, isActive: e.target.checked }))}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>SMS Message</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={8}
                  value={smsTemplateForm.message}
                  onChange={(e) => setSmsTemplateForm(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Enter your SMS message here..."
                />
                <Form.Text className="text-muted">
                  Available placeholders: {'{driverName}'}, {'{driverPhone}'}, {'{from}'}, {'{to}'}, {'{dateTime}'}, {'{passengers}'}, {'{fareAmount}'}, {'{paymentMethods}'}, {'{eta}'}, {'{newEta}'}
                </Form.Text>
              </Form.Group>

              <Alert variant="info">
                <h6>💡 Template Examples:</h6>
                <ul className="mb-0">
                  <li><strong>Taxi Confirmation:</strong> "🚕 Taxi Confirmed! Driver: {'{driverName}'} ({'{driverPhone}'}) From: {'{from}'} To: {'{to}'} Date: {'{dateTime}'} Passengers: {'{passengers}'} Payment: {'{paymentMethods}'}"</li>
                  <li><strong>Taxi On Way:</strong> "🚕 Your taxi is on the way! Driver: {'{driverName}'} ({'{driverPhone}'}) ETA: {'{eta}'}"</li>
                  <li><strong>Taxi Here:</strong> "🚕 Your taxi has arrived! Driver: {'{driverName}'} ({'{driverPhone}'}) Please come out to meet your driver."</li>
                </ul>
              </Alert>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowSmsTemplateModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSaveSmsTemplate}>
              {selectedSmsTemplate ? 'Update Template' : 'Create Template'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Product Management Modal */}
        <Modal show={showProductModal} onHide={() => setShowProductModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {selectedProduct ? 'Edit Product' : 'Add New Product'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSaveProduct}>
            <Modal.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Product Name *</Form.Label>
                    <Form.Control
                      type="text"
                      value={productForm.name}
                      onChange={(e) => setProductForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter product name"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Category *</Form.Label>
                    <Form.Select
                      value={productForm.category}
                      onChange={(e) => setProductForm(prev => ({ ...prev, category: e.target.value }))}
                      required
                    >
                      <option value="">Select Category</option>
                      <option value="business-cards">Business Cards</option>
                      <option value="magnetic-signs">Magnetic Signs</option>
                      <option value="polo-shirts">Polo Shirts</option>
                      <option value="other">Other</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Description *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={productForm.description}
                  onChange={(e) => setProductForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Enter product description"
                  required
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Price (£) *</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      value={productForm.price}
                      onChange={(e) => setProductForm(prev => ({ ...prev, price: e.target.value }))}
                      placeholder="0.00"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Units (Order Quantity)</Form.Label>
                    <Form.Control
                      type="text"
                      value={productForm.units}
                      onChange={(e) => setProductForm(prev => ({ ...prev, units: e.target.value }))}
                      placeholder="e.g., 2's, 5's, 10's, or 1"
                      required
                    />
                    <Form.Text className="text-muted">
                      Specify how this product is ordered (e.g., "2's", "5's", "10's", or "1")
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Specifications</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={productForm.specifications}
                  onChange={(e) => setProductForm(prev => ({ ...prev, specifications: e.target.value }))}
                  placeholder="Enter product specifications (e.g., size, material, color options)"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Product Image</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                />
                <Form.Text className="text-muted">
                  Upload a product image (JPG, PNG, GIF). Max size: 5MB
                </Form.Text>
              </Form.Group>

              {(productForm.imagePreview || (selectedProduct && selectedProduct.image)) && (
                <div className="mb-3">
                  <Form.Label>Image Preview</Form.Label>
                  <div>
                    <img 
                      src={productForm.imagePreview || selectedProduct?.image} 
                      alt="Product preview"
                      style={{ 
                        maxWidth: '200px', 
                        maxHeight: '200px', 
                        objectFit: 'cover',
                        borderRadius: '8px',
                        border: '1px solid #dee2e6'
                      }}
                    />
                    {productForm.imagePreview && (
                      <p className="small text-muted mt-2">New image selected</p>
                    )}
                    {!productForm.imagePreview && selectedProduct && (
                      <p className="small text-muted mt-2">Current product image</p>
                    )}
                  </div>
                </div>
              )}

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Active Product"
                  checked={productForm.isActive}
                  onChange={(e) => setProductForm(prev => ({ ...prev, isActive: e.target.checked }))}
                />
                <Form.Text className="text-muted">
                  Inactive products won't be visible to customers
                </Form.Text>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowProductModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {selectedProduct ? 'Update Product' : 'Create Product'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Merchandise Order Management Modal */}
        <Modal show={showOrderModal} onHide={() => setShowOrderModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              Manage Order {selectedOrder?.orderNumber}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedOrder && (
              <>
                {/* Order Details */}
                <Row className="mb-4">
                  <Col md={6}>
                    <Card className="border-primary">
                      <Card.Header className="bg-primary text-white">
                        <h6 className="mb-0">Order Information</h6>
                      </Card.Header>
                      <Card.Body>
                        <p><strong>Order Number:</strong> {selectedOrder.orderNumber}</p>
                        <p><strong>Product:</strong> {selectedOrder.productName}</p>
                        <p><strong>Quantity:</strong> {selectedOrder.quantity}</p>
                        <p><strong>Total Price:</strong> £{selectedOrder.totalPrice.toFixed(2)}</p>
                        <p><strong>Order Date:</strong> {new Date(selectedOrder.orderDate).toLocaleString('en-GB')}</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="border-info">
                      <Card.Header className="bg-info text-white">
                        <h6 className="mb-0">Customer Information</h6>
                      </Card.Header>
                      <Card.Body>
                        <p><strong>Name:</strong> {selectedOrder.userId?.name || 'Unknown'}</p>
                        <p><strong>Email:</strong> {selectedOrder.userId?.email}</p>
                        <p><strong>Phone:</strong> {selectedOrder.contactPhone}</p>
                        <p><strong>Delivery Address:</strong></p>
                        <small className="text-muted">{selectedOrder.deliveryAddress}</small>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {/* Customization Details */}
                <Card className="mb-4">
                  <Card.Header>
                    <h6 className="mb-0">Customization Details</h6>
                  </Card.Header>
                  <Card.Body>
                    <p><strong>Custom Text:</strong></p>
                    <p className="text-muted">{selectedOrder.customText}</p>
                    {selectedOrder.specialInstructions && (
                      <>
                        <p><strong>Special Instructions:</strong></p>
                        <p className="text-muted">{selectedOrder.specialInstructions}</p>
                      </>
                    )}
                  </Card.Body>
                </Card>

                {/* Order Status Management */}
                <Card className="mb-4">
                  <Card.Header>
                    <h6 className="mb-0">Order Status Management</h6>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Order Status</Form.Label>
                          <Form.Select
                            value={orderStatusForm.status}
                            onChange={(e) => setOrderStatusForm(prev => ({ ...prev, status: e.target.value }))}
                          >
                            <option value="pending">Pending</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="in_production">In Production</option>
                            <option value="shipped">Shipped</option>
                            <option value="delivered">Delivered</option>
                            <option value="cancelled">Cancelled</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Tracking Number</Form.Label>
                          <Form.Control
                            type="text"
                            value={orderStatusForm.trackingNumber}
                            onChange={(e) => setOrderStatusForm(prev => ({ ...prev, trackingNumber: e.target.value }))}
                            placeholder="Enter tracking number"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group className="mb-3">
                      <Form.Label>Admin Notes</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={orderStatusForm.adminNotes}
                        onChange={(e) => setOrderStatusForm(prev => ({ ...prev, adminNotes: e.target.value }))}
                        placeholder="Add notes for this order..."
                      />
                    </Form.Group>
                    <Button
                      variant="primary"
                      onClick={() => handleUpdateOrderStatus(selectedOrder._id)}
                    >
                      Update Order Status
                    </Button>
                  </Card.Body>
                </Card>

                {/* Payment Management */}
                <Card>
                  <Card.Header>
                    <h6 className="mb-0">Payment Management</h6>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Payment Status</Form.Label>
                          <Form.Select
                            value={paymentForm.paymentStatus}
                            onChange={(e) => setPaymentForm(prev => ({ ...prev, paymentStatus: e.target.value }))}
                          >
                            <option value="pending">Pending</option>
                            <option value="paid">Paid</option>
                            <option value="refunded">Refunded</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Payment Method</Form.Label>
                          <Form.Control
                            type="text"
                            value={paymentForm.paymentMethod}
                            onChange={(e) => setPaymentForm(prev => ({ ...prev, paymentMethod: e.target.value }))}
                            placeholder="e.g. Bank Transfer, PayPal"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Button
                      variant="success"
                      onClick={() => handleUpdatePaymentStatus(selectedOrder._id)}
                    >
                      Update Payment Status
                    </Button>
                  </Card.Body>
                </Card>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowOrderModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Bulk Actions Modal */}
        <Modal show={showBulkActionsModal} onHide={() => setShowBulkActionsModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Bulk Actions - {selectedUsers.length} User(s) Selected</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleBulkAction}>
            <Modal.Body>
              <Alert variant="info">
                <strong>Selected Users:</strong> {selectedUsers.length} user(s) will be affected by this action.
              </Alert>
              
              <Form.Group className="mb-3">
                <Form.Label>Action Type</Form.Label>
                <Form.Select
                  value={bulkActionForm.action}
                  onChange={(e) => {
                    setBulkActionForm(prev => ({...prev, action: e.target.value}));
                  }}
                  required
                >
                  <option value="">Select an action...</option>
                  <option value="changeStatus">Change Status</option>
                  <option value="changeRole">Change Role</option>
                </Form.Select>
              </Form.Group>

              {bulkActionForm.action === 'changeStatus' && (
                <Form.Group className="mb-3">
                  <Form.Label>New Status</Form.Label>
                  <Form.Select
                    value={bulkActionForm.newStatus}
                    onChange={(e) => {
                      setBulkActionForm(prev => ({...prev, newStatus: e.target.value}));
                    }}
                    required
                  >
                    <option value="">Select new status...</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                    <option value="pending">Pending</option>
                  </Form.Select>
                </Form.Group>
              )}

              {bulkActionForm.action === 'changeRole' && (
                <Form.Group className="mb-3">
                  <Form.Label>New Role</Form.Label>
                  <Form.Select
                    value={bulkActionForm.newRole}
                    onChange={(e) => {
                      setBulkActionForm(prev => ({...prev, newRole: e.target.value}));
                    }}
                    required
                  >
                    <option value="">Select new role...</option>
                    <option value="professional">Professional</option>
                    <option value="listing-only">Listing Only</option>
                    <option value="admin">Admin</option>
                    <option value="dispatcher">Dispatcher</option>
                  </Form.Select>
                </Form.Group>
              )}

              <Alert variant="warning">
                <strong>Warning:</strong> This action will be applied to all selected users. This cannot be undone.
              </Alert>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowBulkActionsModal(false)}>
                Cancel
              </Button>
              <Button variant="warning" type="submit">
                Apply Bulk Action
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Email Log Details Modal */}
        <Modal show={showEmailLogModal} onHide={() => setShowEmailLogModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Email Log Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedDriverEmailLogs && (
              <div>
                <h5>Driver: {selectedDriverEmailLogs.logs[0]?.driverName}</h5>
                <p className="text-muted">{selectedDriverEmailLogs.logs[0]?.driverEmail}</p>
                
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Date & Time</th>
                      <th>Recipient</th>
                      <th>Subject</th>
                      <th>Type</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedDriverEmailLogs.logs.map((log) => (
                      <tr 
                        key={log._id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => showFullEmailContent(log)}
                        className="email-log-row"
                      >
                        <td>
                          <div>
                            <strong>{new Date(log.sentAt).toLocaleDateString()}</strong>
                            <br />
                            <small className="text-muted">
                              {new Date(log.sentAt).toLocaleTimeString()}
                            </small>
                          </div>
                        </td>
                        <td>
                          <div>
                            {log.recipientName && (
                              <strong>{log.recipientName}</strong>
                            )}
                            <br />
                            <small className="text-muted">{log.recipientEmail}</small>
                          </div>
                        </td>
                        <td>{log.subject}</td>
                        <td>
                          <Badge bg="secondary">{log.emailType}</Badge>
                        </td>
                        <td>
                          <Badge bg={log.status === 'sent' ? 'success' : log.status === 'failed' ? 'danger' : 'warning'}>
                            {log.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {selectedDriverEmailLogs.logs[0]?.errorMessage && (
                  <div className="mt-3">
                    <h6>Error Message:</h6>
                    <Alert variant="danger">
                      {selectedDriverEmailLogs.logs[0].errorMessage}
                    </Alert>
                  </div>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEmailLogModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Full Email Content Modal */}
        <Modal show={showFullEmailModal} onHide={() => setShowFullEmailModal(false)} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>Email Content</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedEmailContent && (
              <div>
                <div className="mb-3">
                  <h5>{selectedEmailContent.subject}</h5>
                  <div className="row">
                    <div className="col-md-6">
                      <strong>From:</strong> {selectedEmailContent.driverName}
                    </div>
                    <div className="col-md-6">
                      <strong>To:</strong> {selectedEmailContent.recipientName || selectedEmailContent.recipientEmail}
                    </div>
                  </div>
                  <div className="row mt-2">
                    <div className="col-md-6">
                      <strong>Sent:</strong> {new Date(selectedEmailContent.sentAt).toLocaleString()}
                    </div>
                    <div className="col-md-6">
                      <strong>Status:</strong> 
                      <Badge bg={selectedEmailContent.status === 'sent' ? 'success' : selectedEmailContent.status === 'failed' ? 'danger' : 'warning'} className="ms-2">
                        {selectedEmailContent.status}
                      </Badge>
                    </div>
                  </div>
                </div>
                
                <hr />
                
                <div className="email-content-container">
                  <h6>Email Content:</h6>
                  <div 
                    className="border p-3 bg-light"
                    style={{ 
                      maxHeight: '500px', 
                      overflow: 'auto',
                      minHeight: '200px'
                    }}
                    dangerouslySetInnerHTML={{ __html: selectedEmailContent.content }}
                  />
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowFullEmailModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

       <ToastContainer position="top-right" />
     </div>
   );
 } 